/**
 * The course. Deterministic from a seed — no level data ever travels over the
 * wire, every client builds the identical geometry at boot.
 *
 * Pure data + math: no three.js, no DOM, and **no `Math.random`** anywhere in
 * this file or anything it calls.
 *
 * Shape of the course, bottom to top:
 *
 * - A start plaza (r = 34 disc) with a guard-rail ring, so a fall that reaches
 *   the bottom lands somewhere survivable instead of rolling off into nothing.
 *   It is that wide because the mountain's first zone is: a miss out on the
 *   Meadow foot falls more or less straight down.
 * - ~100 landing nodes climbing to y ≈ 246 on a wandering path around a
 *   **hollow central shaft**: nothing at all lives inside r < 6 for y > 40, so a
 *   missed jump toward the middle is a clean fall all the way to the plaza.
 * - A gold summit disc at y = 250, reached by a final outward-then-inward hop.
 *
 * The mountain has a **silhouette**, and it comes from the zone table: each zone
 * owns a radial band, so the climb is a broad sunlit foot (r 18–30), a green
 * skirt drawn in above it (15–27), a tighter terraced middle (11–21), crystal
 * arms reaching out (10–26), a broad cornice (14–28) and a tight black spire
 * (10–14). The bands are eased across the seams and pinched to a waist wherever
 * a checkpoint's lobby floor has to be built, so what you see from the plaza is
 * a shape with articulation in it rather than a tube — and no two stretches of
 * the climb can look alike, because no two of them have the same amount of room
 * to happen in.
 *
 * The path is **not a helix**. Going round and round one way reads as a treadmill
 * — you can climb fifty metres without ever being able to tell where you are. So
 * the walk is a seeded march in polar coordinates `(theta, radius)` around the
 * shaft, cut into segments of 6–12 nodes. Each segment rolls its own pattern,
 * from its *own zone's* table — the mix is the other half of what a zone looks
 * like, since the same band full of arcs is a circle and full of spurs is a set
 * of arms:
 *
 * - `arc` — hold the direction, drift the radius gently (the default);
 * - `switchback` — reverse the way round the shaft, so the climb doubles back
 *   over itself and the wall you just left becomes the view;
 * - `spur` — push out to the zone's rim and come back in;
 * - `tighten` — close in on the zone's inner limit, toward the shaft.
 *
 * The opening sixteen nodes are always a plain `arc` — the whole of Meadow
 * Foothills has to read at a glance — and the final 8 % blends back to r = 12
 * with the direction locked, so the summit hop is never a surprise reversal.
 *
 * Whatever a pattern does to the radius, the horizontal gap between consecutive
 * nodes is held at the current zone's **chord**: the angular step is solved from
 * the radius change, using `dr² + 4·r₀r₁·sin²(dθ/2) = chord²`, so a jump's
 * difficulty stays in the zone table's hands rather than the radius's — and the
 * gap stays inside the climbability budget checked by `level.selftest.ts`.
 *
 * Radius and angle are solved *together*, in one forward pass, and that is what
 * lets the walk refuse to stand on itself. Before a node is placed it is
 * measured against everything already laid between three and twenty-eight metres
 * below it, and where something is directly underneath the radius is nudged —
 * pure geometry, no rolls — until it is not. Two pads on the same spoke twenty
 * metres apart is the thing that made an earlier version of this course read as
 * a cramped tube: you land, look up, and see the underside of the next lap.
 *
 * The climb **forks**. Every so often the trunk opens a second way up: a branch
 * lane leaves a shared junction pad, runs its own nodes, and rejoins the trunk a
 * dozen metres higher. A fork is either `opposite` — it goes the *other* way
 * round the shaft, so the two routes can only ever meet at their endpoints — or
 * `parallel`, hanging five metres further out than anything the trunk does in
 * that window.
 *
 * What a branch is *made of* comes from a library of six **motifs** — a rim wall
 * of staggered ledges, a flight of mushrooms, a colonnade, a decked bridge, a
 * spur out to an island, or a plain fast traverse — and the motif decides the
 * branch's promise too. A `bold` lane climbs faster per hop than the trunk
 * stretch it replaces; a `safe` or `scenic` one deliberately climbs slower, more
 * landings and shorter steps for the same height. Either is a reason to take it;
 * a branch that matches the trunk exactly is not, and the planner refuses a
 * window it cannot make into one or the other. Junction pads are widened and
 * carry two little arrows in the colour of the branch's promise — a fork you
 * cannot see is not a fork.
 *
 * The trunk wears set pieces of its own, keyed off the shape the walk is already
 * making: ledges down the inside of a `tighten`, columns through a `switchback`'s
 * turn, an island on the tip of a `spur`. Those never move a node — trunk
 * positions are what the pads, the funnels and every fork window were solved
 * against — they only decide what stands on one.
 *
 * A column *is* the landing there, not a post standing next to one: a `pillar`
 * node emits a single thick cylinder rising to its own height, and you land on
 * the cap. The loose scenery went the same way. A decor roll plants a group of
 * two to four pieces instead of one lonely stick, because a ruined colonnade has
 * to look ruined and a tree has to look like a tree — nothing on this mountain
 * is meant to read as a 0.6 m pole someone left there.
 *
 * The climb is cut into six **zones** — Meadow Foothills, Verdant Steps, Sunken
 * Ruins, Crystal Hollow, Frozen Shelf, Obsidian Crown — with boundaries at
 * y = 30, 75, 120, 165 and 210. Each zone owns its own node spacing, mean rise,
 * landing-pad size, feature mix, palette and decorative props, so the climb
 * starts forgiving (short hops onto 8 m by 6 m meadow slabs) and ends mean (long
 * hops onto narrow basalt ledges) — and the ramp between the two is deliberately
 * steep, so the first thirty metres teach the bounce and everything above them
 * asks for it back. Every zone also owns **one landmark** — a dolmen, a great
 * tree, a ruined gate, a broken geode, a frozen cascade, a gold-veined monolith
 * — standing a measured distance off the route, out past whatever its zone
 * actually built, so that "how far up am I" has an answer you can see rather
 * than one you have to count. A
 * **checkpoint pad** sits on the node nearest each boundary, on a sealed
 * full-width **lobby floor** in the palette of the zone it opens — the one flat
 * place on the mountain, and the visible seam between one level and the next.
 * Nothing passes through a floor, so the climb has to come in over its outer
 * rim: the last hops into a pad are a traverse round the outside. The node
 * count is a consequence of that table rather than a round number: every zone
 * gets enough nodes to cross its own height at its own mean rise.
 *
 * And some of it **moves**. A last pass hangs motion specs (`movers.ts`) on
 * landings the course has already settled — sliding pads whose amplitude is
 * solved so that even the worst phase leaves both hops inside the budget,
 * ferries and elevators you deliberately wait for and ride, a carousel that
 * takes the whole shaft with it and puts you back where it found you, a field of
 * blinking pads in the Crystal Hollow, and hazards that owe you nothing: sweeper
 * bars orbiting over the widest pads and logs swinging across the bridge decks.
 * Motion is a pure function of a shared clock, so it costs the wire nothing and
 * two clients cannot disagree about it. The pass draws from its **own** RNG
 * stream, forked off the level seed: what moves is decided after where the
 * course goes, and retuning the one may never disturb the other.
 */

import { LEVEL_SEED } from './constants';
import { applyMover, clonePrimitive, moverSweptAabb, type MoverSpec } from './movers';
import { mulberry32, pickWeighted, randInt, randRange, type Rng } from './rng';
import {
  identityQuat,
  type LevelData,
  type Material,
  type Primitive,
  type V3,
  type V4,
} from './primitives';

// ---------------------------------------------------------------------------
// Course parameters
// ---------------------------------------------------------------------------

/** Y of the summit disc's top face — the height the progress bar is scaled to. */
const SUMMIT_Y = 250;
/** Radius of the summit disc. The last nodes flare wider than this. */
const SUMMIT_RADIUS = 10;
/**
 * The hard inner limit on any node's radius, anywhere on the mountain. Set so
 * the widest landing pad *the shaft rule applies to* — Verdant's, half-diagonal
 * ≈ 3.6 m — still clears the r = 6 shaft whichever way the pad happens to be
 * turned.
 *
 * Meadow Foothills is the one exception, and a deliberate one: its 4 × 3 m pads
 * reach 5 m from their node and so would hang a corner over the shaft — but the
 * hollow shaft only exists above `SHAFT_MIN_Y` = 40 m and the whole zone tops
 * out at 30, so there is nothing yet to fall past. The rule and the zone never
 * meet, and the Meadow band's own floor of 18 keeps them a long way apart even
 * where the waist at the y = 30 checkpoint pulls it in.
 *
 * How far *out* the path may wander is deliberately **not** one number. Each
 * zone owns a radial band (`rLo … rHi` in {@link ZONES}), which is where the
 * mountain's silhouette comes from: a wide green skirt, a tighter terraced
 * middle, crystal arms reaching out, a broad cornice, a tight black spire. A
 * band's `rLo` may narrow what a zone walks in but may never open it inside
 * this — the pad-versus-shaft promise is per node, not per zone, and
 * {@link bandAlongTrunk} is where that is enforced rather than trusted.
 */
const R_MIN = 10;
/**
 * Radius the climb starts at — just inside the plaza rim, and a little under the
 * middle of the Meadow band, so the first hop is a plainly visible step out onto
 * the foot of the mountain and the opening arc has somewhere to lean.
 */
const R_START = 20;
/**
 * How a zone's band gets to the next one's. Taken literally the table is a
 * step, and the hop out of the Verdant skirt into the Sunken Ruins would be
 * asked for six metres of radius in one jump; blended over this many nodes
 * either side of the seam, the silhouette swells and pinches instead.
 *
 * Three, and it was five, and the difference is the whole silhouette. A zone is
 * twelve to twenty-odd nodes long; five nodes of blend at each seam plus the
 * waist either side of the checkpoint sitting on it left a zone like the Frozen
 * Shelf with five nodes out of fifteen that were ever offered their own band —
 * not enough for any pattern to cross one, so the shelf was built at the waist's
 * radius from bottom to top and the "broad cornice" existed only in the table.
 * The seam still has to be a ramp rather than a step, and three nodes is one:
 * the widest jump the table asks for is the six metres of rim between the
 * Verdant skirt and the Sunken Ruins, which over six nodes is a metre apiece and
 * well inside what one step of radius may change (see {@link radialStepMax}).
 */
const BAND_BLEND_NODES = 3;
/**
 * The waist. However wide the zones either side of a boundary are, the band
 * right at a checkpoint is pulled toward this moderate range: a lobby floor
 * reaches from r = 6.6 out past its own pad, and a pad on a 30 m rim would be a
 * second plaza. So the mountain narrows at every zone seam — which reads as a
 * deliberate landmark between two bulges rather than as a compromise.
 */
const WAIST_LO = 13;
const WAIST_HI = 16;
/**
 * Nodes either side of a checkpoint the waist is blended over. Two, for the same
 * reason the seam blend is three: a pinch is a feature of the silhouette, and a
 * pinch that reaches seven nodes wide in a zone that is fifteen long is not a
 * pinch, it is the zone. The node carrying the pad still gets the waist in full.
 */
const WAIST_BLEND_NODES = 2;
/** A pattern holds for this many nodes before the walk rolls the next one. */
const SEG_MIN = 6;
const SEG_MAX = 12;
/**
 * The opening is always a plain arc: the tutorial zone has to read at a glance.
 * Sized to swallow the whole of Meadow Foothills — (30 − 3) / 1.7 ≈ 16 nodes —
 * so the first zone is one unbroken sweep with no reversal anywhere in it.
 */
const OPENING_ARC_NODES = 16;
/**
 * Longest run the walk may hold one way round before the next segment is forced
 * to be a switchback. A node is 20°–30° of sweep, so this is a turn and a half —
 * past that the climb stops being a path and starts being a helix again.
 */
const MAX_SAME_DIR_NODES = 14;
/**
 * What each pattern asks of the radius, per node — as a **fraction of the band
 * it is asking in**, not as a number of metres.
 *
 * This is the one number that could not be global. A spur in the four-metre
 * Obsidian spire and a spur out over the fourteen-metre Frozen Shelf are the
 * same gesture, and a gesture measured in metres is two different things: two
 * metres a node crosses the spire in one step and needs seven to cross the
 * shelf — more nodes than a segment has, so on the shelf the pattern never
 * arrived anywhere and the "broad cornice" was a table entry rather than a
 * shape. Read against the band, one number means the same thing everywhere: a
 * spur spends a fifth of its band per node and so reaches the rim in five,
 * wherever it is standing, and an arc wanders a twentieth of one.
 *
 * The band the fraction is read against is the *eased* band at the node being
 * placed (see {@link bandAlongTrunk}), not the zone table's — so a segment that
 * starts in a waist and runs out into a zone interior widens its stride as the
 * mountain opens under it, which is the shape a shelf coming out of a pinch
 * actually has.
 */
const ARC_DRIFT = 0.07;
const SWITCHBACK_DRIFT = 0.07;
const SPUR_DRIFT = 0.22;
const TIGHTEN_DRIFT = 0.075;
/**
 * The one big radial sidestep a switchback spends on its turning step, in the
 * same currency: a quarter of the band it turns in. Without it a reversal comes
 * back up the same wall it left and parks its pads three metres over the ones
 * below; with it the two legs are visibly separate lines.
 *
 * A quarter is what the chord budget can pay for. The widest band a switchback
 * ever turns in is the Crystal Hollow's sixteen metres — Meadow's fourteen never
 * sees one, being sixteen nodes of forced arc — so the biggest kick this can ask
 * for is four metres, and four metres of that zone's 5.7 m chord still leaves
 * 4.1 m of tangential step, twice the `MIN_TANGENTIAL` floor. Whatever is left
 * over after that is caught by {@link radialStepMax}, which is what actually
 * bounds every candidate.
 */
const SWITCHBACK_KICK = 0.25;
/**
 * Floor on a step's tangential component, as a fraction of the chord. A step
 * that spent its whole budget climbing outward would have no angular part left
 * and would stack two nodes on the same spoke.
 */
const MIN_TANGENTIAL = 0.35;
/**
 * The widest a single step is allowed to come out once the tangential floor has
 * taken over — see {@link radialStepMax}.
 *
 * The chord solve holds a hop at its zone's chord by spending whatever is left
 * of the budget on the angular part, but only while there is something left:
 * past |dr| ≈ 0.94·chord the `MIN_TANGENTIAL` floor bites and the gap starts
 * growing with the radius change instead. That was academic while a drift was
 * half a metre; a de-stack nudge is metres, and a nudge that widened a hop past
 * what a bounce can cross would be trading a bad view for an unclimbable jump.
 *
 * Held well inside the 7 m `level.selftest.ts` enforces, because the angular
 * jitter is still to come on top: 0.01 rad either side of a 30 m Meadow radius
 * is 0.3 m per node, so two nodes can add 0.6 m to a hop between them.
 */
const STEP_GAP_MAX = 6.3;
/**
 * De-stacking.
 *
 * Two pads on the same spoke twenty metres apart is the single loudest thing
 * that made the old course read as a tube: you land, look up, and see the
 * underside of the next lap. So as the walk lays node `i` it measures the
 * candidate against every node already placed between `STACK_DY_MIN` and
 * `STACK_DY_MAX` metres below it, and where anything sits inside `STACK_CLEAR`
 * horizontally it tries a ladder of radius adjustments — nearest first, never
 * more than `STACK_STEER_MAX` off the line the pattern asked for, always inside
 * the eased band — and takes the first that clears. Where none does it keeps
 * whichever candidate came closest, because a nudge that only half works is
 * still better than the stack it was aiming at.
 *
 * The window's floor is three metres because anything closer than that
 * vertically is a *neighbour*, not a ceiling — consecutive landings on the same
 * lane are supposed to be nearly on top of each other. Its ceiling is
 * twenty-eight because past that the thing overhead is sky.
 *
 * The ladder reaches five metres because three was not enough: the walk revisits
 * an angular position from *both* sides over a zone, and a node with an old lap
 * two metres inside it and another two metres outside has to be able to get past
 * one of them. What actually bounds a rung is `radialStepMax`, not this.
 *
 * Pure geometry: the ladder is a fixed list and the choice is made on distances
 * alone, so which rung wins can never move the RNG stream.
 */
const STACK_CLEAR = 5.5;
const STACK_DY_MIN = 3;
const STACK_DY_MAX = 28;
const STACK_STEER_MAX = 5;
const STACK_STEER_STEP = 0.5;
/**
 * Passes of the height/zone fixed point. Each step's rise comes from its zone
 * and its zone comes from its height, so the two are solved together; three
 * passes is already plenty, the cap is only there to bound the loop.
 */
const HEIGHT_PASSES = 8;
/**
 * Start angle. The first node sits across the plaza from the spawn point, in
 * plain view — standing under it told you nothing about where to go.
 */
const THETA0 = Math.PI / 2;
/** Height of the first node above the plaza. */
const FIRST_NODE_Y = 3.0;
/** Height of the last node — one good hop below the summit rim. */
const LAST_NODE_Y = 246.3;
/**
 * The final approach blends back out to `FLARE_RADIUS` and locks the angular
 * direction, so the summit rim is a jumpable outward hop rather than something
 * directly overhead reached through a surprise reversal.
 */
const FLARE_FRACTION = 0.08;
const FLARE_RADIUS = 12;
/** Per-node jitter: radius (m), angle (rad), and rise (fraction of the zone mean). */
const RADIAL_JITTER = 0.3;
const ANGLE_JITTER = 0.01;
const RISE_JITTER = 0.10;
/** Nothing may exist inside this radius of the Y axis above `SHAFT_MIN_Y`. */
export const SHAFT_RADIUS = 6;
export const SHAFT_MIN_Y = 40;
/**
 * Funnels are forced at these heights so the punishers are not luck of the draw.
 * Exported so `level.selftest.ts` checks the real numbers instead of a copy.
 */
export const FUNNEL_TARGETS: readonly number[] = [148, 195];
/** Funnels below this height would ring the plaza; the climb has to start clean. */
const FUNNEL_MIN_Y = 25;
/** Each funnel costs nine primitives — cap them so the draw budget cannot run away. */
const MAX_FUNNELS = 5;
/**
 * The summit approach: trunk nodes at the top of the climb that nothing nasty may
 * stand on.
 *
 * The last hops onto the rim are the ones a run is lost on, and they were solved
 * against the hop budget and against nothing else — the flare has already locked
 * the direction and blended the radius back to `FLARE_RADIUS` so that the summit
 * is a plain outward step rather than a surprise. A punisher funnel there is a
 * collar round the one jump that has no alternative; a sweeper bar there is the
 * same argument with a beam. Both are kept out, by the same number, because
 * "the summit approach" ought to mean one thing.
 *
 * Six, which at the crown's rise is a little under twenty metres of climb. It
 * has to be that deep because the top of the spire is where the trunk has the
 * most *room* — the last checkpoint is thirty-six metres below, so nothing up
 * here is in a lobby's quarantine — and a hazard placer looking for somewhere
 * legal will find it there every time. Three was tried and was not enough: the
 * bars simply
 * moved down two nodes and landed either side of the ferry, three beats in six
 * metres, which is the pile-up this exists to prevent wearing a different y.
 */
const SUMMIT_APPROACH_NODES = 6;
/** Funnel collar geometry. */
const FUNNEL_SEGMENTS = 8;
const FUNNEL_TILT = (58 * Math.PI) / 180;
const FUNNEL_GAP_M = 4.5;
const FUNNEL_RING_OFFSET = 2.2;
const FUNNEL_HALF_RADIAL = 1.5;
const FUNNEL_HALF_THICK = 0.15;
/** A ledge: the narrow landing. Half-extents, so the footprint is 2.8 m by 1.4 m. */
const LEDGE_HALF: V3 = [1.4, 0.2, 0.7];
/** A mushroom: a springy ball sunk far enough that only the cap is standable. */
const MUSHROOM_RADIUS = 1.75;
const MUSHROOM_SINK = 1.15;
/**
 * A pillar: the *other* narrow landing, and a column rather than a slab.
 *
 * 1.35 m of radius is a cap you have to mean to hit — narrower than a ledge is
 * long, wider than a ledge is deep — and a cylinder cap is a real landing in
 * this engine, so the kind needs no pad under it and gets none. It used to emit
 * an ordinary platform *plus* a 0.6 m post standing two metres away, which was
 * the single largest source of the thin sticks the mountain was littered with:
 * seventeen poles that were not landings, were not scenery anybody had placed on
 * purpose, and did not even mark the node they belonged to.
 *
 * The column runs *down* from the node line rather than up from it. Up would put
 * a shaft in the airspace over the landing, which is the one place a climb needs
 * clear; down is what makes the cap read as the top of something rather than as
 * a disc hanging in the air. How far down is {@link pillarDepth}.
 */
const PILLAR_RADIUS = 1.35;
const PILLAR_DEPTH_MIN = 4;
const PILLAR_DEPTH_MAX = 7;
/**
 * Air a column keeps over whatever is under it, and the shortest it may be cut
 * back to — see {@link trimPillars}.
 *
 * The bargain: a column's depth is the one number on this mountain that is
 * decided without looking at anything else, and four to seven metres of solid
 * rock hung under a node is easily enough to reach a landing on the lap below.
 * So the wanted depth is a *wish*. Where the column would drive through
 * something, it is cut back to half a metre clear of it instead, and where even
 * that is not enough it stops at two metres — because a column that has been
 * trimmed to nothing is the flat disc hanging in the air this kind was rewritten
 * to stop building, and at that point the honest answer is a short column and a
 * self-test (§9) that fails the seed if the two really do still intersect.
 */
const PILLAR_CLEAR = 0.5;
const PILLAR_DEPTH_FLOOR = 2;
/**
 * The narrowest {@link padScales} may trim a column's cap to. Higher than
 * `PAD_MIN_SCALE`, which is a floor for *slabs*: two thirds of a platform is
 * still somewhere to stand, and a third of one is too, but a third of a 1.35 m
 * cap is a 45 cm stick — the exact shape this kind was rewritten to stop
 * producing. A pillar's reach is the smallest on the mountain, so it is seldom
 * the pad being asked to give ground anyway.
 */
const PILLAR_MIN_SCALE = 0.72;
/** A ramp's tilt: steep enough to redirect a bounce, well under the 50° slide angle. */
const RAMP_TILT_MIN = 18 * (Math.PI / 180);
const RAMP_TILT_MAX = 26 * (Math.PI / 180);
/**
 * Clear air two landings keep between their footprints, and how far either one
 * may be shrunk to find it. See {@link padScales}: a pair too crowded even at
 * the floor is left crowded, on the grounds that half a landing is worse than a
 * seam. `level.selftest.ts` §9 is what says whether that ever happens.
 */
const PAD_KEEP = 0.25;
const PAD_MIN_SCALE = 0.3;
/** Checkpoint pad geometry: a squat disc with four little corner posts. */
const CHECKPOINT_RADIUS = 2.6;
const CHECKPOINT_THICK = 0.5;
const CHECKPOINT_POST_HALF: V3 = [0.12, 0.5, 0.12];
/** Inner rim of a lobby floor — 0.6 m outside the shaft, so the drop stays clean. */
const LOBBY_INNER_R = 6.6;
/**
 * How far past the checkpoint's own radius the floor reaches. Small on purpose:
 * the rim is now the way up (see {@link sealLobbyApproaches}), so every metre of
 * apron is a metre added to the detour the last hops before a pad have to spend
 * going around it — and those hops answer to the same gap budget as every other.
 * Exported so `level.selftest.ts` measures the rim against the real number.
 */
export const LOBBY_OUTER_M = 0.5;
const LOBBY_HALF_THICK = 0.25;
/** Slabs in the ring. Exported so `level.selftest.ts` counts the real number. */
export const LOBBY_SEGMENTS = 14;
/**
 * Alternate slabs drop 4 cm. Neighbouring slabs overlap toward the inner rim
 * (see {@link emitLobbyFloor}), and two coplanar top faces would z-fight; 4 cm is
 * far below the 0.15 m tip radius, so nothing about the landing changes.
 */
const LOBBY_STEP = 0.04;
/**
 * A node with less air than this under the slab is "roofed" — under a sealed
 * floor that is a dead end, so it gets moved outside the ring instead.
 */
const LOBBY_CLEARANCE = 4.0;
/** How far outside the rim a relocated approach node is planted. */
const LOBBY_RIM_CLEAR = 1.0;
/**
 * The longest horizontal hop the rim detour is allowed to leave behind. Throwing
 * a node out to the rim stretches the hop that reaches it — from a node hard
 * against `R_MIN` to a rim a few metres past the waist is a stretch the 7 m
 * budget cannot absorb — so {@link sealApproaches} walks back down the lane
 * pulling predecessors outward until every hop is back under this. Just inside
 * the self-test's `MAX_GAP`.
 */
const LOBBY_APPROACH_GAP = 6.9;
/**
 * Roughly one node in three grows a prop, at this distance outward from it.
 *
 * The rate went *up* when the props were overhauled, which sounds like the wrong
 * direction for a mountain whose scenery had been reported as noise. It is not:
 * what made the old props read as noise was never how many there were, it was
 * that each one was a lone thin stick standing beside a platform for no reason
 * anybody could see. A prop is a **cluster** now — two or three pieces that
 * plainly belong to each other and to their zone — and a thing that reads as
 * something wants to be met often enough to be recognised.
 */
const DECOR_CHANCE = 0.33;
const DECOR_MIN_OFFSET = 4.5;
const DECOR_MAX_OFFSET = 6.5;
/**
 * The widest any zone's cluster reaches past its own centre, which is what
 * {@link outwardRoom} has to pay for out of the room left at the rim.
 *
 * The Sunken Ruins' fallen slab sets it, and by a clear margin: it lies 1.7 m
 * outward of the cluster's centre and its own half-diagonal is
 * `hypot(1.2, 0.8)` = 1.44, so it reaches 3.14 m and the yaw it is dropped at is
 * rolled, so it reaches that far whichever way it fell. Nothing else is close —
 * the Frozen snow mound is a 1.2 m ball 1.6 m out (2.8), the tallest Crystal
 * shard leans to about 2.8 as well, the Meadow bush is a 1.3 m ball 1.5 m out
 * (2.8), the Verdant canopy tops out at 2.7 and the Obsidian block at 2.2. This
 * is that worst case with a little rounding on top.
 */
const DECOR_REACH = 3.4;
/**
 * Closest to a node any outward prop is ever pushed, once {@link outwardRoom}
 * has taken the mountain's outer limit out of the asked-for distance. Far enough
 * that the prop is beside the landing rather than on it.
 */
const PROP_MIN_OFFSET = 2.5;
/**
 * Plaza + guard rail.
 *
 * Sized off the widest band rather than off the old trunk: the Meadow foot may
 * reach r = 30 and a climber who misses out there falls more or less straight
 * down, so a twenty-metre disc would have dropped half the opening zone into
 * nothing. The rail keeps its metre inside the rim and still stands three metres
 * outside the Meadow band's own, so a miss on the tutorial lands on the plaza
 * rather than past it; and it gains segments to stay the same length of fence
 * per metre of circumference.
 */
const PLAZA_RADIUS = 34;
const RAIL_RADIUS = 33;
const RAIL_SEGMENTS = 20;
const RAIL_HEIGHT = 1.2;

const SPAWN: V3 = [0, 1.5, -16];

const DEG = Math.PI / 180;

// --- Forks ------------------------------------------------------------------
// A fork is a *window* on the trunk: two trunk nodes, and an alternative way of
// getting from the lower one to the upper one. The window's length is measured
// in trunk nodes because that is what the branch has to beat — a branch is only
// worth taking if it climbs the same height in fewer hops.
//
// Where a branch may *go* is measured against its window's zone band rather than
// against one number the whole mountain shares: see `windowBand` and
// `laneRadiusMax`. "Out on the rim" is r = 28 on the Frozen cornice and r = 14
// in the Obsidian spire, and a fork is meant to be a place the mountain is wider
// than the trunk made it.

/**
 * Trunk nodes a fork window spans, min … and max.
 *
 * The floor is what it is because of how little room there is: a zone that forks
 * at all is 45 m tall, its lobby floor claims eight of them at each end, and what
 * is left has to hold two windows and the cooldown between them. Seven-node
 * windows fit once per zone and no more.
 */
const FORK_SPAN_MIN = 4;
const FORK_SPAN_MAX = 12;
/**
 * How many trunk nodes longer than the shortest one that works a window may be
 * and still be preferred for offering more motifs. Past that, the height the
 * longer window swallows is height the *next* fork needed, and a second fork is
 * worth more than a wider choice of what the first one is made of.
 */
const FORK_SPAN_INDULGENCE = 4;
/** Trunk nodes between one fork's exit and the next one's entry. */
const FORK_COOLDOWN = 2;
/**
 * No fork before this trunk index: the tutorial has to read as one path, and
 * Meadow Foothills is sixteen nodes of it.
 */
const FORK_MIN_INDEX = 18;
/**
 * How many forks each zone wants. Falling short is allowed; overshooting is not.
 * Zone 0 is the tutorial and gets none — one path, one idea — and every zone
 * above it gets two but the crown, where there is no longer room for a second.
 */
const FORKS_PER_ZONE: readonly number[] = [0, 2, 2, 2, 2, 1];
/**
 * How much of the shaft a window's trunk has to have gone round before an
 * `opposite` fork is on the table. Under the floor, "the other way" would be
 * nearly a whole lap and would read as one; over the ceiling there is barely a
 * gap left to come back through, and the branch would be a ladder.
 */
const SWEEP_MIN = 100 * DEG;
const SWEEP_MAX = 330 * DEG;
/**
 * The planner's per-hop rise ceiling for bold lanes (`level.selftest.ts` caps
 * 4.6). The margin is thin on purpose and can afford to be: a branch's heights
 * are not jittered, so this number is exactly what gets built.
 */
const BOLD_MAX_RISE = 4.35;
/** Per-hop rise the planner may ask of a hop *leaving* a mushroom (self-test caps 9). */
const MUSHROOM_RISE = 7.5;
/** Min centreline separation for parallel lanes at similar height. */
const LANE_RADIAL_SEP = 5.5;
/**
 * How far past its window's band a branch lane may hold. A fork is meant to be
 * a place the mountain is *wider* than the trunk made it, so a lane gets the
 * band the trunk was walking plus this — see {@link laneRadiusMax}, which is
 * what replaced the flat lane limit the whole climb used to share.
 */
const LANE_BAND_SLACK = 4;
/**
 * Self-test bound: no node, on any lane, ever sits further out than this.
 *
 * Two claims meet here and the larger wins. The widest band is the Meadow foot's
 * 30 m, which no fork ever adds to — the tutorial's quota is zero — and the
 * widest band a fork *can* open on is the Frozen cornice's 28 m plus
 * `LANE_BAND_SLACK`, which is 32. So this is the outer limit those two numbers
 * imply with two metres of rounding on top, and not a round figure chosen first.
 * Props are cut back against it in {@link outwardRoom}, and the mover pass
 * refuses any pose that leaves it.
 */
export const R_HARD_MAX = 34;
/**
 * The same limit for **scenery**, which is allowed further out than the climb is.
 *
 * The two used to be one number, and that was the quiet reason the mountain had
 * no landmarks: anything big enough to see from the plaza had to fit in the same
 * six metres of rim the pads were already using, so it either overlapped a route
 * or was not big enough to see. Splitting them costs nothing — decor is
 * collidable but nothing is *solved* against it — and buys a great tree, a gate
 * and a monolith standing outside the climb rather than in it.
 *
 * `level.selftest.ts` §5g holds landings to `R_HARD_MAX` and decor to this, so
 * the split is checked rather than merely intended.
 */
export const LANDMARK_R_MAX = 40;
/**
 * Junction pads are wider — two routes leave them.
 *
 * Not always by the full factor: `R_MIN` was chosen so that an *unscaled* pad's
 * 3.75 m half-diagonal still clears the hollow shaft, so a junction hard against
 * the inner limit gets whatever widening is left over and no more. Gating forks
 * on radius instead would throw away a third of the mountain's trunk nodes.
 */
const JUNCTION_PAD_SCALE = 1.45;
/** Radius a widened junction pad may not reach inside — the shaft, plus a hand's width. */
const JUNCTION_SHAFT_KEEP = 6.3;
/**
 * The planner's own hop budget, held a little inside the numbers
 * `level.selftest.ts` enforces (7 m, and 14 m off a mushroom): the lane is
 * solved on nominal geometry and then jittered, and the jitter has to fit in the
 * difference. `LANE_MIN_GAP` is the other end of it — a "hop" of a metre and a
 * half is a stack of pads, not a jump, and that is how a `parallel` fork over a
 * window the trunk barely turned in comes out: a ladder, every landing directly
 * over the last, which is exactly the fault the trunk's own de-stacking exists
 * to prevent. It was 1.5 m and it was not doing the job — most of the mountain's
 * remaining stacked pairs were two nodes of the same branch, four metres apart
 * vertically and under two horizontally. A branch may be steep; it may not be a
 * ladder.
 */
const LANE_GAP_MAX = 6.75;
const LANE_GAP_MAX_MUSHROOM = 13.0;
const LANE_MIN_GAP = 2.5;
/** Bound on the hop search in {@link fitLane}; a lane this long is a bug, not a fork. */
const LANE_HOPS_MAX = 24;
/**
 * Per-node jitter on a branch — a quarter of the trunk's, and only on the two
 * coordinates that are allowed to move. A bold lane's hops are already most of
 * what a bounce can clear, so the jitter's whole worst case (0.24 m across a
 * hop) has to fit inside the difference between `LANE_GAP_MAX` and the 7 m
 * `level.selftest.ts` enforces.
 */
const LANE_RADIAL_JITTER = 0.05;
const LANE_ANGLE_JITTER = 0.002;
/**
 * How much faster than the trunk stretch it replaces a branch's mean rise per
 * hop must be, in metres. `level.selftest.ts` asks for 0.6; the planner asks for
 * a little more so that a retune cannot walk a fork over the line. It can be
 * this close because both sides compute the same thing off the same two shared
 * nodes — nothing between here and the build moves either of them.
 */
const FORK_RISE_GAIN = 0.65;
/**
 * Nominal clearance the planner holds between a branch node and anything else at
 * a similar height, and the height window "similar" means. Again wider than the
 * self-test's 4 m / 3 m, to leave room for the jitter.
 */
const LANE_CLEARANCE = 4.5;
const LANE_CLEARANCE_Y = 3.5;
/**
 * What the two nodes a lane hangs directly off its junctions owe instead. They
 * are one hop from the pad they leave and are meant to crowd it, so the full
 * clearance is not on the table; this is the weaker promise that they are not
 * standing *on* something. Two landings this far apart still overlap, and
 * {@link padScales} is what trims them — but they are visibly two places, which
 * half a metre apart is not.
 */
const LANE_END_CLEARANCE = 2.6;
/**
 * The lane half of {@link STACK_CLEAR}: how far every branch node has to stand
 * from any trunk node within `LANE_STACK_DY` metres above or below it.
 *
 * The trunk steers itself out from under its own laps; a branch cannot, because
 * a lane is solved end to end and there is nothing to nudge. So it is *refused*
 * instead — the window keeps looking, at another radius or another motif or
 * another span, and if nothing there clears the trunk then this stretch of
 * mountain simply does not fork. A fork whose landings hang over the ones it
 * left is the ceiling the whole redesign is trying to get rid of, offered twice.
 *
 * The window's floor is three metres, and it is not decorative. Level-with
 * clearance owns everything within 3.5 m vertically at the same 4.5 m, so for a
 * lane's *middle* nodes the stacking window could indeed start wherever it liked
 * — but the two nodes hanging off the junctions owe only `LANE_END_CLEARANCE`
 * level with the trunk, and that left half a metre of height, from 3 m to 3.5 m,
 * where an end node could sit 2.6 m from a trunk node and be waved through as
 * "crowding the pad it came from". `level.selftest.ts` §5h calls anything past
 * three metres a ceiling, and it was right: fourteen counted pairs across a
 * hundred and twenty seeds came out of that half metre. So the floor is three,
 * the same three, and the end exemption stops where the test's window opens.
 */
const LANE_STACK_CLEAR = 4.5;
const LANE_STACK_DY_MIN = 3;
const LANE_STACK_DY = 28;
/**
 * Slack around a lobby floor's height band that no fork window may touch. Not
 * symmetric: the reroute a floor forces on its approach only ever reaches
 * *downward* (see {@link sealApproaches}), so the metres above a pad are cheap
 * and the ones below it are not.
 */
const LOBBY_FORK_BELOW = 2;
const LOBBY_FORK_ABOVE = 0.5;

// --- Motifs -----------------------------------------------------------------
// A motif is what a lane is *made of*: a radial line to hold, a deformation of
// it, and the node kinds the shape insists on. Everything below is the numbers
// those six shapes are cut from; the shapes themselves are in {@link MOTIFS}.

/**
 * Metres per hop a `gentle` lane must give *away* against the trunk stretch it
 * replaces. A `bold` lane's promise is speed (see `FORK_RISE_GAIN`); a gentle
 * one's is the opposite — more hops for the same height, so every step is
 * smaller than the trunk's. `level.selftest.ts` only asks that a branch differ
 * from its trunk stretch at all unless it is bold, which is what makes the
 * slower half of the bargain legal.
 */
const LANE_RISE_LOSS = 0.25;
/** How many hops past its window's span a gentle lane may spend — see {@link hopBounds}. */
const LANE_GENTLE_EXTRA = 10;
/** Tangential zig-zag between consecutive ledges, in metres. */
const LEDGE_WALL_STAGGER = 0.8;
/** Backdrop slabs behind a ledge wall: how far outward of the lane, and how big. */
const LEDGE_WALL_BACKDROP_OUT = 2.5;
const LEDGE_WALL_BACKDROP_HALF: V3 = [1.6, 3.0, 0.4];
const LEDGE_WALL_BACKDROP_MIN = 3;
const LEDGE_WALL_BACKDROP_MAX = 5;
/** How far the radius weaves either side of a `pillarForest`'s line. */
const PILLAR_FOREST_WEAVE = 2;
/**
 * Free-standing posts in a pillar forest: sideways offset, and how many.
 *
 * Fewer and fatter than they were. Six identical 0.6 m poles of identical height
 * alternating either side of a line is a *fence* — the eye reads the repetition
 * before it reads the shape, and a fence beside a route says nothing about the
 * route. Three or four columns of unequal height, one of them snapped off short,
 * is a ruined colonnade: the same idea, told once properly instead of six times.
 */
const PILLAR_GROVE_OFF = 3;
const PILLAR_GROVE_MIN = 3;
const PILLAR_GROVE_MAX = 4;
/**
 * A grove post's size band, and the height the snapped one keeps.
 *
 * Which post gets which height is pure geometry — {@link decorateLane} draws
 * nothing, by design, so the variation has to come off the lane's own numbers
 * rather than out of the stream.
 */
const PILLAR_GROVE_R_MIN = 1.0;
const PILLAR_GROVE_R_MAX = 1.3;
const PILLAR_GROVE_H_MIN = 2.5;
const PILLAR_GROVE_H_MAX = 6;
const PILLAR_GROVE_SNAPPED_H = 1.6;
/**
 * The wide low slab a set-piece node stands on, and the marker beside it.
 * `ISLAND_MIN_HALF` is the narrowest the slab is ever cut back to — see
 * {@link islandHalf}, and {@link LANE_RIM_KEEP}, which is that number turned
 * into the air a lane holds inside `R_HARD_MAX`.
 */
const ISLAND_HALF = 3.5;
const ISLAND_MIN_HALF = 1.5;
const ISLAND_THICK = 0.4;
const ISLAND_DROP = 0.6;
const ISLAND_MONUMENT_HALF: V3 = [0.4, 1.1, 0.4];
const ISLAND_MONUMENT_OFF = 2.2;
/**
 * How far past its window's band an `islandSpur` reaches at its apex. A spur
 * that stopped at the rim the rest of the zone already uses would not be a spur
 * — the point of the shape is that it goes somewhere nothing else does.
 */
const ISLAND_SPUR_OUT = 3;
/**
 * Air a branch lane keeps between its outermost legal radius and `R_HARD_MAX`:
 * exactly the half-diagonal of the narrowest slab {@link islandHalf} will ever
 * build, so an `islandSpur` parked on the lane limit still has its corners on
 * the mountain.
 */
const LANE_RIM_KEEP = ISLAND_MIN_HALF * Math.SQRT2;
/** How far an `islandSpur` sags below its own spine on the way out. */
const ISLAND_SPUR_SAG = 2.6;
/** How far below its entry anchor a branch node may ever sit. */
const LANE_MAX_DIP = 3;
/** How far one hop on a descending branch may drop (`level.selftest.ts` caps 8). */
const LANE_DROP_MAX = 6;
/** How far a `bridgeRun` bulges out past the line it was given. */
const BRIDGE_BULGE = 5;
/** Deck planks: half-extent, and how far apart along a span they are laid. */
const PLANK_HALF: V3 = [1.1, 0.12, 0.55];
const PLANK_STEP = 2.2;
/** The little posts at the ends of a decked run, and how far they stand proud of it. */
const BRIDGE_POST_HALF: V3 = [0.16, 0.45, 0.16];
const PLANK_POST_LIFT = 0.05;
/**
 * Lowest zone a trunk set piece may dress. Meadow and Verdant are the teaching
 * zones, with no ledge and no pillar of their own — both palettes alias the two
 * onto the platform and the ramp — so seasoning either would mean inventing
 * furniture the zone never had.
 */
const TRUNK_SET_PIECE_MIN_ZONE = 2;

// --- Landmarks --------------------------------------------------------------
// One set piece per zone, big enough to see from the zone below it. The climb
// does not touch them and is not solved against them: they are the answer to
// "where am I on this mountain", which a wandering polar walk otherwise makes
// genuinely hard to answer. See {@link emitLandmarks}.

/**
 * How far past the rim the mountain **actually built at its height** a
 * landmark's centre wants to stand.
 *
 * Not past the rim the zone's band *declares*, which is what it used to be and
 * was the quiet reason the landmarks read as decals rather than as parts of the
 * mountain. A band is permission, not a promise: where a zone's walk did not use
 * its own outer half, `rHi + 8` put the landmark eight metres past a rim nothing
 * had been built on — a frozen cascade hanging at r ≈ 36 with the shelf's own
 * landings topping out at 19, twenty-one metres of nothing between them and no
 * sense at all in which the thing was on the same mountain.
 *
 * At its height, and not over the whole zone, because a zone reaching r = 28 on
 * one spur and walking the rest of itself at 14 has two rims and only one of them
 * is anywhere near the landmark. Measured that way the offset can be smaller
 * *and* mean more: six metres past the outermost landing at that height is a
 * landmark you pass, and the swing below then moves it round the shaft until it
 * is not in anybody's way.
 */
const LANDMARK_OUT = 6;
/** Air a landmark tries to keep between itself and every landing near its height. */
const LANDMARK_KEEP = 1.5;
/**
 * Half-width of the height window a landmark reads its zone's rim off, and the
 * tangential steps it may take to find room.
 *
 * The anchor is simply the trunk node nearest the zone's mid-height — it
 * supplies a height and a starting angle and nothing else, because the radius
 * comes off the rim the mountain built around that height (see
 * {@link LANDMARK_OUT}) rather than off the anchor. It used to be the node with
 * the *most room outward* in the window, and both halves of that were wrong once
 * the radius stopped coming from it. A spire tapers, so "least radius in a
 * window" is always the highest node in that window, and the Obsidian monolith
 * duly ended up level with the summit disc it is supposed to be the last thing
 * before. And a landmark hung off the node furthest *inside* a zone, at a radius
 * taken from the rim, is a landmark eighteen metres from its own anchor — the
 * exact "floating in a void" the built-rim radius was meant to cure.
 *
 * The swing is a *distance* round the shaft rather than an angle, for the same
 * reason the walk's drifts are fractions of a band: seven degrees is four metres
 * of arc out on the Verdant rim and two on the Obsidian spire, so an angular
 * step overshot the first clear line by metres exactly where the landmarks are
 * biggest and there is least room to overshoot into. Twenty-five metres of total
 * search either way, in steps of `LANDMARK_STEP`.
 */
const LANDMARK_WINDOW = 12;
const LANDMARK_STEP = 2.5;
const LANDMARK_SWINGS = 10;

// ---------------------------------------------------------------------------
// Palette — one shared Material object per distinct (colour, physics, tag).
// `levelMesh.ts` batches by colour, so a zone's private palette costs nothing
// beyond the draw calls its colours already imply.
//
// Tags stay the *functional* kind regardless of zone: physics and level stats
// key on the tag, and a frozen ramp is still a ramp.
// ---------------------------------------------------------------------------

const MAT_PLAZA: Material = { color: 0x4a5d4a, tag: 'plaza' };
const MAT_RAIL: Material = { color: 0x6b7a5f, tag: 'rail' };
const MAT_SUMMIT: Material = { color: 0xd4af37, tag: 'summit' };
/** Mushrooms are the same springy red everywhere — the one surface you must read at a glance. */
const MAT_MUSHROOM: Material = { color: 0xd43f3f, tag: 'mushroom', bounceMultiplier: 1.9 };
/** One funnel look for the whole climb: seeing it must mean exactly one thing. */
const MAT_FUNNEL: Material = { color: 0x3f4a55, tag: 'funnel' };
/** Warm teal, chosen to read as "safe" against all six zone palettes. */
const MAT_CHECKPOINT: Material = { color: 0x2fa08c, tag: 'checkpoint' };
/**
 * Junction arrows, one colour per lane role, so what a fork is made of is
 * readable from the pad you decide on rather than from the first hop.
 *
 * A bold branch wears the mushroom's red, because red already means "this one
 * launches you" everywhere else on the mountain; a safe one wears the
 * checkpoint's teal, which already means "this one is looking after you"; a
 * scenic one wears a warm sand that belongs to neither. Tagged `decor`, not
 * `mushroom` or `checkpoint` — they are paint, and must not inherit a bounce or
 * be mistaken for a pad to claim.
 */
const MAT_MARK_BOLD: Material = { color: 0xd43f3f, tag: 'decor' };
const MAT_MARK_SAFE: Material = { color: 0x2fa08c, tag: 'decor' };
const MAT_MARK_SCENIC: Material = { color: 0xd9b26a, tag: 'decor' };

// --- 1. Meadow Foothills ----------------------------------------------------
// Sunlit and a shade lighter than everything above it, so the first thirty
// metres read as open ground rather than as the bottom of the forest — you can
// see at a glance that this is the part of the mountain that is being kind.
const MAT_MEADOW_PLATFORM: Material = { color: 0x7a9a58, tag: 'platform' };
const MAT_MEADOW_RAMP: Material = { color: 0x6c8a4e, tag: 'ramp' };
/**
 * The zone's generic decor brown — what {@link zoneDecorMaterial} hands back for
 * anything the Meadow builds that has no colour of its own. Nothing on the
 * shipped mountain asks: the foot never forks, never spurs and carries no set
 * piece, so it has no island monument, no ledge-wall backdrop and no bridge post
 * to paint. The table stays uniform anyway, for the same reason the zone's
 * `ledge` and `pillar` are aliases rather than holes.
 */
const MAT_MEADOW_TRUNK: Material = { color: 0x8a6440, tag: 'decor' };
const MAT_MEADOW_CANOPY: Material = { color: 0x69a44a, tag: 'decor' };
/** The one accent nothing else on the mountain owns: flowers, and only here. */
const MAT_MEADOW_BLOOM: Material = { color: 0xe8c65a, tag: 'decor' };
const MAT_MEADOW_STONE: Material = { color: 0x9a9282, tag: 'decor' };

// --- 2. Verdant Steps -------------------------------------------------------
const MAT_VERDANT_PLATFORM: Material = { color: 0x5d7a4e, tag: 'platform' };
const MAT_VERDANT_RAMP: Material = { color: 0x4e6a42, tag: 'ramp' };
const MAT_VERDANT_TRUNK: Material = { color: 0x6b4a33, tag: 'decor' };
const MAT_VERDANT_CANOPY: Material = { color: 0x3f6d35, tag: 'decor' };
// The y = 30 boundary opens Verdant, so this is the first zone whose floor
// materials are actually drawn: two adjacent green-browns rather than the
// platform/ramp aliases they used to be, so the alternating slabs read as one
// floor stepped rather than as two kinds of surface.
const MAT_VERDANT_FLOOR: Material = { color: 0x55703f, tag: 'floor' };
const MAT_VERDANT_FLOOR_ALT: Material = { color: 0x4a6238, tag: 'floor' };

// --- 3. Sunken Ruins --------------------------------------------------------
const MAT_RUINS_PLATFORM: Material = { color: 0xb09b74, tag: 'platform' };
const MAT_RUINS_RAMP: Material = { color: 0xa08a63, tag: 'ramp' };
const MAT_RUINS_LEDGE: Material = { color: 0xc4b18a, tag: 'ledge' };
const MAT_RUINS_PILLAR: Material = { color: 0x9a8663, tag: 'pillar' };
const MAT_RUINS_COLUMN: Material = { color: 0x8d7a58, tag: 'decor' };
const MAT_RUINS_SLAB: Material = { color: 0xb5a486, tag: 'decor' };
const MAT_RUINS_FLOOR: Material = { color: 0x9c8760, tag: 'floor' };
const MAT_RUINS_FLOOR_ALT: Material = { color: 0x8a7654, tag: 'floor' };

// --- 4. Crystal Hollow ------------------------------------------------------
const MAT_CRYSTAL_PLATFORM: Material = { color: 0x5a4a7a, tag: 'platform' };
const MAT_CRYSTAL_RAMP: Material = { color: 0x4a3d66, tag: 'ramp' };
const MAT_CRYSTAL_LEDGE: Material = { color: 0x7a6aa0, tag: 'ledge' };
const MAT_CRYSTAL_PILLAR: Material = { color: 0x4a8f96, tag: 'pillar' };
const MAT_CRYSTAL_SHARD_A: Material = { color: 0x8a5fd0, tag: 'decor' };
const MAT_CRYSTAL_SHARD_B: Material = { color: 0x4fd0c8, tag: 'decor' };
const MAT_CRYSTAL_FLOOR: Material = { color: 0x453a63, tag: 'floor' };
const MAT_CRYSTAL_FLOOR_ALT: Material = { color: 0x3a3055, tag: 'floor' };

// --- 5. Frozen Shelf --------------------------------------------------------
// `frictionMultiplier` scales the tangential velocity kept on impact, so > 1 is
// "slippery": you skate on landing. Ramps here are 18°–26°, far below the 50°
// slide angle, so this never touches the slide path — and it stays well under
// 1.5, past which a flat landing stops being recoverable at all.
const ICE_FRICTION = 1.45;
const MAT_FROZEN_PLATFORM: Material = {
  color: 0xa8c4d4,
  tag: 'platform',
  frictionMultiplier: ICE_FRICTION,
};
const MAT_FROZEN_RAMP: Material = { color: 0x8fb3c9, tag: 'ramp', frictionMultiplier: ICE_FRICTION };
const MAT_FROZEN_LEDGE: Material = {
  color: 0xc2d8e4,
  tag: 'ledge',
  frictionMultiplier: ICE_FRICTION,
};
const MAT_FROZEN_PILLAR: Material = { color: 0x7fa0b8, tag: 'pillar' };
const MAT_FROZEN_SPIKE: Material = { color: 0xd4e8f2, tag: 'decor' };
const MAT_FROZEN_SNOW: Material = { color: 0xeef4f8, tag: 'decor' };
// The lobby floor is the one place in this zone you are meant to be able to
// stand still, so it deliberately skips `ICE_FRICTION` — a checkpoint you skate
// off is not a rest stop.
const MAT_FROZEN_FLOOR: Material = { color: 0x92aec1, tag: 'floor' };
const MAT_FROZEN_FLOOR_ALT: Material = { color: 0x82a0b4, tag: 'floor' };

// --- 6. Obsidian Crown ------------------------------------------------------
const MAT_OBSIDIAN_PLATFORM: Material = { color: 0x2e2a33, tag: 'platform' };
const MAT_OBSIDIAN_RAMP: Material = { color: 0x262230, tag: 'ramp' };
const MAT_OBSIDIAN_LEDGE: Material = { color: 0x3d3844, tag: 'ledge' };
const MAT_OBSIDIAN_PILLAR: Material = { color: 0x1f1c26, tag: 'pillar' };
const MAT_OBSIDIAN_BASALT: Material = { color: 0x1a1720, tag: 'decor' };
const MAT_OBSIDIAN_GOLD: Material = { color: 0xd4af37, tag: 'decor' };
const MAT_OBSIDIAN_FLOOR: Material = { color: 0x35303d, tag: 'floor' };
const MAT_OBSIDIAN_FLOOR_ALT: Material = { color: 0x2b2733, tag: 'floor' };

// --- Movers and hazards -----------------------------------------------------
// A landing that travels has to be readable from further off than one that does
// not — you are choosing whether to wait for it from two hops away — so every
// zone's moving pad is that zone's own colour lifted most of the way toward its
// highlight. Same tag and same physics as the surface it replaces: a moving
// ledge is still a ledge, and a Frozen slider is still ice, or riding one would
// be the one place on the shelf you could stand still.
const MAT_MEADOW_MOVER: Material = { color: 0xa8cc80, tag: 'platform' };
const MAT_VERDANT_MOVER: Material = { color: 0x8fbd6e, tag: 'platform' };
const MAT_RUINS_MOVER: Material = { color: 0xe0c894, tag: 'platform' };
const MAT_RUINS_MOVER_LEDGE: Material = { color: 0xe8d8b4, tag: 'ledge' };
const MAT_CRYSTAL_MOVER: Material = { color: 0x9078c8, tag: 'platform' };
const MAT_CRYSTAL_MOVER_LEDGE: Material = { color: 0xa898d0, tag: 'ledge' };
const MAT_FROZEN_MOVER: Material = {
  color: 0xdcf0fa,
  tag: 'platform',
  frictionMultiplier: ICE_FRICTION,
};
const MAT_FROZEN_MOVER_LEDGE: Material = {
  color: 0xe8f6ff,
  tag: 'ledge',
  frictionMultiplier: ICE_FRICTION,
};
const MAT_OBSIDIAN_MOVER: Material = { color: 0x5f5670, tag: 'platform' };
const MAT_OBSIDIAN_MOVER_LEDGE: Material = { color: 0x6b6478, tag: 'ledge' };
/**
 * The Crystal Hollow's blinking pads wear the zone's own shard hues rather than
 * its mover colour, alternating with the phase: the two halves of a blink field
 * are never solid at the same moment, and colouring them apart is what says so
 * before you are standing on the wrong one.
 */
const MAT_CRYSTAL_BLINK_A: Material = { color: 0x8a5fd0, tag: 'platform' };
const MAT_CRYSTAL_BLINK_B: Material = { color: 0x4fd0c8, tag: 'platform' };
/**
 * One warning colour for every hazard on the mountain, for the same reason the
 * mushrooms are one red and the funnels one slate: a surface that hurts must
 * mean exactly one thing wherever you meet it. Tagged `hazard`, so nothing can
 * mistake a sweeper bar for something to aim at.
 */
const MAT_HAZARD: Material = { color: 0x8a3038, tag: 'hazard' };

// ---------------------------------------------------------------------------
// Small quaternion helpers (no three.js in shared/)
// ---------------------------------------------------------------------------

/** Rotation about +Y. Maps local +X to `(cos yaw, 0, -sin yaw)`. */
function quatYaw(yaw: number): V4 {
  const h = yaw * 0.5;
  return [0, Math.sin(h), 0, Math.cos(h)];
}

/** Rotation about local +X. Positive angles tip local +Z downward. */
function quatTiltX(angle: number): V4 {
  const h = angle * 0.5;
  return [Math.sin(h), 0, 0, Math.cos(h)];
}

/** Hamilton product `a ⊗ b` — apply `b` first, then `a`. */
function quatMul(a: V4, b: V4): V4 {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

/**
 * Yaw that aligns a box's local +X with the counter-clockwise tangent of a ring
 * at angle `theta`. As a side effect local +Z ends up pointing *inward*, toward
 * the shaft — which is what the funnel and rail tilts rely on.
 */
function tangentYaw(theta: number): number {
  return Math.atan2(-Math.cos(theta), -Math.sin(theta));
}

/**
 * Yaw that aligns a box's local +X with the world horizontal direction
 * `(dx, dz)`. `quatYaw` maps local +X to `(cos yaw, −sin yaw)` in (x, z), so the
 * z component picks up the sign flip. Nodes use this rather than `tangentYaw`
 * because the path reverses and bulges: the long axis has to follow where the
 * course actually goes, not where a ring at that angle would.
 */
function yawAlong(dx: number, dz: number): number {
  return Math.atan2(-dz, dx);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ---------------------------------------------------------------------------
// Zones
// ---------------------------------------------------------------------------

/** What sits at a node. Every kind is a landing surface — a `pillar`'s cap too. */
export type NodeKind =
  | 'platform'
  | 'ledge'
  | 'ramp'
  | 'mushroom'
  | 'pillar'
  | 'funnel'
  | 'checkpoint';

/**
 * The zone-local materials a node can be built from, plus the two the zone's
 * lobby floor alternates between — a floor wears the palette of the zone it
 * *opens*, so these are read off the zone above the boundary, not the one below.
 */
interface ZonePalette {
  platform: Material;
  ramp: Material;
  ledge: Material;
  pillar: Material;
  floor: Material;
  floorAlt: Material;
  /** The same two landings again, in the shade a *moving* one wears. */
  mover: Material;
  moverLedge: Material;
}

interface Zone {
  /** Human name, for reading the table — the generator never branches on it. */
  name: string;
  /** Progress fraction this zone ends at; the previous entry's `uEnd` starts it. */
  uEnd: number;
  /** Horizontal distance between consecutive nodes here, in metres. */
  chord: number;
  /** Mean vertical step here, in metres, before the global normalisation. */
  rise: number;
  /**
   * The radial band this zone walks in. This is the mountain's **silhouette**:
   * a zone that is 20 m wide at the bottom and 4 m wide at the top is a shape
   * you can see from the plaza, and one whose stretches of climb cannot all
   * look the same. `rLo` may never go under `R_MIN` — that promise is about a
   * pad's corners and the hollow shaft, and no zone gets to waive it; a table
   * entry that tried would be clamped rather than obeyed.
   *
   * The band a *node* is actually walked in is this one eased: see
   * {@link bandAlongTrunk}, which clamps the floor, blends across the seams and
   * pinches to a waist at every checkpoint. Whether a zone then *uses* the band
   * it was given is a property of the seed, and one `level.sweep.ts` scores —
   * see `ZONE_BANDS`, which is this table as the sweep reads it.
   */
  rLo: number;
  rHi: number;
  /** Half-extent of this zone's landing pad — wide and forgiving low, mean high. */
  padHalf: V3;
  /** Relative frequency of each node kind. `checkpoint` is forced, never rolled. */
  weights: Array<[NodeKind, number]>;
  /**
   * Relative frequency of each walk pattern here — the *other* half of a zone's
   * shape, and the half that decides what the band gets used for. A wide band
   * full of arcs is a wide circle; the same band full of spurs is a set of arms.
   * Read in table order by `pickWeighted`, so that order is part of the seed.
   */
  patterns: Array<[Pattern, number]>;
  mats: ZonePalette;
}

/**
 * Progress fraction at a given height. Heights are near-linear in progress (the
 * rises are normalised over the whole climb), so this is exact enough to place a
 * zone boundary within a node or two of the height it names.
 */
function uAtY(y: number): number {
  return (y - FIRST_NODE_Y) / (LAST_NODE_Y - FIRST_NODE_Y);
}

/**
 * Heights the five zone boundaries — and so the five checkpoints — sit at.
 * Exported so the client can crossfade the sky against the real numbers.
 */
export const ZONE_BOUNDARY_YS: readonly number[] = [30, 75, 120, 165, 210];

const ZONES: readonly Zone[] = [
  {
    name: 'Meadow Foothills',
    uEnd: uAtY(30),
    chord: 4.2,
    rise: 1.7,
    // The foot: the widest thing on the mountain, and the reason the plaza is
    // 34 m across and the rail 33. A tutorial wants room to be wrong in, and
    // this one is given the whole skirt of the mountain to be wrong on — the
    // band's rim stands three metres inside the fence, so a miss out here lands
    // on the plaza rather than past it.
    //
    // The rim is 30 and not the 32 the fence would allow because of what stands
    // on a node rather than where the node is. This zone's pads are 8 m by 6 m,
    // a half-diagonal of exactly 5, and no other zone comes within a metre and a
    // half of that — so the Meadow rim is the one place on the mountain where
    // the widest *primitive* is set by the band rather than by a junction or an
    // island. At 32 a corner reached 37.0 m, which is `R_PRIM_MAX` to the
    // centimetre: a limit with no margin in it is a limit that gets widened the
    // first time it fires. At 30 the corner reaches 35 — a metre past the plaza
    // rim, so the foot overhangs the disc it stands on by a hand's width, which
    // is what a foot should do — and the check has two honest metres left.
    rLo: 18,
    rHi: 30,
    // Way wider than anything above it: 8 m by 6 m of ground to land on, so the
    // first thirty metres are about learning the bounce rather than about aim.
    //
    // Wide enough to interact with its own rise, and knowingly. A *ramp* this
    // size, tilted at the roll's steepest 26°, reaches 1.3 m above and below its
    // node line — more than the 1.7 m rise leaves clear — so wherever the roll
    // does put one here {@link padScales} trims it and its neighbours to fit.
    // That is the trimming pass doing its job rather than a fault; it is also
    // why the ramp weight below is only 5, and worth re-reading if the rise or
    // the pad ever changes.
    padHalf: [4.0, 0.25, 3.0],
    // Nothing narrow and nothing punishing at all: no ledge, no pillar, no
    // funnel, and barely a ramp. The opening is a tutorial and says so.
    weights: [
      ['platform', 85],
      ['mushroom', 10],
      ['ramp', 5],
    ],
    // One plain arc, the whole zone. `OPENING_ARC_NODES` forces it anyway over
    // exactly this many nodes; the table says the same thing so that the two can
    // never disagree, and so that the segment roll draws what every other zone's
    // draws — the stream must not depend on which zone it is standing in.
    patterns: [['arc', 100]],
    // Meadow never rolls a ledge or a pillar, and no lobby floor ever wears the
    // *first* zone's palette (a floor wears the palette of the zone it opens,
    // and the lowest boundary opens Verdant); the aliases keep the table uniform
    // without inventing colours that are never drawn.
    mats: {
      platform: MAT_MEADOW_PLATFORM,
      ramp: MAT_MEADOW_RAMP,
      ledge: MAT_MEADOW_PLATFORM,
      pillar: MAT_MEADOW_RAMP,
      floor: MAT_MEADOW_PLATFORM,
      floorAlt: MAT_MEADOW_RAMP,
      mover: MAT_MEADOW_MOVER,
      moverLedge: MAT_MEADOW_MOVER,
    },
  },
  {
    name: 'Verdant Steps',
    uEnd: uAtY(75),
    chord: 4.7,
    rise: 2.1,
    // The skirt, drawn in: still broad, but plainly narrower than the ground
    // that taught you, so the first thing the mountain does after the tutorial
    // is take some of it back.
    rLo: 15,
    rHi: 27,
    padHalf: [3.0, 0.25, 2.0],
    // Still forgiving, but the meadow's wide ground is gone and the mushrooms
    // come twice as thick: this is where the climb stops teaching you the bounce
    // and starts asking you to aim it.
    weights: [
      ['platform', 70],
      ['mushroom', 20],
      ['ramp', 10],
    ],
    // Gentle: mostly arcs, and a spur when the skirt wants a lobe in it. A
    // switchback is a thing to meet once you know what you are doing.
    patterns: [
      ['arc', 60],
      ['switchback', 10],
      ['spur', 20],
      ['tighten', 10],
    ],
    // Verdant never rolls a ledge or a pillar either, so those two stay aliases —
    // but the y = 30 boundary *opens* Verdant, so its floor pair is real geometry
    // and gets two colours of its own.
    mats: {
      platform: MAT_VERDANT_PLATFORM,
      ramp: MAT_VERDANT_RAMP,
      ledge: MAT_VERDANT_PLATFORM,
      pillar: MAT_VERDANT_RAMP,
      floor: MAT_VERDANT_FLOOR,
      floorAlt: MAT_VERDANT_FLOOR_ALT,
      mover: MAT_VERDANT_MOVER,
      moverLedge: MAT_VERDANT_MOVER,
    },
  },
  {
    name: 'Sunken Ruins',
    uEnd: uAtY(120),
    chord: 5.3,
    rise: 2.5,
    // Terraces: a band tighter than the skirt below it, so the skirt reads as a
    // skirt. The waist at y = 75 is the seam between the two.
    rLo: 11,
    rHi: 21,
    padHalf: [2.5, 0.25, 1.8],
    weights: [
      ['platform', 45],
      ['pillar', 20],
      ['ramp', 20],
      ['ledge', 10],
      ['mushroom', 5],
    ],
    // Doubling back on itself: switchback-led, which in a narrow band is what
    // makes a terrace rather than a helix.
    patterns: [
      ['arc', 30],
      ['switchback', 35],
      ['spur', 15],
      ['tighten', 20],
    ],
    mats: {
      platform: MAT_RUINS_PLATFORM,
      ramp: MAT_RUINS_RAMP,
      ledge: MAT_RUINS_LEDGE,
      pillar: MAT_RUINS_PILLAR,
      floor: MAT_RUINS_FLOOR,
      floorAlt: MAT_RUINS_FLOOR_ALT,
      mover: MAT_RUINS_MOVER,
      moverLedge: MAT_RUINS_MOVER_LEDGE,
    },
  },
  {
    name: 'Crystal Hollow',
    uEnd: uAtY(165),
    chord: 5.7,
    rise: 2.8,
    // Arms. The widest band after the skirt, but reached by spurs rather than
    // lived in: the climb keeps leaving the core and coming back to it, which is
    // what a crystal growth looks like from underneath.
    rLo: 10,
    rHi: 26,
    padHalf: [2.4, 0.25, 1.7],
    // First zone that can roll its own funnels.
    weights: [
      ['platform', 45],
      ['ramp', 15],
      ['ledge', 10],
      ['mushroom', 10],
      ['pillar', 10],
      ['funnel', 10],
    ],
    patterns: [
      ['arc', 25],
      ['switchback', 20],
      ['spur', 40],
      ['tighten', 15],
    ],
    mats: {
      platform: MAT_CRYSTAL_PLATFORM,
      ramp: MAT_CRYSTAL_RAMP,
      ledge: MAT_CRYSTAL_LEDGE,
      pillar: MAT_CRYSTAL_PILLAR,
      floor: MAT_CRYSTAL_FLOOR,
      floorAlt: MAT_CRYSTAL_FLOOR_ALT,
      mover: MAT_CRYSTAL_MOVER,
      moverLedge: MAT_CRYSTAL_MOVER_LEDGE,
    },
  },
  {
    name: 'Frozen Shelf',
    uEnd: uAtY(210),
    chord: 6.0,
    rise: 3.1,
    // The cornice: a broad ring with a floor to it, high and wide enough that
    // the Obsidian spire above it has something to rise out of.
    rLo: 14,
    rHi: 28,
    padHalf: [2.3, 0.25, 1.6],
    weights: [
      ['platform', 45],
      ['ramp', 20],
      ['ledge', 15],
      ['funnel', 10],
      ['pillar', 10],
    ],
    // Wandering: no one pattern owns the shelf, which is what a shelf is.
    patterns: [
      ['arc', 40],
      ['switchback', 25],
      ['spur', 25],
      ['tighten', 10],
    ],
    mats: {
      platform: MAT_FROZEN_PLATFORM,
      ramp: MAT_FROZEN_RAMP,
      ledge: MAT_FROZEN_LEDGE,
      pillar: MAT_FROZEN_PILLAR,
      floor: MAT_FROZEN_FLOOR,
      floorAlt: MAT_FROZEN_FLOOR_ALT,
      mover: MAT_FROZEN_MOVER,
      moverLedge: MAT_FROZEN_MOVER_LEDGE,
    },
  },
  {
    name: 'Obsidian Crown',
    uEnd: 1,
    chord: 6.2,
    rise: 3.3,
    // The spire. Four metres of band, hard against the shaft: everything up
    // here is the same narrow coil, and the summit flare at r = 12 sits in the
    // middle of it, so the last hops need no special pleading.
    rLo: 10,
    rHi: 14,
    padHalf: [2.1, 0.25, 1.5],
    // No mushrooms up here: the last turn gives nothing back.
    weights: [
      ['platform', 40],
      ['ledge', 25],
      ['ramp', 15],
      ['pillar', 10],
      ['funnel', 10],
    ],
    // Coiling: in a four-metre band a spur has nowhere to go, so the shape has
    // to come from reversals and from closing on the shaft.
    patterns: [
      ['arc', 30],
      ['switchback', 30],
      ['spur', 10],
      ['tighten', 30],
    ],
    mats: {
      platform: MAT_OBSIDIAN_PLATFORM,
      ramp: MAT_OBSIDIAN_RAMP,
      ledge: MAT_OBSIDIAN_LEDGE,
      pillar: MAT_OBSIDIAN_PILLAR,
      floor: MAT_OBSIDIAN_FLOOR,
      floorAlt: MAT_OBSIDIAN_FLOOR_ALT,
      mover: MAT_OBSIDIAN_MOVER,
      moverLedge: MAT_OBSIDIAN_MOVER_LEDGE,
    },
  },
];

/**
 * The zone names, bottom to top. Read off the table rather than copied, so a
 * renamed zone renames itself everywhere the player is told where they are.
 */
export const ZONE_NAMES: readonly string[] = ZONES.map((z) => z.name);

/**
 * The radial band each zone declares, bottom to top — the mountain's silhouette
 * as the table states it, with `rLo` already held to `R_MIN` exactly as
 * {@link bandAlongTrunk} holds it.
 *
 * Exported so `level.sweep.ts` can score a seed on how much of its own band each
 * zone actually walked, which is the difference between a mountain with a shape
 * and a mountain with a table describing one. Read off `ZONES` rather than
 * copied, for the same reason `ZONE_NAMES` is: a retuned band has to move the
 * thing that judges it.
 */
export const ZONE_BANDS: ReadonlyArray<{ lo: number; hi: number }> = ZONES.map((z) => ({
  lo: Math.max(z.rLo, R_MIN),
  hi: z.rHi,
}));

/** Which zone owns progress fraction `u ∈ [0, 1]`. */
function zoneIndexAt(u: number): number {
  for (let i = 0; i < ZONES.length - 1; i++) {
    if (u < (ZONES[i] as Zone).uEnd) return i;
  }
  return ZONES.length - 1;
}

// ---------------------------------------------------------------------------
// Node layout
// ---------------------------------------------------------------------------

interface Node {
  /** World position of the node's landing surface (top face centre). */
  x: number;
  y: number;
  z: number;
  /**
   * World angle around the shaft. Accumulated along the walk rather than wrapped,
   * so it runs past ±π and *decreases* wherever a switchback has turned the
   * course around — which is what lets a funnel read the signed step ahead of it.
   */
  theta: number;
  /** Distance from the shaft axis. */
  radius: number;
  /** Yaw that aligns local +X with the course direction here. */
  yaw: number;
  /** Index into `ZONES`, from this node's progress along the climb. */
  zone: number;
  kind: NodeKind;
  /**
   * Which lane of the {@link Course} owns this node — an index into
   * `Course.lanes`. The stretch of climb every route shares is the trunk, lane
   * 0; a fork's own nodes carry that fork's lane id.
   */
  lane: number;
  /**
   * How far a `pillar`'s column actually runs below the node line, once
   * {@link trimPillars} has cut the wanted depth back to what the geometry under
   * it leaves room for. Absent until the course is settled, and meaningless on
   * every other kind; {@link pillarDepth} is what everything reads it through, so
   * a caller can never accidentally get the wish instead of the answer.
   */
  drop?: number;
}

function zoneOf(node: Node): Zone {
  return ZONES[node.zone] as Zone;
}

/**
 * What a lane is *for*. The trunk is the one line every climb takes; the rest
 * are alternatives that leave it and come back, each with a promise attached —
 * `safe` costs distance and gives it back in margin, `bold` is the short way
 * with the small landings, `scenic` is neither but is worth looking at.
 */
export type LaneRole = 'trunk' | 'safe' | 'bold' | 'scenic';

/**
 * The shape a lane's nodes are built into. A lane is a stretch of climb with one
 * idea in it, so that taking a fork twice tells you something: the ledge wall is
 * always the ledge wall. What each one *is* lives in {@link MOTIFS}.
 */
export type MotifId =
  | 'traverse'
  | 'ledgeWall'
  | 'mushroomFlight'
  | 'pillarForest'
  | 'islandSpur'
  | 'bridgeRun';

/**
 * One traversable line through the course.
 *
 * A lane leaves the climb at a node it shares with whatever it branched off,
 * runs its own `body` of nodes, and rejoins at another shared node. `entry` and
 * `exit` are therefore *not* the lane's own nodes — they belong to the lane they
 * were split from, which is what lets two lanes agree on where a fork opens and
 * where it closes without duplicating the geometry at either end.
 */
interface Lane {
  id: number;
  role: LaneRole;
  motif: MotifId;
  /** Index into `Course.nodes` of the shared node this lane leaves from. */
  entry: number;
  /** Index into `Course.nodes` of the shared node it rejoins at. */
  exit: number;
  /** This lane's own (unshared) node indices, in climb order. */
  body: number[];
}

/**
 * The laid-out course: every node, and the lanes that thread them.
 *
 * The flat `nodes` array is still the emission order and still what the hash is
 * taken over — but it is no longer the *path*. Anything that asks "what comes
 * next" has to ask a lane (see {@link lanePath}), because the next change gives
 * the climb forks and a flat array cannot say which of two nodes follows a
 * split. Today there is exactly one lane, the trunk, and every lane-aware
 * lookup collapses to `i ± 1` — which is the point: the behaviour is identical,
 * the seam is in place.
 */
interface Course {
  nodes: Node[];
  lanes: Lane[];
  /**
   * Which pattern the polar walk was running at each *trunk* node, and which
   * segment of the walk that node belongs to. Both are indexed by trunk index,
   * which is also the flat index — every branch node is appended after the trunk
   * is laid, so the first `pattern.length` entries of `nodes` are the trunk in
   * order. This is what lets the trunk wear set pieces that match the shape it
   * is already making: ledges down the inside of a `tighten`, columns through
   * the turn of a `switchback`, an island on the tip of a `spur`.
   */
  pattern: Pattern[];
  segment: number[];
  /**
   * Nodes that stand on a wide low slab — a spur's tip on the trunk, and the
   * apex of every `islandSpur` branch. Not a `NodeKind`: the node is still
   * whatever it was, the island is what it is standing on.
   */
  islands: Set<number>;
}

/**
 * The nodes a lane is climbed through, in order: `[entry, ...body, exit]`.
 *
 * The trunk is the exception — it has no lane to leave from, so its path is
 * simply every node it owns, in flat order. (With `entry = 0`, `exit = n − 1`
 * and `body = 1 … n − 2` the two definitions agree; reading membership off the
 * nodes keeps them agreeing once forks start inserting their nodes into the
 * middle of the array.)
 */
function lanePath(course: Course, lane: Lane): number[] {
  if (lane.role === 'trunk') {
    const path: number[] = [];
    for (let i = 0; i < course.nodes.length; i++) {
      if ((course.nodes[i] as Node).lane === lane.id) path.push(i);
    }
    return path;
  }
  return [lane.entry, ...lane.body, lane.exit];
}

/**
 * For every node, the node before and after it *along the lane that owns it* —
 * or −1 where there is none. Shared entry/exit nodes answer with their own
 * lane's neighbours rather than a branch's, so a fork can never rewrite where
 * the trunk thinks it is going.
 *
 * On a trunk-only course this is exactly `i − 1` and `i + 1`.
 */
function laneNeighbours(course: Course): { prev: number[]; next: number[] } {
  const prev = new Array<number>(course.nodes.length).fill(-1);
  const next = new Array<number>(course.nodes.length).fill(-1);
  for (const lane of course.lanes) {
    const path = lanePath(course, lane);
    for (let k = 1; k < path.length; k++) {
      const a = path[k - 1] as number;
      const b = path[k] as number;
      if ((course.nodes[a] as Node).lane === lane.id) next[a] = b;
      if ((course.nodes[b] as Node).lane === lane.id) prev[b] = a;
    }
  }
  return { prev, next };
}

/**
 * How many steps the climb spends in each zone: enough to cross that zone's
 * slice of the mountain at that zone's own mean rise. The node budget is
 * therefore a consequence of the zone table — retune a rise and the spacing
 * follows instead of the climb quietly getting steeper.
 */
function zoneStepCounts(): number[] {
  const counts: number[] = [];
  for (let i = 0; i < ZONES.length; i++) {
    const lo = i === 0 ? FIRST_NODE_Y : (ZONE_BOUNDARY_YS[i - 1] as number);
    const hi = i === ZONES.length - 1 ? LAST_NODE_Y : (ZONE_BOUNDARY_YS[i] as number);
    counts.push(Math.max(1, Math.ceil((hi - lo) / (ZONES[i] as Zone).rise)));
  }
  return counts;
}

/**
 * Heights for every node, plus the zone that owns each *step* (and so its rise
 * and its chord).
 *
 * A step's rise comes from its zone and its zone comes from its height, so the
 * two are solved as a fixed point: guess the split from `zoneStepCounts`, lay
 * the heights out, re-read each step's zone off the height it now starts at,
 * repeat. It settles in two or three passes and lands the zone boundaries within
 * one step of the heights the table actually names.
 *
 * Every pass normalises the jittered rises so the last node sits exactly on
 * `LAST_NODE_Y`, which is what keeps the per-step rise comfortably inside the
 * budget `level.selftest.ts` checks. The jitters are drawn once, up front, so
 * the iteration cannot disturb the RNG stream.
 */
function layoutHeights(rng: Rng, n: number, counts: number[]): { ys: number[]; stepZone: number[] } {
  const jitter: number[] = [];
  for (let i = 0; i < n - 1; i++) jitter.push(randRange(rng, -RISE_JITTER, RISE_JITTER));

  let stepZone: number[] = [];
  for (let z = 0; z < counts.length; z++) {
    for (let k = 0; k < (counts[z] as number); k++) stepZone.push(z);
  }

  const heightsFor = (zones: number[]): number[] => {
    let sum = 0;
    const w: number[] = [];
    for (let i = 0; i < n - 1; i++) {
      const rise = (ZONES[zones[i] as number] as Zone).rise * (1 + (jitter[i] as number));
      w.push(rise);
      sum += rise;
    }
    const scale = sum > 0 ? (LAST_NODE_Y - FIRST_NODE_Y) / sum : 0;
    const ys = [FIRST_NODE_Y];
    for (let i = 0; i < n - 1; i++) ys.push((ys[i] as number) + (w[i] as number) * scale);
    return ys;
  };

  for (let pass = 0; pass < HEIGHT_PASSES; pass++) {
    const ys = heightsFor(stepZone);
    const next = ys.slice(0, n - 1).map((y) => zoneIndexAt(uAtY(y)));
    let settled = true;
    for (let i = 0; i < n - 1; i++) {
      if (next[i] !== stepZone[i]) {
        settled = false;
        break;
      }
    }
    if (settled) return { ys, stepZone };
    stepZone = next;
  }
  // Ran out of passes on an oscillating boundary: return heights that match the
  // assignment we finished on, so the two can never disagree downstream.
  return { ys: heightsFor(stepZone), stepZone };
}

/** What a segment of the walk does to the way round, and to the radius. */
type Pattern = 'arc' | 'switchback' | 'spur' | 'tighten';

/** Which zone owns trunk node `i`, given the per-*step* assignment. */
function nodeZone(stepZone: number[], i: number, n: number): number {
  return stepZone[Math.min(i, n - 2)] as number;
}

/**
 * The most a step across a `chord`-metre zone may change the radius by before
 * the hop it belongs to reaches `STEP_GAP_MAX`.
 *
 * Solved rather than tabulated: with the tangential part floored at
 * `MIN_TANGENTIAL · chord`, a step's gap is `√(dr² + (0.35·chord)²)`, so the
 * inverse is exact. Every candidate radius the walk considers — pattern drift,
 * jitter and de-stack nudge together — is held inside this against the node
 * below, which is what keeps the steering from buying a view with a jump nobody
 * can make.
 */
function radialStepMax(chord: number): number {
  return Math.sqrt(Math.max(0, STEP_GAP_MAX ** 2 - (chord * MIN_TANGENTIAL) ** 2));
}

/**
 * The radial band every trunk node is actually walked inside.
 *
 * {@link ZONES} names a band per zone, and a zone boundary is a single step —
 * taken literally, the one hop out of the Sunken Ruins into the Crystal Hollow
 * would be asked for five metres of radius, and the hop out of the Verdant
 * skirt for six. So the two bands are blended across `BAND_BLEND_NODES` either
 * side of the seam, and the mountain swells and narrows instead of stepping.
 *
 * Over the top of that goes the **waist**: at every checkpoint the band is
 * pulled toward `WAIST_LO … WAIST_HI` whatever the zones either side are doing.
 * A checkpoint carries a sealed lobby floor reaching from r = 6.6 out past its
 * own pad, and a pad on the Meadow rim would have meant a second plaza sixty-odd
 * metres across hanging off the mountain. Pinching there costs nothing and buys
 * a silhouette with articulation in it: foot, seam, skirt, seam, and so on to
 * the spire.
 *
 * Pure math over an assignment that already exists. It draws nothing, which is
 * exactly what lets it be computed before the walk and handed in — and the
 * checkpoint indices it needs come from {@link pickCheckpointIndices}, which
 * draws nothing either.
 */
function bandAlongTrunk(
  stepZone: number[],
  n: number,
  checkpointIdx: number[],
): { lo: number[]; hi: number[] } {
  const zoneAt = (i: number): Zone => ZONES[nodeZone(stepZone, i, n)] as Zone;
  /**
   * A zone's floor as the walk is allowed to see it. The one place the table's
   * `rLo` is read, and so the one place its promise has to be kept: a band may
   * narrow what a zone walks in but may never open it inside `R_MIN`, because
   * that number is about a pad's corners and the hollow shaft rather than about
   * any zone. Enforced here rather than trusted to the table, so a band someone
   * later edits to 8 is a tighter zone instead of a pad hanging over the drop —
   * and everything applied on top interpolates between values that already pass.
   */
  const floorOf = (z: Zone): number => Math.max(z.rLo, R_MIN);
  const lo: number[] = [];
  const hi: number[] = [];
  for (let i = 0; i < n; i++) {
    const z = zoneAt(i);
    lo.push(floorOf(z));
    hi.push(z.rHi);
  }
  // A seam sits *between* nodes b − 1 and b, so the blend is centred on b − ½.
  // Zones are a dozen nodes and more long and the blend is three, so two seams
  // can never reach each other and the order these are applied in cannot matter.
  for (let b = 1; b < n; b++) {
    const below = zoneAt(b - 1);
    const above = zoneAt(b);
    if (below === above) continue;
    for (let i = Math.max(0, b - BAND_BLEND_NODES); i < Math.min(n, b + BAND_BLEND_NODES); i++) {
      const t = clamp((i - (b - 0.5)) / (2 * BAND_BLEND_NODES) + 0.5, 0, 1);
      lo[i] = lerp(floorOf(below), floorOf(above), t);
      hi[i] = lerp(below.rHi, above.rHi, t);
    }
  }
  // The waist may only ever *pinch*. Clamping its two numbers into the band they
  // are being applied to before the blend is what says so, and it matters at
  // exactly one seam: the Obsidian Crown's whole band is 10–14, narrower than
  // the waist's own 13–16, so a waist taken literally would have *widened* the
  // spire at the one checkpoint where the mountain is supposed to be at its
  // tightest — and the first nodes of the crown were duly built out past their
  // own rim. A pinch that opens something is not a pinch.
  for (const c of checkpointIdx) {
    for (let i = Math.max(0, c - WAIST_BLEND_NODES); i <= Math.min(n - 1, c + WAIST_BLEND_NODES); i++) {
      const w = 1 - Math.abs(i - c) / (WAIST_BLEND_NODES + 1);
      const bLo = lo[i] as number;
      const bHi = hi[i] as number;
      lo[i] = lerp(bLo, clamp(WAIST_LO, bLo, bHi), w);
      hi[i] = lerp(bHi, clamp(WAIST_HI, bLo, bHi), w);
    }
  }
  return { lo, hi };
}

/**
 * The polar walk: where every trunk node goes.
 *
 * Radius and angle are solved together, in one forward pass, because they are
 * not independent. The angular step is whatever holds the horizontal gap at the
 * zone's chord given the radius change — for a step from r₀ to r₁ through dθ,
 *
 *     gap² = dr² + 4·r₀r₁·sin²(dθ/2),
 *
 * so the tangential budget left over after paying for `dr` fixes dθ exactly,
 * and difficulty stays in the zone table's hands rather than the radius's. That
 * solve is per-step local, so nothing is lost by doing it here — and everything
 * is gained, because a walk that knows where its earlier nodes ended up can
 * steer out from under them (see `STACK_CLEAR`). A radius decided in one pass
 * and turned into positions in another cannot.
 *
 * Segments of 6–12 nodes each roll a pattern from *their own zone's* table and
 * hold it, which is what makes the shape legible from below — a switchback you
 * can see coming is a landmark, one every other node is just noise. Per zone,
 * because a pattern mix is half of what a zone looks like: the same band full
 * of arcs is a circle and full of spurs is a set of arms.
 *
 * The pattern each node was walked under, and which segment it belongs to, come
 * back with the geometry: both are worked out here anyway, and the trunk's set
 * pieces are chosen off them (see {@link trunkSetPieces}).
 *
 * **RNG discipline.** Three draws happen at every segment boundary and two at
 * every node, always, in that fixed order, whatever the geometry turns out to
 * be. The band, the flare and the de-stack ladder are pure math on top of what
 * was drawn — no rung of the ladder rolls anything, and which one wins is
 * decided on distances alone.
 */
function walkTrunk(
  rng: Rng,
  ys: number[],
  stepZone: number[],
  checkpointIdx: number[],
): { radius: number[]; theta: number[]; pattern: Pattern[]; segment: number[] } {
  const n = ys.length;
  const band = bandAlongTrunk(stepZone, n, checkpointIdx);
  const flareFrom = Math.max(1, Math.floor(n * (1 - FLARE_FRACTION)));

  /** Node positions, kept as we go: the de-stack pass measures against them. */
  const radius: number[] = [];
  const theta: number[] = [];
  const xs: number[] = [];
  const zs: number[] = [];
  /** The unjittered angle chain the chord solve accumulates along. */
  const spine: number[] = [];
  const patterns: Pattern[] = [];
  const segments: number[] = [];

  let segId = -1;
  let r = R_START;
  let d = 1;
  let pattern: Pattern = 'arc';
  /**
   * What the segment asks of the radius per node, signed, as a fraction of the
   * band — turned into metres against *this* node's band, so a segment that runs
   * out of a waist into a zone interior lengthens its stride as the mountain
   * opens under it.
   */
  let drift = 0;
  /** The same for the segment's *first* step; `drift` handles the rest. */
  let kick = 0;
  let segLen = 0;
  let segPos = 0;
  let sinceFlip = 0;

  for (let i = 0; i < n; i++) {
    const lo = band.lo[i] as number;
    const hi = band.hi[i] as number;
    if (segPos >= segLen) {
      // All three draws happen on every segment boundary, in a fixed order, so
      // the stream never depends on which branch the segment ends up taking.
      segLen = randInt(rng, SEG_MIN, SEG_MAX);
      const rolled = pickWeighted(rng, (ZONES[nodeZone(stepZone, i, n)] as Zone).patterns);
      const sway = randRange(rng, -1, 1);
      // The opening and the summit approach are always plain arcs: no reversal
      // while the game is still teaching, and none on the last jumps either.
      // Everywhere else, a direction that has held too long has to break.
      pattern =
        i < OPENING_ARC_NODES || i >= flareFrom
          ? 'arc'
          : sinceFlip >= MAX_SAME_DIR_NODES
            ? 'switchback'
            : rolled;
      segPos = 0;
      segId++;
      switch (pattern) {
        case 'switchback': {
          d = -d;
          sinceFlip = 0;
          // Signed off the current radius rather than rolled — always toward the
          // roomier half of the band — so the leg coming back peels away from the
          // one it just left instead of climbing the same wall in reverse with
          // its pads hanging three metres over the ones below.
          const out = r < (lo + hi) * 0.5 ? 1 : -1;
          drift = out * SWITCHBACK_DRIFT;
          kick = out * SWITCHBACK_KICK;
          break;
        }
        case 'spur':
          drift = SPUR_DRIFT;
          kick = drift;
          break;
        case 'tighten':
          drift = -TIGHTEN_DRIFT;
          kick = drift;
          break;
        default:
          // An arc is the pattern with no opinion about the radius — it holds
          // the way round and lets the radius wander. Left symmetric, that is a
          // random walk, and a random walk started against a wall averages the
          // wall: the skirt's twenty-odd nodes open at `R_START`, three metres
          // off the floor of a fourteen-metre band, and a fair coin will not
          // walk them out to the rim in the nodes there are. So the
          // wander is given the one opinion it can have without becoming another
          // pattern — *toward the middle of the band it is in*, by however far
          // off-centre the segment starts, so an arc out on a rim leans back in
          // and an arc hugging the floor leans out. A spur or a tighten still
          // says whatever it wants; this is only about the default — and in
          // Meadow Foothills, which is nothing but arcs, it is the whole shape.
          drift =
            (sway + clamp(((lo + hi) * 0.5 - r) / Math.max(1e-6, (hi - lo) * 0.5), -1, 1)) *
            ARC_DRIFT;
          kick = drift;
          break;
      }
      // Never point a segment at a wall it is already against: a radius pinned
      // on its clamp for ten nodes is a plain circular arc, which is the one
      // shape this walk exists to avoid. Measured as a fraction of the band, so
      // the rule means the same thing in a 14 m skirt as in a 4 m spire.
      const at = (r - lo) / Math.max(1e-6, hi - lo);
      if ((at > 0.75 && drift > 0) || (at < 0.25 && drift < 0)) {
        drift = -drift;
        kick = -kick;
      }
    }
    // Two per node, always, whether or not they end up being applied: the ends
    // of the climb are solved against exactly the radius and angle the walk
    // named, because the first hop and the summit hop have no slack to spend.
    const dTheta = randRange(rng, -ANGLE_JITTER, ANGLE_JITTER);
    const dRadius = randRange(rng, -RADIAL_JITTER, RADIAL_JITTER);
    const ends = i === 0 || i === n - 1;

    // A spur pushes out for the first half of its segment and reels back in for
    // the second, so it returns to the wall it left from rather than stranding
    // the climb on the rim.
    if (i > 0) {
      const ask =
        segPos === 0 ? kick : pattern === 'spur' && segPos * 2 >= segLen ? -drift : drift;
      r = clamp(r + ask * (hi - lo), lo, hi);
    } else {
      r = clamp(R_START, lo, hi);
    }

    /**
     * The walk state turned into a node radius: jittered, then blended toward
     * `FLARE_RADIUS` over the summit approach so the last hops are an outward
     * step onto the rim rather than a surprise, and finally held inside one
     * step's worth of radius change from the node below. The jitter is folded in
     * *before* the chord solve, so the jittered radius is the one the gap is
     * held against; the angular jitter is all that is left to nudge a gap, and
     * 0.01 rad is under 0.3 m even out on the Verdant rim.
     */
    const flare = i >= flareFrom ? (i - flareFrom + 1) / (n - flareFrom) : 0;
    const rBelow = i > 0 ? (radius[i - 1] as number) : 0;
    const drMax =
      i > 0 ? radialStepMax((ZONES[stepZone[i - 1] as number] as Zone).chord) : Infinity;
    const shape = (walk: number): number => {
      const flared = lerp(clamp(ends ? walk : walk + dRadius, lo, hi), FLARE_RADIUS, flare);
      return i > 0 ? clamp(flared, rBelow - drMax, rBelow + drMax) : flared;
    };

    /** The angle that holds this step at its zone's chord, for a candidate radius. */
    const solve = (r1: number): number => {
      if (i === 0) return THETA0;
      const chord = (ZONES[stepZone[i - 1] as number] as Zone).chord;
      const dr = r1 - rBelow;
      const tangential = Math.sqrt(
        Math.max(chord * chord - dr * dr, (chord * MIN_TANGENTIAL) * (chord * MIN_TANGENTIAL)),
      );
      const half = Math.min(0.9, tangential / (2 * Math.sqrt(rBelow * r1)));
      return (spine[i - 1] as number) + d * 2 * Math.asin(half);
    };

    /**
     * How much room a candidate leaves under itself: the closest already-placed
     * node in the `STACK_DY_MIN … STACK_DY_MAX` window below, or `Infinity`
     * where the window is empty. Heights only increase along the trunk, so the
     * window is a contiguous run of predecessors and the scan can stop dead.
     */
    const roomUnder = (rc: number, ta: number): number => {
      const x = rc * Math.cos(ta);
      const z = rc * Math.sin(ta);
      let worst = Infinity;
      for (let j = i - 1; j >= 0; j--) {
        const dy = (ys[i] as number) - (ys[j] as number);
        if (dy <= STACK_DY_MIN) continue;
        if (dy >= STACK_DY_MAX) break;
        const flat = Math.hypot(x - (xs[j] as number), z - (zs[j] as number));
        if (flat < worst) worst = flat;
      }
      return worst;
    };

    // De-stacking: the walk's own radius first, then nudges either side of it,
    // nearest first, until one clears — or until the ladder runs out, in which
    // case the roomiest rung tried is what gets built. The ladder shortens to
    // nothing across the summit flare, where the whole point is that the last
    // hops are predictable.
    let walk = r;
    let rNode = shape(r);
    let spineAngle = solve(rNode);
    let room = roomUnder(rNode, spineAngle + (ends ? 0 : dTheta));
    const steer = STACK_STEER_MAX * (1 - flare);
    for (let k = 1; room < STACK_CLEAR && k * STACK_STEER_STEP <= steer + 1e-9; k++) {
      for (const sign of [1, -1]) {
        const cand = clamp(r + sign * k * STACK_STEER_STEP, lo, hi);
        const rc = shape(cand);
        const sa = solve(rc);
        const got = roomUnder(rc, sa + (ends ? 0 : dTheta));
        if (got > room) {
          room = got;
          walk = cand;
          rNode = rc;
          spineAngle = sa;
        }
        if (room >= STACK_CLEAR) break;
      }
    }
    // The correction persists: the next node drifts from where this one ended
    // up, not from the line it was steered off. A nudge that reset every node
    // would be a zig-zag, and a zig-zag is not a course correction.
    r = walk;

    const angle = spineAngle + (ends ? 0 : dTheta);
    radius.push(rNode);
    spine.push(spineAngle);
    theta.push(angle);
    xs.push(rNode * Math.cos(angle));
    zs.push(rNode * Math.sin(angle));
    patterns.push(pattern);
    segments.push(segId);
    segPos++;
    sinceFlip++;
  }

  return { radius, theta, pattern: patterns, segment: segments };
}

/**
 * Lay out the whole course: heights, then the polar walk, then a type for each
 * node — and the lanes those nodes are threaded onto.
 *
 * The walk produces one line — the trunk — and {@link planForks} then hangs
 * branches off it. Every branch node is *appended*, never spliced, so trunk
 * order and flat order still agree for the trunk itself (checkpoint emission
 * order depends on that); everything downstream reads its neighbours through
 * {@link lanePath} rather than off the flat array, because past the first fork
 * the array is emission order and nothing more.
 */
function layoutNodes(rng: Rng): Course {
  const counts = zoneStepCounts();
  let n = 1;
  for (const c of counts) n += c;

  const { ys, stepZone } = layoutHeights(rng, n, counts);
  // Where the pads will land, decided off bare heights so the walk and the fork
  // planner can both ask without moving the RNG stream out from under
  // `assignKinds`. The walk needs it too now: the band pinches to a waist at
  // every checkpoint, and that is a question about which node carries a pad.
  // Trunk indices and flat indices still agree here — every branch node is
  // appended after this.
  const checkpointIdx = pickCheckpointIndices(ys);
  const { radius, theta, pattern, segment } = walkTrunk(rng, ys, stepZone, checkpointIdx);

  const nodes: Node[] = [];
  for (let i = 0; i < n; i++) {
    const t = theta[i] as number;
    const r = radius[i] as number;
    nodes.push({
      x: r * Math.cos(t),
      y: ys[i] as number,
      z: r * Math.sin(t),
      theta: t,
      radius: r,
      // Filled in below: yaw needs the neighbours' positions, not just an angle.
      yaw: 0,
      zone: nodeZone(stepZone, i, n),
      kind: 'platform',
      lane: 0,
    });
  }

  const body: number[] = [];
  for (let i = 1; i < n - 1; i++) body.push(i);
  const course: Course = {
    nodes,
    lanes: [{ id: 0, role: 'trunk', motif: 'traverse', entry: 0, exit: n - 1, body }],
    pattern,
    segment,
    islands: new Set<number>(),
  };

  // Settle the trunk before any branch measures itself against it — the rim
  // detour moves nodes several metres outward, and a clearance promised against
  // the pre-seal position is a promise about somewhere the course no longer is.
  sealApproaches(
    nodes,
    checkpointIdx.map((i) => nodes[i] as Node),
    nodes.map((_, i) => i - 1),
  );

  for (const plan of planForks(rng, nodes, checkpointIdx)) walkLane(rng, plan, course);

  assignKinds(rng, course);
  sealLobbyApproaches(course);
  // Yaws last: `sealLobbyApproaches` moves nodes, and a bearing computed before
  // the move is a bearing to somewhere the course no longer goes.
  faceAlongCourse(course);
  // And the columns after that, for the same reason twice over: how deep one may
  // run is a question about where the nodes around it ended up *and* about which
  // way their pads are turned.
  trimPillars(course);
  return course;
}

/**
 * Face each node the way the course leaves it. On a helix that was the ring
 * tangent; with reversals and spurs it has to be the real bearing to the next
 * node, or half the platforms end up broadside to the jump. The last node of a
 * lane inherits the bearing it arrived on.
 *
 * Bearings are taken *along a lane*, not along the flat array: a fork's first
 * pad has to face where that fork goes, not at whatever node happens to be
 * stored beside it. Each node is turned by the lane that owns it, so a shared
 * entry keeps facing down the trunk rather than being spun by every branch that
 * leaves it.
 */
function faceAlongCourse(course: Course): void {
  const nodes = course.nodes;
  for (const lane of course.lanes) {
    const path = lanePath(course, lane);
    if (path.length < 2) continue;
    for (let k = 0; k < path.length; k++) {
      const at = path[k] as number;
      if ((nodes[at] as Node).lane !== lane.id) continue;
      const ahead = k < path.length - 1;
      const from = nodes[path[ahead ? k : k - 1] as number] as Node;
      const to = nodes[path[ahead ? k + 1 : k] as number] as Node;
      (nodes[at] as Node).yaw = yawAlong(to.x - from.x, to.z - from.z);
    }
  }
}

/**
 * Push every approach node a lobby floor would roof out past the floor's rim.
 *
 * The floors are sealed rings (see {@link emitLobbyFloor}), so the only way to
 * get from under one to on top of it is around the outside. Any node close
 * enough under a pad that the floor would become its ceiling is therefore thrown
 * radially outward past the rim — same angle, same height, only further out.
 * What used to be a climb up through a stairwell becomes an exposed traverse
 * along the rim, finishing with a hop up over the lip onto the floor itself.
 *
 * Which nodes a floor would roof is a question about *height*, not about
 * position in any array: the test is over every node on the course, keeping the
 * ones that sit below the pad with less than `LOBBY_CLEARANCE` of air under the
 * slab. On a single climbing line that is the same set as walking backwards
 * from the pad until the headroom is comfortable — heights only ever increase,
 * so the walk could never have reached past it — but a fork's nodes are not
 * stored behind the pad they pass under, and a predecessor walk would sail
 * straight over them.
 *
 * Nothing here is rolled: the reroute reads only geometry that already exists,
 * so it never disturbs the RNG stream. `level.selftest.ts` holds both halves of
 * the bargain to numbers — the hop budget in §5, and the promise that no node
 * ends up roofed in §8.
 */
function sealLobbyApproaches(course: Course): void {
  sealApproaches(
    course.nodes,
    course.nodes.filter((node) => node.kind === 'checkpoint'),
    laneNeighbours(course).prev,
  );
}

/**
 * The geometry half of {@link sealLobbyApproaches}, taking the pads directly and
 * a `prev` map giving each node's predecessor along the lane that owns it.
 *
 * Split out because the fork planner needs the trunk in its *final* position
 * before it can promise a branch any clearance, and that is several steps before
 * a node knows it is a checkpoint. It is idempotent — a node already outside the
 * rim is left alone, and the smoothing below only ever acts on hops the throw
 * itself stretched — so running it early and again at the end costs nothing and
 * changes nothing.
 */
function sealApproaches(nodes: Node[], pads: Node[], prev: number[]): void {
  for (const pad of pads) {
    const underY = pad.y - CHECKPOINT_THICK - 2 * LOBBY_HALF_THICK;
    const rTarget = pad.radius + LOBBY_OUTER_M + LOBBY_RIM_CLEAR;
    const moved: number[] = [];
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i] as Node;
      if (node === pad) continue;
      // Level with the floor or above it: standing on the ring, not under it.
      if (node.y >= pad.y) continue;
      if (underY - node.y >= LOBBY_CLEARANCE) continue;
      if (node.radius >= rTarget) continue;
      node.radius = rTarget;
      node.x = rTarget * Math.cos(node.theta);
      node.z = rTarget * Math.sin(node.theta);
      moved.push(i);
    }
    // The throw pays its own bill. A node flung from the inner wall to the rim
    // leaves the hop that reaches it stretched — sometimes far past what a
    // bounce can cross — so walk back down the lane from every moved node,
    // pulling each predecessor outward until its hop is back under
    // `LOBBY_APPROACH_GAP`. Pulling *outward* genuinely shortens the hop: with
    // the far end fixed at radius r₁ and the spokes dθ apart, the gap
    // r₀² + r₁² − 2·r₀r₁·cos dθ bottoms out at r₀ = r₁·cos dθ, and every moved
    // node starts inside that. Each pull is the smallest that suffices, so the
    // detour still hugs the line the walk chose as closely as the budget allows.
    for (const m of moved) {
      let cur = m;
      for (let guard = 0; guard < 8; guard++) {
        const p = prev[cur] ?? -1;
        if (p < 0) break;
        const a = nodes[p] as Node;
        const b = nodes[cur] as Node;
        if (Math.hypot(b.x - a.x, b.z - a.z) <= LOBBY_APPROACH_GAP) break;
        const dTheta = b.theta - a.theta;
        const c = Math.cos(dTheta);
        const disc = LOBBY_APPROACH_GAP ** 2 - b.radius ** 2 * (1 - c * c);
        // A radial pull first: it keeps the walk's angles intact. The smaller
        // root is the first radius at which the hop fits; past the minimum the
        // gap would grow again, so the pull only helps a predecessor still
        // inside it.
        const want = disc >= 0 ? b.radius * c - Math.sqrt(disc) : Infinity;
        if (disc >= 0 && want > a.radius) {
          a.radius = want;
          a.x = want * Math.cos(a.theta);
          a.z = want * Math.sin(a.theta);
        } else {
          // No outward pull can save this hop — the spokes are too far apart,
          // or the predecessor's radius is pinned past the sweet spot (two
          // rim-thrown nodes side by side). So close the angle instead: swing
          // the predecessor's spoke toward the moved node until the hop, at
          // the radius it already has, is exactly the budget. The rotation
          // stretches the hop underneath by what this one gave up, which the
          // next lap of this walk absorbs the same way; every hop further down
          // has slack, so the debt shrinks as it sinks.
          const fit =
            (a.radius ** 2 + b.radius ** 2 - LOBBY_APPROACH_GAP ** 2) /
            (2 * a.radius * b.radius);
          a.theta = b.theta - Math.sign(dTheta) * Math.acos(clamp(fit, -1, 1));
          a.x = a.radius * Math.cos(a.theta);
          a.z = a.radius * Math.sin(a.theta);
        }
        cur = p;
      }
    }
  }
}

/**
 * Which nodes carry a checkpoint pad: the node nearest each zone boundary.
 * Matched on *height*, which is the dimension the boundaries are specified in —
 * matching on `u` instead drifts several metres, because the per-zone rises make
 * height only approximately linear in progress.
 *
 * Takes bare heights, and draws nothing. Both matter, and to two callers now.
 * The fork planner has to know where the pads will land *before* the kinds are
 * assigned, because a fork may not open across a boundary; the walk itself has
 * to know before it takes a single step, because the band pinches to a waist at
 * every checkpoint (see {@link bandAlongTrunk}). Neither could ask if the
 * asking moved the RNG stream out from under `assignKinds`.
 */
function pickCheckpointIndices(ys: number[]): number[] {
  const picked: number[] = [];
  for (const targetY of ZONE_BOUNDARY_YS) {
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < ys.length; i++) {
      const d = Math.abs((ys[i] as number) - targetY);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best >= 0) picked.push(best);
  }
  return picked;
}

// ---------------------------------------------------------------------------
// Forks
// ---------------------------------------------------------------------------

/**
 * The band a fork window works in: the widest of the two anchors' zones.
 *
 * Radii on a branch used to be absolute — a ledge wall was always at r = 17, an
 * island spur always at 18 — which was defensible while the whole mountain
 * shared one ten-metre band and is nonsense now. "Out on the rim" means r = 28
 * on the Frozen cornice and r = 14 in the Obsidian spire, and a motif whose
 * whole idea is *reaching* has to be told which. A window that straddles a seam
 * gets the roomier of the two bands, because that is the one its geometry has to
 * fit inside anyway.
 */
function windowBand(entry: Node, exit: Node): { lo: number; hi: number } {
  const a = ZONES[entry.zone] as Zone;
  const b = ZONES[exit.zone] as Zone;
  return { lo: Math.min(a.rLo, b.rLo), hi: Math.max(a.rHi, b.rHi) };
}

/**
 * How far out a branch lane may hold, given its window's band.
 *
 * A fork should be a place the mountain is wider than the trunk made it, so a
 * lane gets its band plus `LANE_BAND_SLACK` — but never so far out that an
 * `islandSpur` parked on the limit would hang its slab's corners off the
 * mountain, which is what `LANE_RIM_KEEP` is holding back for.
 */
function laneRadiusMax(bandHi: number): number {
  return Math.min(bandHi + LANE_BAND_SLACK, R_HARD_MAX - LANE_RIM_KEEP);
}

/**
 * Which way a branch gets from its entry to its exit.
 *
 * `opposite` takes the long way round the shaft — the trunk swept most of a
 * circle in this window, so the branch sweeps the rest of it, the other way.
 * Because the two sweeps together are exactly one turn, the routes occupy
 * disjoint arcs and can only meet where they are meant to: at the junctions.
 *
 * `parallel` runs the same way round but holds `LANE_RADIAL_SEP` clear of
 * anything the trunk does in the window — outside it where there is room out
 * there, and hard against the shaft where there is not — and cuts the corner the
 * trunk wandered round.
 */
type ForkShape = 'opposite' | 'parallel';

/** A decided fork, ready for {@link walkLane} to lay nodes along. */
interface ForkPlan {
  /** Trunk node the branch leaves from, and the one it rejoins at. */
  entry: number;
  exit: number;
  shape: ForkShape;
  role: LaneRole;
  motif: MotifId;
  /** Signed, unwrapped angular sweep from the entry node to the exit node. */
  sweep: number;
  /** Radius the lane holds through its middle. */
  rHold: number;
  /** How far out this window lets a lane reach — see {@link laneRadiusMax}. */
  rMax: number;
  /** Hops the lane spends easing out to `rHold`, and easing back — see {@link laneShape}. */
  ease: number;
  /** Hops from entry to exit — one more than the lane's own node count. */
  hops: number;
}

/** A lane's nodes before jitter: one entry per *body* node, in climb order. */
interface LaneShape {
  theta: number[];
  radius: number[];
  y: number[];
}

/**
 * What one motif *is*.
 *
 * Every lane is the same construction — a line from the entry anchor to the exit
 * anchor, eased out to a radius it holds through the middle — and a motif is the
 * handful of numbers and three small functions that bend that construction into
 * a shape with an idea in it. Nothing here knows about primitives: a motif
 * decides where the nodes go and what kind sits on each, and the emitters and
 * {@link decorateLane} read the same table back when it is time to build.
 */
interface MotifSpec {
  /** What taking this fork promises. Junction arrows are coloured off it. */
  role: LaneRole;
  /**
   * `fast` lanes must beat the trunk stretch they replace by `FORK_RISE_GAIN`
   * metres a hop; `gentle` ones must be slower than it by `LANE_RISE_LOSS` —
   * more landings for the same height, every one of them a shorter step. Both
   * are answers to "why would I go that way"; only the first is a shortcut.
   */
  pace: 'fast' | 'gentle';
  /**
   * Zones this motif may open in. Both anchors have to be inside the range.
   * Nothing starts at zone 0: Meadow Foothills has a fork quota of zero, so the
   * tutorial is one path with one idea in it and no motif ever lands there.
   */
  minZone: number;
  maxZone: number;
  /** Body nodes this shape is legible with, at fewest and at most. */
  minBody: number;
  maxBody: number;
  /** Hops spent easing out to the held radius, or `null` to take the line's own. */
  ease: number | null;
  /**
   * The radius the middle of the lane holds, given the candidate line's — and
   * given the window's band, for the two motifs whose whole idea is a *place*
   * rather than a deformation. "Out on the rim" is a different number in every
   * zone now, so a rim wall and an island spur are told which one theirs is
   * instead of carrying a constant that used to be true everywhere.
   */
  rHold: (base: number, band: { lo: number; hi: number }) => number;
  /**
   * How body node `b` of `m` leaves the base line: `[radial, tangential,
   * vertical]` metres. The tangential term is metres along the ring, converted
   * to an angle against that node's own radius.
   */
  warp: (b: number, m: number) => V3;
  /** The kind this shape insists on at body node `b` of `m`; `null` to roll it. */
  kindAt: (b: number, m: number) => NodeKind | null;
  /** Body node that stands on a wide island, or −1 for none. */
  islandAt: (m: number) => number;
  /** May this lane give height back on the way out? */
  descends: boolean;
}

const NO_WARP = (): V3 => [0, 0, 0];
const NO_KIND = (): NodeKind | null => null;
const NO_ISLAND = (): number => -1;

/** Which body node of an `islandSpur` is the island; the next one is the launch. */
function islandIndex(m: number): number {
  return Math.floor((m - 1) / 2);
}

/**
 * How much of its sag an `islandSpur` is carrying at body node `b`: eased in on
 * the way out, held across the island and the mushroom beyond it, gone after.
 * The recovery therefore all happens on the hop that *leaves* the mushroom,
 * which is the one hop with the budget to pay for it.
 */
function islandSag(b: number, m: number): number {
  const at = islandIndex(m);
  if (b < at) return (b + 1) / (at + 1);
  return b <= at + 1 ? 1 : 0;
}

/**
 * The motif library.
 *
 * Read in table order everywhere it matters — the weighted roll, and the
 * fallback scan when the rolled motif does not fit the window it landed on — so
 * that order is part of the contract, not an accident of how the object literal
 * was typed.
 */
const MOTIFS: Record<MotifId, MotifSpec> = {
  // The plain fast line: whatever the window's own geometry suggested, climbed
  // in the fewest hops that fit. Kinds are left to the zone table, which is the
  // only motif that does — a traverse is a shortcut, not a statement.
  traverse: {
    role: 'bold',
    pace: 'fast',
    minZone: 1,
    maxZone: 5,
    minBody: 1,
    maxBody: LANE_HOPS_MAX - 1,
    ease: null,
    rHold: (base) => base,
    warp: NO_WARP,
    kindAt: NO_KIND,
    islandAt: NO_ISLAND,
    descends: false,
  },
  // Alternating mushroom and platform, starting at `body[0]`: the hop *onto* the
  // lane stays an ordinary step and the first launch happens from the branch. A
  // hop leaving a mushroom is allowed most of twice the rise, so the height is
  // dealt out in proportion to what each hop may spend — which is what makes the
  // flight a series of launches rather than a staircase that happens to be red.
  // Obsidian gives nothing back, so the top zone never grows one.
  mushroomFlight: {
    role: 'bold',
    pace: 'fast',
    minZone: 1,
    maxZone: 4,
    minBody: 2,
    maxBody: LANE_HOPS_MAX - 1,
    ease: null,
    rHold: (base) => base,
    warp: NO_WARP,
    kindAt: (b) => (b % 2 === 0 ? 'mushroom' : 'platform'),
    islandAt: NO_ISLAND,
    descends: false,
  },
  // A rim wall: held out on its zone's own rim with nothing behind it but sky,
  // every other landing a narrow ledge, and the ledges stepped a little either
  // side of the line so the wall zig-zags instead of reading as a rail. Ledges
  // land on even body indices — odd path indices — so two can never end up
  // adjacent, and the junction anchors at both ends stay the plain platforms
  // they have to be.
  ledgeWall: {
    role: 'safe',
    pace: 'gentle',
    minZone: 2,
    maxZone: 5,
    minBody: 3,
    maxBody: LANE_HOPS_MAX - 1,
    ease: null,
    rHold: (_base, band) => band.hi,
    warp: (b): V3 => [0, b % 2 === 0 ? LEDGE_WALL_STAGGER : -LEDGE_WALL_STAGGER, 0],
    kindAt: (b) => (b % 2 === 0 ? 'ledge' : 'platform'),
    islandAt: NO_ISLAND,
    descends: false,
  },
  // A colonnade, and since the `pillar` kind became a column it is a literal
  // one: the radius weaves a full period either side of the line and every
  // landing past the first *is* a column cap, with free-standing ones between
  // them (see {@link decorateLane}). `body[0]` stays a plain wide pad so the
  // first hop off the junction is not also the first hop onto a narrow round top.
  pillarForest: {
    role: 'scenic',
    pace: 'gentle',
    minZone: 2,
    maxZone: 5,
    minBody: 3,
    maxBody: LANE_HOPS_MAX - 1,
    ease: null,
    rHold: (base) => base,
    warp: (b, m): V3 => [
      PILLAR_FOREST_WEAVE * Math.sin((Math.PI * 2 * (b + 1)) / (m + 1)),
      0,
      0,
    ],
    kindAt: (b) => (b === 0 ? 'platform' : 'pillar'),
    islandAt: NO_ISLAND,
    descends: false,
  },
  // The one shape that goes *down* before it goes up: three to five nodes out to
  // the rim, sagging below their own spine, an island at the apex to stand on
  // and look from, and a mushroom immediately past it so the way back is a
  // single launch that pays the sag off in one go.
  islandSpur: {
    role: 'scenic',
    pace: 'gentle',
    minZone: 1,
    maxZone: 5,
    minBody: 3,
    maxBody: 5,
    // Fixed at two: with one, the ease blend saturates on the first hop and all
    // three body nodes sit on the rim together — a plateau, not a spur.
    ease: 2,
    rHold: (_base, band) => band.hi + ISLAND_SPUR_OUT,
    warp: (b, m): V3 => [0, 0, -ISLAND_SPUR_SAG * islandSag(b, m)],
    kindAt: (b, m) => (b === islandIndex(m) + 1 ? 'mushroom' : 'platform'),
    islandAt: islandIndex,
    descends: true,
  },
  // Built structure: the line bulges five metres out and back, and the spans
  // between its landings are decked with planks (see {@link decorateLane}). Every
  // node is a plain platform — a deck has to start and end somewhere flat.
  bridgeRun: {
    role: 'safe',
    pace: 'gentle',
    minZone: 1,
    maxZone: 5,
    minBody: 3,
    maxBody: LANE_HOPS_MAX - 1,
    ease: null,
    rHold: (base) => base + BRIDGE_BULGE,
    warp: NO_WARP,
    kindAt: () => 'platform',
    islandAt: NO_ISLAND,
    descends: false,
  },
};

/**
 * What a fork's branch is built out of. Rolled once per accepted window and then
 * clamped, never re-rolled — see {@link planForks}.
 *
 * Weighted toward the two shapes that carry the climb rather than comment on it:
 * a traverse and a flight are the fast half of the mountain, and the four slower
 * shapes share the rest between them. `islandSpur` is the lightest because it is
 * the only one that goes *down* — a fine thing to meet twice on a climb and a
 * tiring one to meet six times.
 */
const FORK_MOTIF_WEIGHTS: Array<[MotifId, number]> = [
  ['traverse', 22],
  ['mushroomFlight', 22],
  ['ledgeWall', 18],
  ['pillarForest', 16],
  ['islandSpur', 8],
  ['bridgeRun', 14],
];

/**
 * Every motif, in the order the eligibility scan and the fallback walk read
 * them. Taken off the weight table rather than written out again, so the two can
 * never drift apart.
 */
const MOTIF_ORDER: readonly MotifId[] = FORK_MOTIF_WEIGHTS.map(([id]) => id);

/**
 * Where a lane's height goes, hop by hop.
 *
 * Hop `k` runs from path index `k − 1` to `k`, where path index 0 is the entry
 * anchor, `1 … m` the lane's own nodes and `m + 1` the exit anchor. A hop that
 * leaves a mushroom is allowed most of twice the rise, so it is weighted for
 * twice as much of the climb — and of the sweep, because one parameter drives
 * all three coordinates. Everything else spreads evenly.
 */
function laneHopWeights(hops: number, motif: MotifId): number[] {
  const spec = MOTIFS[motif];
  const m = hops - 1;
  const w: number[] = [];
  for (let k = 1; k <= hops; k++) {
    w.push(k >= 2 && spec.kindAt(k - 2, m) === 'mushroom' ? MUSHROOM_RISE : BOLD_MAX_RISE);
  }
  return w;
}

/**
 * The nominal (unjittered) polar coordinates of a lane's body nodes.
 *
 * One parameter `t` drives all three coordinates, so a hop that is allowed more
 * rise takes proportionally more of the sweep too — a mushroom launch goes
 * further as well as higher, which is what it looks like when you take one.
 *
 * The radius eases out to `rHold` over the lane's first `ease` hops and back
 * over its last. How many is a real decision, not a taste: a `parallel` fork
 * owes five metres of sidestep, and a lane that pays it all on the first hop has
 * nothing left in that hop's gap budget to travel with — while a lane that
 * spreads it over two spends its first third drifting toward the line it meant
 * to take, and arrives no shorter than the trunk it was supposed to beat. Which
 * costs less depends on how long the lane is, so {@link fitWindow} tries both —
 * except where a motif names its own, as `islandSpur` does.
 *
 * On top of that base line sits the motif's own `warp`: a radial push, a
 * sideways step and a sag, in metres, per body node. The warp is applied after
 * the ease and before the clamp, so a motif can lean on the line it was given
 * without ever leaning off the mountain.
 */
function laneShape(
  entry: Node,
  exit: Node,
  sweep: number,
  rHold: number,
  rMax: number,
  ease: number,
  hops: number,
  motif: MotifId,
): LaneShape {
  const spec = MOTIFS[motif];
  const m = hops - 1;
  const w = laneHopWeights(hops, motif);
  let total = 0;
  for (const v of w) total += v;
  const shape: LaneShape = { theta: [], radius: [], y: [] };
  let acc = 0;
  for (let j = 1; j < hops; j++) {
    acc += w[j - 1] as number;
    const t = acc / total;
    const base = lerp(entry.radius, exit.radius, t);
    const blend = Math.min(1, j / ease, (hops - j) / ease);
    const [dr, dt, dy] = spec.warp(j - 1, m);
    const r = clamp(lerp(base, rHold, blend) + dr, R_MIN, rMax);
    shape.theta.push(entry.theta + sweep * t + dt / r);
    shape.radius.push(r);
    shape.y.push(lerp(entry.y, exit.y, t) + dy);
  }
  return shape;
}

/** World-space positions of `[entry, ...body, exit]` for a nominal lane shape. */
function lanePoints(entry: Node, exit: Node, shape: LaneShape): V3[] {
  const pts: V3[] = [[entry.x, entry.y, entry.z]];
  for (let j = 0; j < shape.theta.length; j++) {
    const r = shape.radius[j] as number;
    const a = shape.theta[j] as number;
    pts.push([r * Math.cos(a), shape.y[j] as number, r * Math.sin(a)]);
  }
  pts.push([exit.x, exit.y, exit.z]);
  return pts;
}

/**
 * The fewest hops in `[lo, hi]` this motif can be climbed across in, or `null`
 * if no number of hops in that range makes every step jumpable.
 *
 * Searched upward rather than solved, because the budget a hop answers to
 * depends on what it leaves *from*: a mushroom buys nearly twice the rise and
 * twice the reach, so the closed form would be a different one per motif. The
 * search is over at most `LANE_HOPS_MAX` cheap trials.
 *
 * The range is where the motif's *pace* lands — a fast lane is capped at the
 * hops it can spend and still beat the trunk, a gentle one floored at the hops
 * it must spend to be genuinely gentler (see {@link hopBounds}) — so the first
 * fit found is always the cheapest lane that keeps the promise.
 */
function fitLane(
  entry: Node,
  exit: Node,
  sweep: number,
  rHold: number,
  rMax: number,
  ease: number,
  motif: MotifId,
  lo: number,
  hi: number,
): { hops: number; shape: LaneShape } | null {
  const spec = MOTIFS[motif];
  for (let hops = Math.max(2, lo); hops <= hi; hops++) {
    const m = hops - 1;
    const shape = laneShape(entry, exit, sweep, rHold, rMax, ease, hops, motif);
    const pts = lanePoints(entry, exit, shape);
    let ok = true;
    for (let k = 1; k <= hops && ok; k++) {
      // Hop `k` leaves path index `k − 1`, which is body node `k − 2`; only a
      // body node can be a mushroom, and the entry anchor never is.
      const from = k >= 2 && spec.kindAt(k - 2, m) === 'mushroom';
      const a = pts[k - 1] as V3;
      const b = pts[k] as V3;
      const rise = b[1] - a[1];
      const gap = Math.hypot(b[0] - a[0], b[2] - a[2]);
      const riseCap = from ? MUSHROOM_RISE : BOLD_MAX_RISE;
      const gapCap = from ? LANE_GAP_MAX_MUSHROOM : LANE_GAP_MAX;
      const riseFloor = spec.descends ? -LANE_DROP_MAX : 0;
      ok =
        rise > riseFloor &&
        rise <= riseCap &&
        gap <= gapCap &&
        gap >= LANE_MIN_GAP &&
        b[1] >= entry.y - LANE_MAX_DIP;
    }
    if (ok) return { hops, shape };
  }
  return null;
}

/**
 * The hop counts a motif of this pace may be built in across a window that
 * climbs `dy` metres in `span` trunk nodes, or `null` if the pace is impossible
 * here at all.
 *
 * A branch and the trunk stretch it replaces cover exactly the same height, so
 * the only thing that can differ between them is how many hops they spend on it,
 * and both promises are therefore statements about a hop count:
 *
 * - `fast` — `dy / hops ≥ dy / span + FORK_RISE_GAIN`, so at most that many hops;
 * - `gentle` — `dy / hops ≤ dy / span − LANE_RISE_LOSS`, so at least that many.
 *
 * A window whose trunk already climbs slower than `LANE_RISE_LOSS` a hop has no
 * room under it for a gentle lane, which is what the `null` is.
 *
 * Gentle lanes are capped at `LANE_GENTLE_EXTRA` hops past the span as well.
 * Not for the promise's sake — a slower lane is only more so — but for the
 * mountain's: a branch that spends twenty landings crossing a window it could
 * have crossed in nine is most of a zone's node budget spent on one fork, and
 * the fork after it then has nowhere left to open.
 */
function hopBounds(pace: 'fast' | 'gentle', dy: number, span: number): { lo: number; hi: number } | null {
  const trunkRise = dy / span;
  if (pace === 'fast') {
    return { lo: 2, hi: Math.floor(dy / (trunkRise + FORK_RISE_GAIN) + 1e-9) };
  }
  const target = trunkRise - LANE_RISE_LOSS;
  if (target <= 0) return null;
  return {
    lo: Math.ceil(dy / target - 1e-9),
    hi: Math.min(LANE_HOPS_MAX, span + LANE_GENTLE_EXTRA),
  };
}

/**
 * Does this lane keep its distance from the trunk, and from the branches already
 * planned?
 *
 * Checked at the same nominal geometry `level.selftest.ts` will measure the real
 * thing at, with the same exemption: a lane's first and last body node are
 * *supposed* to crowd the junction they hang off, so only the middle of a lane
 * owes anyone room.
 *
 * Which leaves those two nodes able to crowd more than their own junction: they
 * are also a hop from the trunk node *past* it, and a hop is less than a pad is
 * wide. Insisting on the full clearance there would refuse the fork rather than
 * move it, so they owe a smaller one — `LANE_END_CLEARANCE`, which is only ever
 * about not landing in the same place twice. Their pads sort the rest out at
 * emission time by giving ground (see {@link padScales}).
 *
 * Every node of a lane owes the trunk one thing more: it may not be *stacked*
 * on it. Level-with clearance is about two routes you cannot tell apart;
 * `LANE_STACK_CLEAR` over `LANE_STACK_DY` metres is about a branch that runs
 * directly over the trunk it left, which is the ceiling this whole geometry is
 * trying to get rid of, sold back as a choice. The trunk can steer out from
 * under itself node by node; a lane is solved end to end, so the only answer
 * here is to refuse the line and let {@link fitWindow} try another radius,
 * another motif or another span.
 *
 * The end exemption deliberately does *not* extend to it — measured, not
 * assumed: exempting the junctions' trunk neighbourhoods as well buys back a
 * third of the forks the rule costs and loses more seeds to stacking than it
 * saves. A blanket pass is worse still, because a short window's lane is two
 * body nodes and both of them are ends, so the whole branch would owe nothing;
 * that is how a fork ends up hanging its landings seven metres under the trunk
 * it just left. Crowding the pad you came from is one thing. Being a ceiling is
 * another, and the exemption was only ever for the first.
 *
 * Which is why the two questions are asked separately rather than as one
 * if/else on height. They overlap: `LANE_STACK_DY_MIN … LANE_CLEARANCE_Y` is
 * half a metre of height that is *both* level-with and stacked, and an end node
 * in it owes the weaker promise on the first count and the full 4.5 m on the
 * second. Written as a chain of alternatives the first answer won and the half
 * metre was a hole — a hole the self-test could see, since §5h opens its window
 * at three metres and this one used to open at three and a half.
 */
function laneIsClear(
  shape: LaneShape,
  entry: Node,
  exit: Node,
  trunk: Node[],
  taken: LaneShape[],
): boolean {
  const m = shape.theta.length;
  for (let j = 0; j < m; j++) {
    const near = j === 0 || j === m - 1 ? LANE_END_CLEARANCE : LANE_CLEARANCE;
    const r = shape.radius[j] as number;
    const a = shape.theta[j] as number;
    const x = r * Math.cos(a);
    const z = r * Math.sin(a);
    const y = shape.y[j] as number;
    for (const node of trunk) {
      // The junctions the lane hangs off are the one thing it is allowed to
      // crowd; they are also the only trunk nodes it is *supposed* to touch.
      if (node === entry || node === exit) continue;
      const dy = Math.abs(node.y - y);
      const flat = Math.hypot(node.x - x, node.z - z);
      if (dy < LANE_CLEARANCE_Y && flat < near) return false;
      if (dy > LANE_STACK_DY_MIN && dy < LANE_STACK_DY && flat < LANE_STACK_CLEAR) return false;
    }
    for (const other of taken) {
      for (let k = 0; k < other.theta.length; k++) {
        if (Math.abs((other.y[k] as number) - y) >= LANE_CLEARANCE_Y) continue;
        const or = other.radius[k] as number;
        const oa = other.theta[k] as number;
        if (Math.hypot(or * Math.cos(oa) - x, or * Math.sin(oa) - z) < near) return false;
      }
    }
  }
  return true;
}

/** One way a window could be built: a motif, and the line it fits this window on. */
interface MotifOption {
  shape: ForkShape;
  sweep: number;
  rHold: number;
  /** The window's outer limit, carried so the build clamps exactly as the fit did. */
  rMax: number;
  ease: number;
  hops: number;
  /** Nominal geometry, for the *next* window to keep clear of. */
  lane: LaneShape;
}

/** Everything {@link fitWindow} worked out about a window it is willing to fork. */
interface WindowFit {
  entry: number;
  exit: number;
  /**
   * The motifs this window can be built from, and how — keyed by motif, so the
   * roll afterwards can only ever land on geometry that was checked. Never
   * empty: a window with no options is not a window.
   */
  options: Map<MotifId, MotifOption>;
}

/**
 * Can a fork open across trunk nodes `i0 … i1`, and if so, along what line?
 *
 * Pure geometry — deliberately. Acceptance draws nothing, so the RNG stream is
 * decided by where the trunk went rather than by how many windows were tried and
 * thrown away, and the per-fork rolls can happen afterwards in acceptance order.
 *
 * A window has to survive, in order: the pads (no fork lands on one, opens under
 * one, or is roofed by one); a shape that gets from one end to the other inside
 * the hop budget; the *promise* — a branch that climbs at the trunk's own rate
 * is not an alternative, it is a detour, so it has to be faster or genuinely
 * gentler; and finally room, measured against the trunk and against the forks
 * already planned.
 *
 * *Every* motif is fitted here, not just the one that will be rolled, so the
 * roll afterwards cannot land on a lane whose geometry was never checked — and
 * the count of motifs that fit is what the window is ranked on, because a window
 * offering one shape is a window whose roll has already been made for it.
 */
function fitWindow(
  nodes: Node[],
  i0: number,
  i1: number,
  padIdx: Set<number>,
  padYs: number[],
  taken: LaneShape[],
): WindowFit | null {
  const entry = nodes[i0] as Node;
  const exit = nodes[i1] as Node;
  const span = i1 - i0;
  const dy = exit.y - entry.y;
  if (dy <= 0) return null;
  for (let i = i0; i <= i1; i++) if (padIdx.has(i)) return null;
  for (const padY of padYs) {
    // The band a lobby floor owns: the slab itself, and everything close enough
    // under it to be roofed. A branch inside it would be thrown out to the rim
    // by `sealApproaches` — geometry the planner never agreed to.
    const lo = padY - LOBBY_CLEARANCE - LOBBY_FORK_BELOW;
    const hi = padY + LOBBY_FORK_ABOVE;
    if (entry.y <= hi && exit.y >= lo) return null;
  }

  // The lines worth trying. `opposite` — the other way round the shaft, which
  // two routes can never be confused across — and then a ladder of `parallel`
  // radii: clear outside everything the trunk did here, clear inside it, and the
  // two limits of the window's own band. Which of these is even distinct from
  // the others depends entirely on where the trunk went and how wide the zone
  // it went there in is: in the Obsidian spire all four collapse onto each
  // other, and on the Verdant skirt they are four genuinely different lines.
  const band = windowBand(entry, exit);
  const rMax = laneRadiusMax(band.hi);
  let rOuter = 0;
  let rInner = Infinity;
  for (let i = i0; i <= i1; i++) {
    const r = (nodes[i] as Node).radius;
    rOuter = Math.max(rOuter, r);
    rInner = Math.min(rInner, r);
  }
  const dTheta = exit.theta - entry.theta;
  const swept = Math.abs(dTheta);
  const candidates: Array<{ shape: ForkShape; sweep: number; rHold: number; ease: number }> = [];
  for (const ease of [1, 2]) {
    if (swept >= SWEEP_MIN && swept <= SWEEP_MAX) {
      candidates.push({
        shape: 'opposite',
        sweep: dTheta - Math.sign(dTheta) * Math.PI * 2,
        rHold: clamp((entry.radius + exit.radius) * 0.5, R_MIN, rMax),
        ease,
      });
    }
    for (const r of [rOuter + LANE_RADIAL_SEP, rInner - LANE_RADIAL_SEP, band.lo, rMax]) {
      candidates.push({
        shape: 'parallel',
        sweep: dTheta,
        rHold: clamp(r, R_MIN, rMax),
        ease,
      });
    }
  }

  // Which motifs could open here at all, on zone alone — the geometry is next.
  const eligible = MOTIF_ORDER.filter((id) => {
    const spec = MOTIFS[id];
    return (
      entry.zone >= spec.minZone &&
      exit.zone >= spec.minZone &&
      entry.zone <= spec.maxZone &&
      exit.zone <= spec.maxZone
    );
  });

  // Whether a line is far enough from the trunk is settled by measuring it, not
  // by the radius it was asked to hold: a `parallel` branch cuts the corner the
  // trunk wandered round, so it can be a long way from it at the same radius —
  // and the radial band is not wide enough to insist on separating everything
  // that way.
  //
  // Each motif bends the line it is given its own way, so each gets its own
  // radius and its own hop count off the same candidate; what the candidates
  // are ranked on is how many motifs came through, and then how cheaply the
  // cheapest of them did it. Ties go to the earlier candidate, so `opposite`
  // keeps its precedence over the parallel ladder.
  let best: Map<MotifId, MotifOption> | null = null;
  let bestCount = 0;
  let bestHops = Infinity;
  for (const cand of candidates) {
    const found = new Map<MotifId, MotifOption>();
    for (const id of eligible) {
      const spec = MOTIFS[id];
      const bounds = hopBounds(spec.pace, dy, span);
      if (bounds === null) continue;
      const lo = Math.max(bounds.lo, spec.minBody + 1);
      const hi = Math.min(bounds.hi, spec.maxBody + 1, LANE_HOPS_MAX);
      const ease = spec.ease ?? cand.ease;
      const rHold = clamp(spec.rHold(cand.rHold, band), R_MIN, rMax);
      const fit = fitLane(entry, exit, cand.sweep, rHold, rMax, ease, id, lo, hi);
      if (fit === null) continue;
      // The bounds are solved in floats; the promise is re-read off the hop
      // count that actually came back, so a rounding step cannot sell a fork
      // whose numbers do not hold.
      const gain = dy / fit.hops - dy / span;
      if (spec.pace === 'fast' ? gain < FORK_RISE_GAIN : gain > -LANE_RISE_LOSS) continue;
      if (!laneIsClear(fit.shape, entry, exit, nodes, taken)) continue;
      found.set(id, {
        shape: cand.shape,
        sweep: cand.sweep,
        rHold,
        rMax,
        ease,
        hops: fit.hops,
        lane: fit.shape,
      });
    }
    if (found.size === 0) continue;
    let hops = Infinity;
    for (const opt of found.values()) hops = Math.min(hops, opt.hops);
    if (found.size < bestCount || (found.size === bestCount && hops >= bestHops)) continue;
    best = found;
    bestCount = found.size;
    bestHops = hops;
  }
  return best === null ? null : { entry: i0, exit: i1, options: best };
}

/**
 * The motif a window is actually built from: the rolled one where it fits, and
 * otherwise the next one that does, scanning {@link MOTIF_ORDER} cyclically from
 * where the roll landed.
 *
 * Cyclic rather than a fixed preference list on purpose. A fixed list would send
 * every miss to the same motif, and misses are not rare — half the windows on
 * the mountain fit two or three shapes out of six — so the fallback would end up
 * the most common thing on the course. Walking on from the roll spreads them.
 */
function clampMotif(rolled: MotifId, options: Map<MotifId, MotifOption>): MotifId {
  const from = MOTIF_ORDER.indexOf(rolled);
  for (let k = 0; k < MOTIF_ORDER.length; k++) {
    const id = MOTIF_ORDER[(from + k) % MOTIF_ORDER.length] as MotifId;
    if (options.has(id)) return id;
  }
  return rolled;
}

/**
 * Decide where the climb splits.
 *
 * The scan walks candidate entry nodes in trunk order and, at each one, tries
 * every legal span and keeps the window that buys the most speed. Accepting a
 * window jumps the scan past its exit plus the cooldown, so forks never nest and
 * never touch. Zone quotas cap the count; falling short in a zone is allowed and
 * happens — a zone whose trunk barely turned has no window that could be made
 * faster, and inventing one there would mean a branch that is a slower way up.
 *
 * **RNG discipline.** The scan itself draws nothing: which windows are accepted
 * is a pure function of the trunk's geometry. The one roll per fork — its motif —
 * happens afterwards, once per *accepted* window in acceptance order, so the
 * stream depends on how many forks there are and nothing else. The roll is made
 * unconditionally over the whole motif table and then clamped to what the window
 * can actually build (see {@link clampMotif}) rather than skipped, so a window
 * that cannot take mushrooms does not shift every later fork's motif.
 *
 * Because the roll comes after every window has been accepted, a window cannot
 * know which of its motifs will win — so *all* of them are reserved: every
 * fitting motif's nominal geometry goes into `taken`, and later windows keep
 * clear of the lot. Conservative by a metre or two, and the only way the lane
 * that gets built is guaranteed to be one the neighbours were measured against.
 */
function planForks(rng: Rng, nodes: Node[], checkpointIdx: number[]): ForkPlan[] {
  const n = nodes.length;
  // Whatever the summit approach is, it is not a place to be offered a choice.
  const flareFrom = Math.max(1, Math.floor(n * (1 - FLARE_FRACTION)));
  const padIdx = new Set<number>(checkpointIdx);
  const padYs = checkpointIdx.map((i) => (nodes[i] as Node).y);
  const quota = FORKS_PER_ZONE.slice();

  const chosen: WindowFit[] = [];
  const taken: LaneShape[] = [];

  let openFrom = FORK_MIN_INDEX;
  for (let i0 = FORK_MIN_INDEX; i0 + FORK_SPAN_MIN < flareFrom; i0++) {
    if (i0 < openFrom) continue;
    const zone = (nodes[i0] as Node).zone;
    if ((quota[zone] ?? 0) <= 0) continue;
    // Shortest window first: every window that gets this far already keeps its
    // promise, so a longer one buys nothing except the height the *next* fork
    // needed — and between two lobby floors there is only room for so many.
    // Among the short ones, the window that offers the most shapes wins, so the
    // roll still gets a say in what the branch turns out to be. A mountain of
    // nothing but red ladders is one idea repeated, not a choice offered five
    // times.
    let best: WindowFit | null = null;
    let bestSpan = 0;
    let rich: WindowFit | null = null;
    for (let span = FORK_SPAN_MIN; span <= FORK_SPAN_MAX; span++) {
      const i1 = i0 + span;
      if (i1 >= flareFrom) break;
      // Past the point where a longer window could still be worth the wait.
      if (best !== null && span > bestSpan + FORK_SPAN_INDULGENCE) break;
      const fit = fitWindow(nodes, i0, i1, padIdx, padYs, taken);
      if (fit === null) continue;
      if (best === null) {
        best = fit;
        bestSpan = span;
      }
      if (rich === null || fit.options.size > rich.options.size) rich = fit;
    }
    if (rich !== null && best !== null && rich.options.size > best.options.size) best = rich;
    if (best === null) continue;
    chosen.push(best);
    for (const opt of best.options.values()) taken.push(opt.lane);
    quota[zone] = (quota[zone] as number) - 1;
    openFrom = best.exit + FORK_COOLDOWN;
  }

  return chosen.map((fit): ForkPlan => {
    const motif = clampMotif(pickWeighted(rng, FORK_MOTIF_WEIGHTS), fit.options);
    const opt = fit.options.get(motif) as MotifOption;
    return {
      entry: fit.entry,
      exit: fit.exit,
      shape: opt.shape,
      role: MOTIFS[motif].role,
      motif,
      sweep: opt.sweep,
      rHold: opt.rHold,
      rMax: opt.rMax,
      ease: opt.ease,
      hops: opt.hops,
    };
  });
}

/**
 * Lay a branch's own nodes down and thread them onto a new lane.
 *
 * The endpoints belong to the trunk and are not touched — that is what makes a
 * fork a fork. Everything between them is appended to the flat array, so trunk
 * indices keep meaning what they meant.
 *
 * Heights are *not* jittered. The rise per hop is exactly what {@link fitLane}
 * solved for, and on a bold lane that number is already most of what a bounce
 * can clear; the radius and angle carry the jitter instead, at half the trunk's
 * amplitude, which is what the planner's tighter gap budget pays for.
 */
function walkLane(rng: Rng, plan: ForkPlan, course: Course): void {
  const nodes = course.nodes;
  const entry = nodes[plan.entry] as Node;
  const exit = nodes[plan.exit] as Node;
  const shape = laneShape(
    entry,
    exit,
    plan.sweep,
    plan.rHold,
    plan.rMax,
    plan.ease,
    plan.hops,
    plan.motif,
  );
  const id = course.lanes.length;
  const body: number[] = [];
  for (let j = 0; j < shape.theta.length; j++) {
    // Both draws happen for every node, in a fixed order.
    const dTheta = randRange(rng, -LANE_ANGLE_JITTER, LANE_ANGLE_JITTER);
    const dRadius = randRange(rng, -LANE_RADIAL_JITTER, LANE_RADIAL_JITTER);
    const theta = (shape.theta[j] as number) + dTheta;
    const r = clamp((shape.radius[j] as number) + dRadius, R_MIN, plan.rMax);
    const y = shape.y[j] as number;
    body.push(nodes.length);
    nodes.push({
      x: r * Math.cos(theta),
      y,
      z: r * Math.sin(theta),
      theta,
      radius: r,
      yaw: 0,
      zone: zoneIndexAt(uAtY(y)),
      kind: 'platform',
      lane: id,
    });
  }
  const island = MOTIFS[plan.motif].islandAt(body.length);
  if (island >= 0 && island < body.length) course.islands.add(body[island] as number);
  course.lanes.push({
    id,
    role: plan.role,
    motif: plan.motif,
    entry: plan.entry,
    exit: plan.exit,
    body,
  });
}

/**
 * The trunk's set pieces: which of its nodes wear a ledge, which stand on a
 * column, and which spur tips are candidates for an island.
 *
 * Nothing here *moves* a node. Trunk positions are load-bearing — the pads, the
 * funnels and every fork window were solved against them — so a set piece is a
 * kind and a decoration and nothing else. What it keys off is the shape the walk
 * was already making at that node (see {@link walkTrunk}): a `tighten` closing on
 * the shaft gets a run of narrow ledges down its inside, a `switchback` gets
 * columns through its turn, a `spur` gets somewhere to stand at its tip.
 *
 * Both runs take every *other* eligible node. A ledge has to: it is one of the
 * kinds that may never sit beside another (see `exclusive`), and the toggle is
 * per eligible node rather than per node, so skipping one for any other reason
 * can never quietly put two together. Columns do not have to but read better for
 * it, and a switchback only gets them over the first half of its segment — the
 * turn itself, which is the part worth marking. Half of every switchback would
 * be a quarter of the whole trunk on a seed that reverses often, and set pieces
 * are seasoning.
 *
 * `blocked` carries everything whose kind is already spoken for: the pads, the
 * forced funnels, the junction anchors and the solid ends of the climb.
 */
function trunkSetPieces(
  course: Course,
  trunkPath: number[],
  blocked: Set<number>,
): { ledges: Set<number>; pillars: Set<number>; spurs: number[] } {
  const nodes = course.nodes;
  const ledges = new Set<number>();
  const pillars = new Set<number>();
  const spurs: number[] = [];

  let k = 0;
  while (k < trunkPath.length) {
    const seg = course.segment[k];
    let end = k;
    while (end < trunkPath.length && course.segment[end] === seg) end++;
    const pattern = course.pattern[k];

    if (pattern === 'spur') {
      // The tip: the outermost node of the segment. Whether it gets an island is
      // decided later, once its kind is known.
      let apex = -1;
      let apexR = -Infinity;
      for (let p = k; p < end; p++) {
        const at = trunkPath[p] as number;
        const r = (nodes[at] as Node).radius;
        if (r > apexR) {
          apexR = r;
          apex = at;
        }
      }
      if (apex >= 0) spurs.push(apex);
    } else if (pattern === 'tighten' || pattern === 'switchback') {
      const target = pattern === 'tighten' ? ledges : pillars;
      const until = pattern === 'tighten' ? end : k + Math.ceil((end - k) / 2);
      let take = true;
      for (let p = k; p < until; p++) {
        const at = trunkPath[p] as number;
        if ((nodes[at] as Node).zone < TRUNK_SET_PIECE_MIN_ZONE) continue;
        if (blocked.has(at)) continue;
        // A ledge owes the same room to its neighbours that a rolled one does —
        // including across a segment seam, where the toggle has just reset.
        if (pattern === 'tighten') {
          const back = trunkPath[p - 1];
          const ahead = trunkPath[p + 1];
          if (back !== undefined && (blocked.has(back) || ledges.has(back))) continue;
          if (ahead !== undefined && blocked.has(ahead)) continue;
        }
        if (take) target.add(at);
        take = !take;
      }
    }
    k = end;
  }
  return { ledges, pillars, spurs };
}

/**
 * Choose a node type for every node, honouring the adjacency and forcing rules.
 *
 * Iteration is over the flat array — that is the order the RNG is drawn in, and
 * it must not move — but every *adjacency* question is asked of the lane that
 * owns the node, because "the node before this one" means the one you jumped
 * from, not the one stored beside it.
 *
 * Checkpoints and forced funnels are **trunk business**. Both are chosen by
 * height, and a branch node passing a boundary is at the right height by
 * accident; letting one win would move a pad onto a route half the players never
 * take, or hide the level's two guaranteed punishers on an optional detour.
 */
function assignKinds(rng: Rng, course: Course): void {
  const nodes = course.nodes;
  const n = nodes.length;
  const { prev, next } = laneNeighbours(course);

  const trunkLane = course.lanes.find((lane) => lane.role === 'trunk');
  const trunkPath = trunkLane !== undefined ? lanePath(course, trunkLane) : [];
  const checkpoints = new Set<number>(
    pickCheckpointIndices(trunkPath.map((i) => (nodes[i] as Node).y)).map(
      (k) => trunkPath[k] as number,
    ),
  );

  // Forced funnels: the trunk node nearest each target height. A pad is the one
  // place you are promised safety, so the search steps over them to the
  // next-nearest.
  const funnelForced = new Set<number>();
  for (const targetY of FUNNEL_TARGETS) {
    let best = -1;
    let bestD = Infinity;
    for (const i of trunkPath) {
      if (checkpoints.has(i)) continue;
      const d = Math.abs((nodes[i] as Node).y - targetY);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best >= 0) funnelForced.add(best);
  }
  /**
   * The last stretch of the *trunk path* — not of the array. Once branches are
   * appended, "near the end of `nodes`" stops meaning "near the summit".
   */
  const nearSummit = new Set<number>();
  for (let k = Math.max(0, trunkPath.length - SUMMIT_APPROACH_NODES); k < trunkPath.length; k++) {
    nearSummit.add(trunkPath[k] as number);
  }
  /**
   * Junction anchors. A ledge or a funnel at a fork is a coin flip on both
   * routes at once, so every node two lanes share is a plain wide platform.
   */
  const junctions = new Set<number>();
  for (const lane of course.lanes) {
    if (lane.role === 'trunk') continue;
    junctions.add(lane.entry);
    junctions.add(lane.exit);
  }
  /**
   * Kinds a lane's motif decides for itself — a `mushroomFlight` alternates
   * mushroom and platform, a `ledgeWall` alternates ledge and platform, and so
   * on down {@link MOTIFS}. The shape *is* the lane, and rolling it would make
   * taking the same fork twice tell you nothing. A `traverse` is the exception
   * and forces nothing: it is a shortcut, not a statement, and its nodes roll
   * their zone's own table like the trunk's do.
   */
  const motifKind = new Map<number, NodeKind>();
  for (const lane of course.lanes) {
    if (lane.role === 'trunk') continue;
    const spec = MOTIFS[lane.motif];
    const m = lane.body.length;
    for (let b = 0; b < m; b++) {
      const kind = spec.kindAt(b, m);
      if (kind !== null) motifKind.set(lane.body[b] as number, kind);
    }
  }

  /** The two ends of the trunk, read off its path rather than off the array. */
  const solid = new Set<number>();
  if (trunkPath.length > 0) solid.add(trunkPath[0] as number);
  for (let k = Math.max(0, trunkPath.length - 2); k < trunkPath.length; k++) {
    solid.add(trunkPath[k] as number);
  }

  /**
   * The trunk's own seasoning, keyed off the shape the walk was making. Decided
   * before anything is rolled, so the rolls that remain can be checked against
   * it in both directions — a forced ledge joins `forced` below, which is what
   * stops a rolled ledge or funnel landing next to one.
   */
  const setPieces = trunkSetPieces(
    course,
    trunkPath,
    new Set<number>([...checkpoints, ...funnelForced, ...junctions, ...solid]),
  );

  /**
   * Every node whose kind is decided up front *and* is one of the exclusive
   * kinds: the pads, the forced funnels and the forced ledges.
   *
   * Forced pillars are deliberately not here, and stayed out when the kind
   * became a column. A column cap is narrow — narrower than most pads — but it
   * is *round*, and that is the whole difference: there is no wrong way to come
   * at it and no long axis to miss the short way, which is exactly what makes a
   * ledge a one-chance landing. Two columns in a row is a colonnade, which the
   * `pillarForest` motif builds on purpose; two ledges in a row is a coin flip.
   */
  const forced = new Set<number>([...checkpoints, ...funnelForced, ...setPieces.ledges]);

  // A ledge and a funnel are both "you get one narrow chance" nodes; two in a
  // row is not difficulty, it is a coin flip. Checkpoints join the club from the
  // other side: a pad you cannot reliably reach or leave is not a rest stop.
  const exclusive = (k: NodeKind | undefined): boolean =>
    k === 'ledge' || k === 'funnel' || k === 'checkpoint';
  /** Forced funnels still to come — they always win against the budget. */
  const forcedAfter = (i: number): number => {
    let c = 0;
    for (const f of funnelForced) if (f > i) c++;
    return c;
  };
  let funnels = 0;
  const conflicts = (k: NodeKind, i: number): boolean => {
    if (k === 'funnel') {
      const node = nodes[i] as Node;
      // Funnels are the trunk's punishers. A branch is already the risky way up;
      // ringing it with a collar as well is two bills for the same choice.
      if (node.lane !== 0) return true;
      if (node.y < FUNNEL_MIN_Y) return true;
      if (nearSummit.has(i)) return true; // keep the summit approach clean
      if (funnels + forcedAfter(i) >= MAX_FUNNELS) return true;
    }
    if (!exclusive(k)) return false;
    // Adjacency along the lane: the node you came from, and the one you leave
    // for. On the trunk these are `i − 1` and `i + 1`.
    const back = prev[i] as number;
    const ahead = next[i] as number;
    if (back >= 0 && exclusive(nodes[back]?.kind)) return true;
    return ahead >= 0 && forced.has(ahead);
  };

  for (let i = 0; i < n; i++) {
    const node = nodes[i] as Node;
    if (checkpoints.has(i)) {
      node.kind = 'checkpoint';
      continue;
    }
    if (funnelForced.has(i)) {
      node.kind = 'funnel';
      funnels++;
      continue;
    }
    // The trunk's first node and its last two are always solid: a guaranteed
    // first target and a guaranteed launch pad for the summit. Junctions join
    // them — a fork has to be a decision, not a landing problem.
    if (solid.has(i) || junctions.has(i)) {
      node.kind = 'platform';
      continue;
    }
    const motif = motifKind.get(i);
    if (motif !== undefined) {
      node.kind = motif;
      continue;
    }
    // Trunk set pieces, decided off the walk's own shape. Like every other
    // forcing they short-circuit the roll rather than filtering it, which is
    // what keeps one node to at most one draw.
    if (setPieces.ledges.has(i)) {
      node.kind = 'ledge';
      continue;
    }
    if (setPieces.pillars.has(i)) {
      node.kind = 'pillar';
      continue;
    }
    const weights = zoneOf(node).weights;
    let kind = pickWeighted(rng, weights);
    for (let attempt = 0; attempt < 3 && conflicts(kind, i); attempt++) {
      kind = pickWeighted(rng, weights);
    }
    node.kind = conflicts(kind, i) ? 'platform' : kind;
    if (node.kind === 'funnel') funnels++;
  }

  // A mini rest island on the tip of each spur — but only where the tip came out
  // a plain platform. Under a ramp the slab would be a second floor at a
  // different angle, and under anything narrow it would quietly undo the
  // narrowness, which was the point of that node.
  for (const at of setPieces.spurs) {
    if ((nodes[at] as Node).kind === 'platform') course.islands.add(at);
  }
}

// ---------------------------------------------------------------------------
// Primitive emission
// ---------------------------------------------------------------------------

/**
 * The most a node's pad may be widened before its corner would reach into the
 * hollow shaft. Never below 1: `R_MIN` was chosen so an *unscaled* pad always
 * clears, and this only governs the widening on top of that.
 */
function junctionRoom(node: Node): number {
  const half = zoneOf(node).padHalf;
  return Math.max(1, (node.radius - JUNCTION_SHAFT_KEEP) / Math.hypot(half[0], half[2]));
}

/**
 * How far below the node line a `pillar`'s column *wants* to run.
 *
 * Pure math off numbers the node already has, which is what lets
 * {@link nodeFootprint} and {@link emitPillar} agree without either of them
 * drawing: a draw here would move the stream for every node after it, and the
 * whole point of a set piece is that deciding to build one changes nothing else.
 *
 * The zone sets the base — the ruins' columns are stubs of something that was
 * taller, the frozen shelf's are piers driven into the ice, so they get deeper
 * as the climb goes up — and the node's own angle wobbles it either side. Two
 * neighbours in a colonnade come out different depths, which is the difference
 * between a row of columns and a comb.
 *
 * A wish, not an answer: what gets built is this cut back to what is underneath,
 * by {@link trimPillars}. Read it through {@link pillarDepth}.
 */
function pillarDepthWanted(node: Node): number {
  const base = lerp(PILLAR_DEPTH_MIN, PILLAR_DEPTH_MAX, node.zone / (ZONES.length - 1));
  const wobble = 0.5 - 0.5 * Math.cos(node.theta * 3 + node.y * 0.35);
  return clamp(base - 1.2 + 2.4 * wobble, PILLAR_DEPTH_MIN, PILLAR_DEPTH_MAX);
}

/**
 * How far below the node line a `pillar`'s column actually runs — the trimmed
 * depth where the course has been settled, and the wish everywhere else.
 *
 * Every reader goes through here, so {@link nodeFootprint}, {@link emitPillar}
 * and the mover planner can never disagree about how much rock is hanging under
 * a cap. The fallback matters: {@link trimPillars} runs at the end of the layout,
 * and anything asking before that is asking about a node whose neighbours are
 * not settled yet, where the wish is the only honest answer.
 */
function pillarDepth(node: Node): number {
  return node.drop ?? pillarDepthWanted(node);
}

/**
 * Cut every column back to what is standing under it.
 *
 * A column is the one piece of geometry on this mountain sized without looking
 * at anything else — {@link pillarDepthWanted} reads a zone and an angle and
 * nothing more — and four to seven metres of solid rock hanging under a node
 * reaches a long way down. On a climb that deliberately puts its laps within a
 * few metres of each other, that is enough to drive a shaft straight through the
 * pad below: the self-test's §9 catches it, but catching it costs a seed, and
 * the seed was not the thing that was wrong.
 *
 * So the wish is trimmed. Anything whose footprint the column would come down
 * inside — measured in plan, column radius plus that landing's own reach — pushes
 * the column's bottom up to `PILLAR_CLEAR` above its top face, and the shortest
 * of all those answers wins. Nothing shorter than `PILLAR_DEPTH_FLOOR` is built:
 * past that the cure is worse than the disease, and §9 is still there to fail the
 * seed if two surfaces really do end up growing through each other.
 *
 * Pure geometry over nodes and kinds that are already settled — it draws nothing,
 * it moves nothing, and it runs last in the layout for exactly that reason. The
 * only thing it reads that a column could itself change is another landing's
 * *top* face, which no depth can move.
 */
function trimPillars(course: Course): void {
  const nodes = course.nodes;
  const junctions = new Set<number>();
  for (const lane of course.lanes) {
    if (lane.role === 'trunk') continue;
    junctions.add(lane.entry);
    junctions.add(lane.exit);
  }
  const feet = nodes.map((node, i) =>
    nodeFootprint(node, junctions.has(i), course.islands.has(i)),
  );
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as Node;
    if (node.kind !== 'pillar') continue;
    let depth = pillarDepthWanted(node);
    for (let j = 0; j < nodes.length; j++) {
      if (j === i) continue;
      const foot = feet[j];
      if (foot === undefined || foot === null) continue;
      // Only what is under the cap can be pierced, and only what the column
      // would come down on top of: everything else is somewhere else in plan.
      if (foot.hi >= node.y) continue;
      const other = nodes[j] as Node;
      if (Math.hypot(other.x - node.x, other.z - node.z) >= PILLAR_RADIUS + foot.reach) continue;
      depth = Math.min(depth, node.y - foot.hi - PILLAR_CLEAR);
    }
    node.drop = Math.max(depth, PILLAR_DEPTH_FLOOR);
  }
}

/**
 * The footprint a node's landing occupies: how far it reaches from the node
 * whichever way it is turned, and the slice of height it fills. `null` for the
 * kinds that do not put a box on the node line at all — a mushroom is a sunk
 * sphere and a checkpoint is a disc on its own sealed floor, and neither is
 * something another pad can grow through.
 *
 * A ramp is measured at the steepest tilt the roll can produce rather than at
 * the one it got, because this is worked out before the tilt is drawn. That
 * makes the answer conservative in the only direction that is safe: a ramp is
 * given more room than it turns out to need, never less.
 *
 * A pillar is the one kind whose footprint is *taller* than it is wide: a narrow
 * cap with several metres of column hanging under it, and the column is as solid
 * as the cap. Reporting only the cap would let {@link padScales} wave through a
 * landing four metres lower that the shaft goes straight through.
 */
function nodeFootprint(
  node: Node,
  junction: boolean,
  island: boolean,
): { reach: number; lo: number; hi: number } | null {
  const half = zoneOf(node).padHalf;
  let reach: number;
  let lo: number;
  let hi: number;
  switch (node.kind) {
    case 'mushroom':
    case 'checkpoint':
      return null;
    case 'ledge':
      reach = Math.hypot(LEDGE_HALF[0], LEDGE_HALF[2]);
      lo = node.y - 2 * LEDGE_HALF[1];
      hi = node.y;
      break;
    case 'ramp':
      reach = Math.hypot(half[0], half[2]);
      lo = node.y - 2 * half[1] * Math.cos(RAMP_TILT_MAX) - half[2] * Math.sin(RAMP_TILT_MAX);
      hi = node.y + half[2] * Math.sin(RAMP_TILT_MAX);
      break;
    case 'pillar':
      reach = PILLAR_RADIUS;
      lo = node.y - pillarDepth(node);
      hi = node.y;
      break;
    default:
      // Platforms, and the one kind that still stands on one: funnels.
      reach =
        Math.hypot(half[0], half[2]) *
        (junction ? Math.min(JUNCTION_PAD_SCALE, junctionRoom(node)) : 1);
      lo = node.y - 2 * half[1];
      hi = node.y;
      break;
  }
  if (island) {
    reach = Math.max(reach, islandHalf(node) * Math.SQRT2);
    lo = Math.min(lo, node.y - ISLAND_DROP - 2 * ISLAND_THICK);
  }
  return { reach, lo, hi };
}

/**
 * How much each node's landing has to give back so that no two of them grow
 * through each other.
 *
 * Two intersecting pads read as one broken shape, and physics resolves an
 * overlap by picking a face — so a landing inside another one throws you out
 * sideways through whichever it happened to pick. The climb nonetheless *wants*
 * landings that crowd: the two nodes a branch hangs off its junctions are one
 * hop from the pad they leave, and a hop on this mountain is shorter than a pad
 * is wide. Refusing those forks would be paying for a seam with a route.
 *
 * So the pads give ground instead. Where two footprints would meet, both are
 * scaled down by the same factor until they clear each other by `PAD_KEEP` —
 * both, so that neither node is singled out as the one that pays, and by a
 * factor rather than a subtraction so a pad keeps its shape. Nothing is moved
 * and nothing is dropped: the node is still exactly where every hop was solved
 * against, it is simply a smaller thing to land on, which is the honest price of
 * putting two routes that close together.
 *
 * Pure geometry over nodes and kinds that already exist, so it draws nothing.
 * The one pass is conservative: the reaches it compares are the unshrunk ones,
 * so a pad shrunk twice ends up further clear than it strictly had to be.
 */
function padScales(course: Course, junctions: Set<number>): number[] {
  const nodes = course.nodes;
  const scale = new Array<number>(nodes.length).fill(1);
  const feet = nodes.map((node, i) =>
    nodeFootprint(node, junctions.has(i), course.islands.has(i)),
  );
  for (let i = 0; i < nodes.length; i++) {
    const a = feet[i];
    if (a === undefined || a === null) continue;
    const na = nodes[i] as Node;
    for (let j = i + 1; j < nodes.length; j++) {
      const b = feet[j];
      if (b === undefined || b === null) continue;
      // Height first: it is what separates the great majority of close pairs,
      // including every pair of consecutive landings on any one lane.
      if (Math.min(a.hi, b.hi) - Math.max(a.lo, b.lo) <= 0) continue;
      const nb = nodes[j] as Node;
      const d = Math.hypot(nb.x - na.x, nb.z - na.z);
      const want = a.reach + b.reach;
      if (d >= want + PAD_KEEP) continue;
      const s = Math.max(PAD_MIN_SCALE, (d - PAD_KEEP) / want);
      if (s < (scale[i] as number)) scale[i] = s;
      if (s < (scale[j] as number)) scale[j] = s;
    }
  }
  return scale;
}

/**
 * The standard landing pad, sized and coloured by the node's zone.
 *
 * `scale` widens the footprint (never the thickness) for the one case that needs
 * it: a junction, where two routes leave the same pad and the moment of choosing
 * should not also be a moment of falling off. It is capped by the room the node
 * actually has — a pad's corner may not reach into the hollow shaft whichever
 * way the pad is turned, and `R_MIN` only ever promised that for an unscaled one.
 * It also comes back *below* one wherever {@link padScales} found the node too
 * close to another landing to keep its full width.
 */
function emitPlatform(out: Primitive[], node: Node, scale = 1): void {
  const zone = zoneOf(node);
  const half = zone.padHalf;
  const s = Math.min(scale, junctionRoom(node));
  out.push({
    kind: 'box',
    center: [node.x, node.y - half[1], node.z],
    half: [half[0] * s, half[1], half[2] * s],
    quat: quatYaw(node.yaw),
    material: zone.mats.platform,
  });
}

/**
 * Two small arrows on a junction pad, pointing the way the branch goes.
 *
 * A fork nobody can see is not a fork — it is a dead end that happens to loop
 * back. They sit *on* the pad rather than out past its edge, both because that
 * is where you are standing when the choice is in front of you and because a
 * marker a few metres inward of a node hugging the inner limit would be hanging
 * in the hollow shaft.
 */
function emitJunctionMarker(out: Primitive[], anchor: Node, first: Node, role: LaneRole): void {
  const dx = first.x - anchor.x;
  const dz = first.z - anchor.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return;
  const ux = dx / len;
  const uz = dz / len;
  const yaw = yawAlong(dx, dz);
  const material =
    role === 'bold' ? MAT_MARK_BOLD : role === 'safe' ? MAT_MARK_SAFE : MAT_MARK_SCENIC;
  for (let k = 0; k < 2; k++) {
    const d = 2.2 + k * 0.9;
    out.push({
      kind: 'box',
      center: [anchor.x + ux * d, anchor.y + 0.08, anchor.z + uz * d],
      half: [0.5, 0.08, 0.25],
      quat: quatYaw(yaw),
      material,
    });
  }
}

function emitLedge(out: Primitive[], node: Node, scale = 1): void {
  out.push({
    kind: 'box',
    center: [node.x, node.y - LEDGE_HALF[1], node.z],
    half: [LEDGE_HALF[0] * scale, LEDGE_HALF[1], LEDGE_HALF[2] * scale],
    quat: quatYaw(node.yaw),
    material: zoneOf(node).mats.ledge,
  });
}

function emitRamp(out: Primitive[], node: Node, rng: Rng, scale = 1): void {
  const zone = zoneOf(node);
  const half = zone.padHalf;
  // 18°–26° — steep enough to redirect a bounce, well under the 50° slide angle.
  const tilt = randRange(rng, RAMP_TILT_MIN, RAMP_TILT_MAX) * (rng() < 0.5 ? -1 : 1);
  out.push({
    kind: 'box',
    center: [node.x, node.y - half[1] * Math.cos(tilt), node.z],
    half: [half[0] * scale, half[1], half[2] * scale],
    quat: quatMul(quatYaw(node.yaw), quatTiltX(tilt)),
    material: zone.mats.ramp,
  });
}

function emitMushroom(out: Primitive[], node: Node): void {
  // Sunk below the node line so only the springy cap sticks up.
  out.push({
    kind: 'sphere',
    center: [node.x, node.y - MUSHROOM_SINK, node.z],
    radius: MUSHROOM_RADIUS,
    material: MAT_MUSHROOM,
  });
}

/**
 * How far outward of a node a prop may actually stand.
 *
 * Props are always pushed *outward* — inward would eat into the hollow shaft
 * wherever the walk is hugging it — and what they aim at is the climb's own
 * outer limit, `R_HARD_MAX`. A node out on the Verdant rim is already most of
 * the way there, and six metres past it is off the mountain entirely, so the
 * asked-for distance is cut to whatever is left once the prop's own `reach` (its
 * largest half-extent away from its centre) has been paid for.
 *
 * Never negative and never so short the prop ends up standing on the pad it was
 * decorating — which is the one case where the answer overshoots the limit it
 * was aiming at, because a prop *on* its own landing is worse than a prop a
 * metre off the edge. That is legal now rather than merely tolerated: decor is
 * checked against `LANDMARK_R_MAX` rather than `R_HARD_MAX` (see
 * `level.selftest.ts` §5g), and the floor here cannot reach anywhere near it.
 */
function outwardRoom(node: Node, want: number, reach: number): number {
  return Math.min(want, Math.max(PROP_MIN_OFFSET, R_HARD_MAX - reach - node.radius));
}

/**
 * A column, and the whole node: one thick cylinder running down from the node
 * line, landed on the flat of its cap.
 *
 * This kind used to be a platform with a thin post standing beside it, which is
 * two shapes saying different things — you land on the slab and the post is
 * scenery you have to remember is not a slab. One shape says it once: the top of
 * the column *is* the landing, it is narrower than a pad because a pillar was
 * always meant to be the tight option, and there is nothing else there to read.
 *
 * `scale` is {@link padScales}' trimming, applied to the radius for the same
 * reason it is applied to a pad's half-extents — the cap is the landing, and a
 * landing that crowds another one gives ground. It is floored well above the
 * general `PAD_MIN_SCALE`: half a platform is still somewhere to stand, whereas
 * a column trimmed to a third of its radius is a stick, and this whole change
 * exists to get rid of sticks. A pillar's reach is small enough that it is
 * rarely the pad that has to give anything up in the first place, and where the
 * floor does bite and leaves two things genuinely growing through each other,
 * `level.selftest.ts` §9 fails the seed rather than shipping it — the floor is a
 * preference, the intersection test is the law.
 */
function emitPillar(out: Primitive[], node: Node, scale = 1): void {
  out.push({
    kind: 'cylinder',
    a: [node.x, node.y - pillarDepth(node), node.z],
    b: [node.x, node.y, node.z],
    radius: PILLAR_RADIUS * Math.max(scale, PILLAR_MIN_SCALE),
    material: zoneOf(node).mats.pillar,
  });
}

/** The zone's own colour for a piece of scenery that has no kind of its own. */
function zoneDecorMaterial(zoneIndex: number): Material {
  switch (zoneIndex) {
    case 0:
      return MAT_MEADOW_TRUNK;
    case 1:
      return MAT_VERDANT_TRUNK;
    case 2:
      return MAT_RUINS_COLUMN;
    case 3:
      return MAT_CRYSTAL_SHARD_A;
    case 4:
      return MAT_FROZEN_SPIKE;
    default:
      return MAT_OBSIDIAN_BASALT;
  }
}

/**
 * A wide low slab under a node, with a small monument beside it: somewhere to
 * stand that is bigger than a landing.
 *
 * It sits just *under* the pad the node already carries rather than replacing
 * it, so the node keeps the exact landing surface every hop was solved against
 * and the island is only what spills out past its edges. The half-extent is cut
 * back wherever the node is far enough out that a full-size slab's corners would
 * reach past `R_HARD_MAX` — which is exactly the case an `islandSpur` is built
 * to produce.
 */
function islandHalf(node: Node): number {
  return clamp((R_HARD_MAX - node.radius) / Math.SQRT2, ISLAND_MIN_HALF, ISLAND_HALF);
}

function emitIsland(out: Primitive[], node: Node, scale = 1): void {
  const half = islandHalf(node) * scale;
  const top = node.y - ISLAND_DROP;
  out.push({
    kind: 'box',
    center: [node.x, top - ISLAND_THICK, node.z],
    half: [half, ISLAND_THICK, half],
    quat: quatYaw(node.yaw),
    material: zoneOf(node).mats.platform,
  });
  // Off to one side, so arriving has something to arrive at and the middle of
  // the slab stays clear for the landing it is under.
  const off = Math.min(ISLAND_MONUMENT_OFF, half - 0.6);
  out.push({
    kind: 'box',
    center: [
      node.x - Math.sin(node.theta) * off,
      top + ISLAND_MONUMENT_HALF[1],
      node.z + Math.cos(node.theta) * off,
    ],
    half: [ISLAND_MONUMENT_HALF[0], ISLAND_MONUMENT_HALF[1], ISLAND_MONUMENT_HALF[2]],
    quat: quatYaw(node.yaw),
    material: zoneDecorMaterial(node.zone),
  });
}

/**
 * The lobby floor under a checkpoint: a ring of slabs spanning the whole
 * playable width, from just outside the shaft out past the course itself, in the
 * palette of the zone this boundary *opens*. It is the one place on the mountain
 * with room to stand still, and it turns a near miss above the boundary into a
 * landing instead of a fall back to the last pad.
 *
 * Its top sits `CHECKPOINT_THICK` below the node line, so the teal disc lands on
 * it as a 0.5 m dais rather than fighting it for the same plane.
 *
 * **The ring is sealed.** There is no way up through a floor: a fall from the
 * zone above ends here rather than dropping back down the mountain, and the only
 * below-to-above transition anywhere on it is around the outer rim. The course
 * arrives that way because `sealLobbyApproaches` has already thrown every node
 * the ring would have roofed outside it, so the last hops before a pad run along
 * the rim and finish by stepping up over the lip.
 *
 * Slab length is solved at the **outer** radius, not the middle: sized at the
 * mid-radius the rim would open a wedge-shaped hole between every pair of slabs,
 * which is exactly where a landing is most likely. Paying for that means the
 * slabs overlap toward the inner rim, which is harmless — except that two
 * coplanar top faces z-fight, so alternate slabs drop `LOBBY_STEP`.
 *
 * Draws no `rng`: floors are pure geometry over nodes that already exist, so
 * emitting them cannot shift anything downstream in the stream.
 */
function emitLobbyFloor(out: Primitive[], pad: Node, zone: Zone): void {
  const topY = pad.y - CHECKPOINT_THICK;
  const rOut = pad.radius + LOBBY_OUTER_M;
  const rMid = (LOBBY_INNER_R + rOut) * 0.5;
  const segAngle = (Math.PI * 2) / LOBBY_SEGMENTS;
  const halfLen = rOut * Math.tan(segAngle * 0.5);

  for (let k = 0; k < LOBBY_SEGMENTS; k++) {
    // Phased off the pad's own angle, so the dais never straddles a seam.
    const a = pad.theta + segAngle * (k + 0.5);
    out.push({
      kind: 'box',
      center: [
        rMid * Math.cos(a),
        topY - LOBBY_HALF_THICK - (k % 2 === 1 ? LOBBY_STEP : 0),
        rMid * Math.sin(a),
      ],
      half: [halfLen, LOBBY_HALF_THICK, (rOut - LOBBY_INNER_R) * 0.5],
      // Local +X along the tangent, local +Z inward — the funnel's convention.
      quat: quatYaw(tangentYaw(a)),
      material: k % 2 === 1 ? zone.mats.floorAlt : zone.mats.floor,
    });
  }
}

/**
 * Checkpoint pad: a squat teal disc with its top face on the node line, ringed
 * by four stubby posts. Deliberately unlike every zone's own furniture — the one
 * thing on the course you want recognisable from two turns below.
 */
function emitCheckpoint(out: Primitive[], node: Node): void {
  out.push({
    kind: 'cylinder',
    a: [node.x, node.y - CHECKPOINT_THICK, node.z],
    b: [node.x, node.y, node.z],
    radius: CHECKPOINT_RADIUS,
    material: MAT_CHECKPOINT,
  });
  for (let k = 0; k < 4; k++) {
    const a = node.yaw + (Math.PI * 0.5) * k;
    out.push({
      kind: 'box',
      center: [
        node.x + CHECKPOINT_RADIUS * Math.cos(a),
        node.y + CHECKPOINT_POST_HALF[1],
        node.z + CHECKPOINT_RADIUS * Math.sin(a),
      ],
      half: [CHECKPOINT_POST_HALF[0], CHECKPOINT_POST_HALF[1], CHECKPOINT_POST_HALF[2]],
      quat: quatYaw(node.yaw),
      material: MAT_CHECKPOINT,
    });
  }
}

/**
 * The signed angular step the course takes *out* of a node, falling back to the
 * one it arrived on where there is nothing ahead. Only the funnels care — their
 * gap has to open the way the lane runs — but the mover planner has to be able
 * to build the same collar the emitter will, so the two read it from here.
 */
function aheadAngle(nodes: Node[], prev: number[], next: number[], i: number): number {
  const node = nodes[i] as Node;
  const forward = nodes[next[i] as number];
  const back = nodes[prev[i] as number];
  if (forward !== undefined) return forward.theta - node.theta;
  return back !== undefined ? node.theta - back.theta : 0;
}

/**
 * Punisher funnel: a collar of inward-tilted slabs ringing the shaft at this
 * height, with a single gap facing the way the course runs. `aheadAngle` is the
 * *signed* angular step to the next node, so the gap follows the course through
 * a switchback instead of always opening counter-clockwise. The slabs are
 * at 58° — above the 50° slide angle — so a jump that sails over the node lands
 * on the collar, slides inward and drops into the shaft instead of catching the
 * platform below.
 *
 * The slab length is derived from the ring radius rather than fixed, because a
 * fixed 4 m slab leaves 60 m of holes in a 90 m circumference and stops being a
 * funnel at all.
 */
function emitFunnel(out: Primitive[], node: Node, aheadAngle: number, scale = 1): void {
  emitPlatform(out, node, scale);

  const ringR = node.radius + FUNNEL_RING_OFFSET;
  const gapAngle = Math.min(FUNNEL_GAP_M / ringR, 0.6);
  const gapCenter = node.theta + aheadAngle * 0.5;
  const segAngle = (Math.PI * 2 - gapAngle) / FUNNEL_SEGMENTS;
  const halfLen = ringR * Math.tan(segAngle * 0.5);
  const ringY = node.y - 0.4;

  for (let k = 0; k < FUNNEL_SEGMENTS; k++) {
    const a = gapCenter + gapAngle * 0.5 + segAngle * (k + 0.5);
    out.push({
      kind: 'box',
      center: [ringR * Math.cos(a), ringY, ringR * Math.sin(a)],
      half: [halfLen, FUNNEL_HALF_THICK, FUNNEL_HALF_RADIAL],
      // Local +Z points inward, so a positive tilt drops the inner edge.
      quat: quatMul(quatYaw(tangentYaw(a)), quatTiltX(FUNNEL_TILT)),
      material: MAT_FUNNEL,
    });
  }
}

/**
 * A zone's decorative prop, planted *outward* from the node — away from the
 * hollow shaft, and off the line the course actually runs. It is collidable like
 * everything else; landing on one is a legal, if unhelpful, outcome.
 *
 * Every one of the six branches below plants a **cluster**: two or three pieces
 * of the same idea, about a third bigger than the single piece each of these
 * used to be. That is the whole difference between scenery and litter. A 0.25 m
 * stick with one ball on top is not a tree — it is a lollipop, and thirty of
 * them dotted around a mountain is what the complaint about "weird poles" was
 * actually about. A
 * trunk you could not put your arms round, with three overlapping masses of leaf
 * over it, is a tree at any distance you can see it from.
 *
 * The distance is rolled unconditionally and then cut to the room the node
 * actually has: six and a half metres out from a branch node on the rim would
 * put a tree past the edge of the mountain.
 *
 * **Draw discipline.** Each branch draws a fixed number of values in a fixed
 * order, whatever it then decides to build — the third piece of a cluster is
 * chosen by a roll that happens whether or not the piece is planted, exactly as
 * the obsidian gold cap always has been. What varies is how much of what was
 * drawn gets used; what never varies is how far the stream moved.
 */
function emitDecor(out: Primitive[], node: Node, rng: Rng, zoneIndex: number): void {
  const dist = outwardRoom(
    node,
    randRange(rng, DECOR_MIN_OFFSET, DECOR_MAX_OFFSET),
    DECOR_REACH,
  );
  // Outward normal, and the tangent the pieces of a cluster are set out along.
  const nx = Math.cos(node.theta);
  const nz = Math.sin(node.theta);
  const tx = -nz;
  const tz = nx;
  const x = node.x + nx * dist;
  const z = node.z + nz * dist;
  const y = node.y - 0.5;

  switch (zoneIndex) {
    case 0: {
      // Meadow: a boulder with a bush grown up in its lee, and more often than
      // not a spray of yellow blooms beside them. This is the one zone whose
      // props *are* what there is to look at — no ledge, no column, no funnel,
      // no fork, and a landmark you meet once — so an empty tutorial would read
      // as an unfinished one rather than as a gentle one. The blooms are the
      // single accent nothing else on the mountain owns, which is what makes the
      // first thirty metres somewhere you have been rather than a ramp.
      const yaw = randRange(rng, 0, Math.PI * 2);
      const rock = randRange(rng, 0.75, 1.15);
      const bush = randRange(rng, 0.9, 1.3);
      const bloom = randRange(rng, 0.45, 0.7);
      const side = rng() < 0.5 ? 1 : -1;
      const flowered = rng() < 0.6;
      out.push({
        kind: 'box',
        center: [x, y + rock, z],
        half: [rock * 1.3, rock, rock * 1.1],
        quat: quatYaw(yaw),
        material: MAT_MEADOW_STONE,
      });
      out.push({
        kind: 'sphere',
        center: [x + tx * 1.5 * side, y + bush * 0.55, z + tz * 1.5 * side],
        radius: bush,
        material: MAT_MEADOW_CANOPY,
      });
      if (flowered) {
        out.push({
          kind: 'sphere',
          center: [
            x - tx * 1.3 * side + nx * 0.5,
            y + bloom * 0.5,
            z - tz * 1.3 * side + nz * 0.5,
          ],
          radius: bloom,
          material: MAT_MEADOW_BLOOM,
        });
      }
      break;
    }
    case 1: {
      // Verdant: a tree — one solid trunk under two or three canopy masses,
      // offset from each other so the crown has a silhouette instead of being a
      // ball. The third mass sits higher and to the other side: a crown that
      // leans is a tree, a crown that is symmetrical is a topiary.
      const h = randRange(rng, 3, 5);
      const r0 = randRange(rng, 1.7, 2.0);
      const r1 = randRange(rng, 1.4, 1.8);
      const r2 = randRange(rng, 1.4, 1.7);
      const lean = randRange(rng, 0, Math.PI * 2);
      const full = rng() < 0.55;
      const lx = Math.cos(lean);
      const lz = Math.sin(lean);
      out.push({
        kind: 'cylinder',
        a: [x, y, z],
        b: [x, y + h, z],
        radius: 0.55,
        material: MAT_VERDANT_TRUNK,
      });
      out.push({
        kind: 'sphere',
        center: [x, y + h + r0 * 0.45, z],
        radius: r0,
        material: MAT_VERDANT_CANOPY,
      });
      out.push({
        kind: 'sphere',
        center: [x + lx * 0.9, y + h + r1 * 0.1, z + lz * 0.9],
        radius: r1,
        material: MAT_VERDANT_CANOPY,
      });
      if (full) {
        out.push({
          kind: 'sphere',
          center: [x - lx * 0.8, y + h + r2 * 0.85, z - lz * 0.8],
          radius: r2,
          material: MAT_VERDANT_CANOPY,
        });
      }
      break;
    }
    case 2: {
      // Ruins: a snapped column, the drum that came off it lying beside it, and
      // often a second stub — one broken column is an accident, two is a ruin.
      const h = randRange(rng, 1.6, 3.6);
      const yaw = randRange(rng, 0, Math.PI * 2);
      const stub = randRange(rng, 0.6, 1.6);
      const side = rng() < 0.5 ? 1 : -1;
      const pair = rng() < 0.6;
      out.push({
        kind: 'cylinder',
        a: [x, y, z],
        b: [x, y + h, z],
        radius: 0.65,
        material: MAT_RUINS_COLUMN,
      });
      out.push({
        kind: 'box',
        center: [x + nx * 1.7, y + 0.2, z + nz * 1.7],
        half: [1.2, 0.2, 0.8],
        quat: quatYaw(yaw),
        material: MAT_RUINS_SLAB,
      });
      if (pair) {
        out.push({
          kind: 'cylinder',
          a: [x + tx * 1.5 * side, y, z + tz * 1.5 * side],
          b: [x + tx * 1.5 * side, y + stub, z + tz * 1.5 * side],
          radius: 0.5,
          material: MAT_RUINS_COLUMN,
        });
      }
      break;
    }
    case 3: {
      // Crystal: two or three shards out of one seam, leaning apart, violet and
      // cyan alternating. Drawn for all three and planted for as many as the
      // count asked for — the discarded roll is the price of the stream staying
      // where it is whichever way the count came out.
      const three = rng() < 0.5;
      const hs = [0, 0, 0];
      const yaws = [0, 0, 0];
      const tilts = [0, 0, 0];
      for (let k = 0; k < 3; k++) {
        hs[k] = randRange(rng, 1.6, 2.9);
        yaws[k] = randRange(rng, 0, Math.PI * 2);
        tilts[k] = randRange(rng, 10, 22) * DEG;
      }
      const offT = [-0.9, 0.9, 0];
      const offN = [0, 0, 1.1];
      for (let k = 0; k < (three ? 3 : 2); k++) {
        const h = hs[k] as number;
        const tilt = tilts[k] as number;
        const ot = offT[k] as number;
        const on = offN[k] as number;
        out.push({
          kind: 'box',
          center: [
            x + tx * ot + nx * on,
            y + h * Math.cos(tilt),
            z + tz * ot + nz * on,
          ],
          half: [0.45, h, 0.45],
          quat: quatMul(quatYaw(yaws[k] as number), quatTiltX(tilt)),
          material: k % 2 === 0 ? MAT_CRYSTAL_SHARD_A : MAT_CRYSTAL_SHARD_B,
        });
      }
      break;
    }
    case 4: {
      // Frozen: an ice spike out of a half-sunk snow mound, with a smaller spike
      // leaning out of the same drift the other way — a wind did this, and wind
      // does not make one of anything.
      const h = randRange(rng, 2.0, 3.3);
      const yaw = randRange(rng, 0, Math.PI * 2);
      const small = randRange(rng, 1.0, 1.8);
      const yaw2 = randRange(rng, 0, Math.PI * 2);
      const pair = rng() < 0.6;
      const tilt = 12 * DEG;
      out.push({
        kind: 'box',
        center: [x, y + h * Math.cos(tilt), z],
        half: [0.5, h, 0.5],
        quat: quatMul(quatYaw(yaw), quatTiltX(tilt)),
        material: MAT_FROZEN_SPIKE,
      });
      out.push({
        kind: 'sphere',
        center: [x + tx * 1.6, y + 0.2, z + tz * 1.6],
        radius: 1.2,
        material: MAT_FROZEN_SNOW,
      });
      if (pair) {
        out.push({
          kind: 'box',
          center: [
            x + tx * 1.7 + nx * 0.4,
            y + 0.3 + small * Math.cos(tilt),
            z + tz * 1.7 + nz * 0.4,
          ],
          half: [0.35, small, 0.35],
          quat: quatMul(quatYaw(yaw2), quatTiltX(-tilt)),
          material: MAT_FROZEN_SPIKE,
        });
      }
      break;
    }
    default: {
      // Obsidian: a basalt column, gold-capped about half the time, standing on
      // a broken-off block of the same stone.
      const h = randRange(rng, 1.1, 2.6);
      const yaw = randRange(rng, 0, Math.PI * 2);
      const capped = rng() < 0.6;
      const block = randRange(rng, 0.5, 1.3);
      const side = rng() < 0.5 ? 1 : -1;
      out.push({
        kind: 'box',
        center: [x, y + h, z],
        half: [0.65, h, 0.65],
        quat: quatYaw(yaw),
        material: MAT_OBSIDIAN_BASALT,
      });
      out.push({
        kind: 'box',
        center: [x + tx * 1.5 * side, y + block, z + tz * 1.5 * side],
        half: [0.5, block, 0.5],
        quat: quatYaw(yaw * 0.5),
        material: MAT_OBSIDIAN_BASALT,
      });
      if (capped) {
        out.push({
          kind: 'box',
          center: [x, y + h * 2 + 0.15, z],
          half: [0.45, 0.15, 0.45],
          quat: quatYaw(yaw),
          material: MAT_OBSIDIAN_GOLD,
        });
      }
      break;
    }
  }
}

/**
 * Would a plank whose top face is at `top` grow through anyone's landing?
 *
 * A plank clashes with a landing when it is close enough in plan to be over it
 * *and* its own slab overlaps that landing's slice of height — which is exactly
 * the near ends of every span, before the deck has climbed clear of the pad it
 * started from and after it has dropped under the one it is heading for. It is
 * asked of every node on the course, not just the bridge's own: a lane that
 * passes a trunk pad at deck height would otherwise nail a plank through it.
 *
 * What a landing *is* comes from {@link nodeFootprint} rather than from the zone's
 * pad table, which is the same correction {@link postClashes} already makes and
 * for the same reason: the pad table describes a platform, and a node can also
 * be a column hanging metres below its own cap or a wide low island slab reaching
 * five metres out. Re-deriving the box from `padHalf` quietly waved both of those
 * through — and a plank driven through the corner of an island is a §9 failure
 * that costs a seed for a reason nobody could see in the geometry.
 *
 * Conservative on both sides: the reaches are the untrimmed ones
 * {@link padScales} started from, so this never misses a real crossing and only
 * ever throws away a plank or two more than it had to.
 */
function plankClashes(course: Course, junctions: Set<number>, top: V3): boolean {
  const nodes = course.nodes;
  const reach = Math.hypot(PLANK_HALF[0], PLANK_HALF[2]);
  const lo = top[1] - 2 * PLANK_HALF[1];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as Node;
    if (Math.abs(node.y - top[1]) > 8) continue;
    const foot = nodeFootprint(node, junctions.has(i), course.islands.has(i));
    // The two kinds that put nothing on the node line are covered by the box
    // that holds a mushroom cap and a checkpoint disc alike.
    const pReach = foot === null ? Math.max(MUSHROOM_RADIUS, CHECKPOINT_RADIUS) : foot.reach;
    const pLo = foot === null ? node.y - MUSHROOM_SINK - MUSHROOM_RADIUS : foot.lo;
    const pHi = foot === null ? node.y + MUSHROOM_RADIUS - MUSHROOM_SINK : foot.hi;
    if (Math.hypot(node.x - top[0], node.z - top[2]) >= pReach + reach) continue;
    if (Math.min(pHi, top[1]) - Math.max(pLo, lo) > 0) return true;
  }
  return false;
}

/** One upright post, as the clearance test below has to see it. */
interface Post {
  x: number;
  z: number;
  loY: number;
  hiY: number;
  r: number;
}

/**
 * Would a free-standing post grow through anyone's landing, or through a post
 * already standing?
 *
 * The colonnade's twin of {@link plankClashes}, and it exists for the same
 * reason: a post is planted three metres to the side of the node it belongs to,
 * and three metres is less than a hop, so the node *after* that one is regularly
 * right where the post wants to stand — and where the radius weaves, so is the
 * post that node was given. The old thin poles did both of these constantly.
 * Nothing caught it, because nothing they grew through was tagged as a landing
 * on both sides of the comparison; a pillar is a column you land on now, and
 * `level.selftest.ts` §9 checks it against every other landing, so the grove has
 * to be honest about where it puts things.
 *
 * The node half is asked of every node on the course rather than of the lane's
 * own, exactly like the planks: a post beside a branch that passes a trunk pad
 * at the same height would otherwise be driven straight through it.
 * Conservative on both sides — the reaches are the untrimmed ones
 * {@link padScales} started from, and the two kinds with no pad on the node line
 * at all are covered by the box that holds a mushroom and a checkpoint disc
 * alike.
 */
function postClashes(
  course: Course,
  junctions: Set<number>,
  standing: readonly Post[],
  post: Post,
): boolean {
  const nodes = course.nodes;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as Node;
    if (node.y < post.loY - 4 || node.y > post.hiY + 4) continue;
    const foot = nodeFootprint(node, junctions.has(i), course.islands.has(i));
    const reach = foot === null ? Math.max(MUSHROOM_RADIUS, CHECKPOINT_RADIUS) : foot.reach;
    const lo = foot === null ? node.y - MUSHROOM_SINK - MUSHROOM_RADIUS : foot.lo;
    const hi = foot === null ? node.y + MUSHROOM_RADIUS - MUSHROOM_SINK : foot.hi;
    if (Math.min(hi, post.hiY) - Math.max(lo, post.loY) <= 0) continue;
    if (Math.hypot(node.x - post.x, node.z - post.z) >= reach + post.r + PAD_KEEP) continue;
    return true;
  }
  for (const other of standing) {
    if (Math.min(other.hiY, post.hiY) - Math.max(other.loY, post.loY) <= 0) continue;
    if (Math.hypot(other.x - post.x, other.z - post.z) >= other.r + post.r + PAD_KEEP) continue;
    return true;
  }
  return false;
}

/**
 * The furniture a whole lane grows, as opposed to what sits on one node.
 *
 * Three of the six motifs are only half a shape without it: a ledge wall wants
 * something behind it or it is a row of ledges in mid-air, a pillar forest wants
 * columns it does *not* land on standing between the ones it does, and a bridge
 * is not a bridge until the spans between its posts are decked.
 *
 * **Draws nothing.** Counts and placements are read off the lane's own geometry
 * — how many nodes it has, and where they are — so decoration cannot shift
 * anything downstream in the RNG stream, exactly like the lobby floors. Every
 * piece is pushed outward or sideways from nodes that already cleared the shaft
 * and `R_HARD_MAX`, and clamped again on the way out through {@link outwardRoom}.
 *
 * `hazards` is whatever {@link planMovers} decided should swing over this lane —
 * already sized, posed and carrying its own spec. It arrives finished rather
 * than being rolled here for the same reason nothing else here is: this function
 * must stay incapable of moving the stream.
 */
function decorateLane(
  out: Primitive[],
  course: Course,
  lane: Lane,
  junctions: Set<number>,
  hazards: readonly Primitive[],
): void {
  const nodes = course.nodes;
  const body = lane.body;
  for (const prim of hazards) out.push(prim);
  if (body.length === 0) return;

  switch (lane.motif) {
    case 'ledgeWall': {
      // Backdrop slabs, evenly spread down the lane: three where it is short,
      // five where it is long, and never more than it has nodes to hang them on.
      const count = Math.min(
        body.length,
        clamp(Math.round(body.length / 2), LEDGE_WALL_BACKDROP_MIN, LEDGE_WALL_BACKDROP_MAX),
      );
      for (let k = 0; k < count; k++) {
        const pick = count > 1 ? Math.round((k * (body.length - 1)) / (count - 1)) : 0;
        const node = nodes[body[pick] as number] as Node;
        const reach = Math.hypot(LEDGE_WALL_BACKDROP_HALF[0], LEDGE_WALL_BACKDROP_HALF[2]);
        const d = outwardRoom(node, LEDGE_WALL_BACKDROP_OUT, reach);
        out.push({
          kind: 'box',
          center: [
            node.x + Math.cos(node.theta) * d,
            node.y + LEDGE_WALL_BACKDROP_HALF[1] - 1,
            node.z + Math.sin(node.theta) * d,
          ],
          half: [
            LEDGE_WALL_BACKDROP_HALF[0],
            LEDGE_WALL_BACKDROP_HALF[1],
            LEDGE_WALL_BACKDROP_HALF[2],
          ],
          quat: quatYaw(node.yaw),
          material: zoneDecorMaterial(node.zone),
        });
      }
      break;
    }
    case 'pillarForest': {
      // Columns you do *not* land on, alternating either side of the line, so
      // the lane runs through the colonnade rather than along the edge of it.
      // Sideways only: a tangential step never brings a post closer to the shaft.
      //
      // Three or four of them, no two the same, and the last one snapped off at
      // knee height. What was here before was six poles of one radius and one
      // height, which the eye reads as a fence and then stops reading — the lane
      // it was supposed to dress went back to looking like bare pads with sticks
      // beside them. Height and thickness come off each post's own node (a
      // cheap wobble on its angle and height) because {@link decorateLane} draws
      // nothing at all, on purpose: dressing a lane must never be able to move
      // the stream for everything built after it.
      //
      // The posts hang off `body[1 …]` rather than the whole body: `body[0]` is
      // the one node of this motif that is a plain wide pad, and three metres is
      // not enough room beside one. Every other node here is a column of its own
      // and leaves the post most of a metre of air. Where even that is not true
      // — the radius weaves, and a weave brings the *next* node, or the post
      // that node was already given, round into the space — the post tries the
      // other side of the line and then gives up, the same bargain
      // {@link plankClashes} strikes for a bridge deck.
      const count = clamp(body.length - 1, PILLAR_GROVE_MIN, PILLAR_GROVE_MAX);
      const span = body.length - 2;
      const standing: Post[] = [];
      for (let k = 0; k < count; k++) {
        const pick = 1 + (count > 1 && span > 0 ? Math.round((k * span) / (count - 1)) : 0);
        const node = nodes[body[Math.min(pick, body.length - 1)] as number] as Node;
        const base = node.y - 1.2;
        const wobble = 0.5 - 0.5 * Math.cos(node.theta * 5 + node.y * 0.6);
        // The snapped one is the last, so a grove always ends on the broken note
        // rather than opening on it.
        const height =
          k === count - 1
            ? PILLAR_GROVE_SNAPPED_H
            : lerp(PILLAR_GROVE_H_MIN, PILLAR_GROVE_H_MAX, wobble);
        const radius = lerp(PILLAR_GROVE_R_MIN, PILLAR_GROVE_R_MAX, 1 - wobble);
        const side = k % 2 === 0 ? 1 : -1;
        for (const s of [side, -side]) {
          const post: Post = {
            x: node.x - Math.sin(node.theta) * PILLAR_GROVE_OFF * s,
            z: node.z + Math.cos(node.theta) * PILLAR_GROVE_OFF * s,
            loY: base,
            hiY: base + height,
            r: radius,
          };
          if (postClashes(course, junctions, standing, post)) continue;
          standing.push(post);
          out.push({
            kind: 'cylinder',
            a: [post.x, post.loY, post.z],
            b: [post.x, post.hiY, post.z],
            radius,
            material: zoneOf(node).mats.pillar,
          });
          break;
        }
      }
      break;
    }
    case 'bridgeRun': {
      // A deck across every span between landings: planks a plank-length apart,
      // top faces on the straight line joining the two node lines, so the run
      // reads as a ramp you can walk or bounce up rather than a row of steps
      // floating beside one.
      //
      // Which is also why the ends of a span go undecked. A landing pad is
      // nearly as long as a hop on this mountain, so the first plank off a node
      // would be *inside* that node's pad — the deck only clears the pads once
      // it has climbed past them, and {@link plankClashes} is what decides where
      // that is. Short spans and shallow ones therefore get fewer planks, or
      // none; a span with two is already unmistakably a bridge.
      for (let k = 1; k < body.length; k++) {
        const a = nodes[body[k - 1] as number] as Node;
        const b = nodes[body[k] as number] as Node;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dz = b.z - a.z;
        const flat = Math.hypot(dx, dz);
        if (flat < PLANK_STEP) continue;
        const zone = zoneOf(a);
        const yaw = yawAlong(dx, dz);
        // Laid at exactly one plank-length apart and centred on the span, so
        // consecutive planks meet face to face rather than growing through each
        // other — which `level.selftest.ts` §9 would rightly object to.
        const planks = Math.max(1, Math.floor(flat / PLANK_STEP));
        const run = planks * PLANK_STEP;
        let first = -1;
        let last = -1;
        for (let p = 0; p < planks; p++) {
          const t = (flat - run + PLANK_STEP * (2 * p + 1)) / (2 * flat);
          const c: V3 = [a.x + dx * t, a.y + dy * t, a.z + dz * t];
          if (plankClashes(course, junctions, c)) continue;
          if (first < 0) first = p;
          last = p;
          out.push({
            kind: 'box',
            center: [c[0], c[1] - PLANK_HALF[1], c[2]],
            half: [PLANK_HALF[0], PLANK_HALF[1], PLANK_HALF[2]],
            quat: quatYaw(yaw),
            material: zone.mats.platform,
          });
        }
        // A little post at each end of whatever actually got decked, so the
        // deck has visible ends instead of stopping in mid-air.
        if (first < 0) continue;
        for (const p of first === last ? [first] : [first, last]) {
          const t = (flat - run + PLANK_STEP * (2 * p + 1)) / (2 * flat);
          out.push({
            kind: 'box',
            center: [
              a.x + dx * t,
              a.y + dy * t + BRIDGE_POST_HALF[1] + PLANK_POST_LIFT,
              a.z + dz * t,
            ],
            half: [BRIDGE_POST_HALF[0], BRIDGE_POST_HALF[1], BRIDGE_POST_HALF[2]],
            quat: quatYaw(yaw),
            material: zoneDecorMaterial(a.zone),
          });
        }
      }
      break;
    }
    default:
      break;
  }
}

function emitPlaza(out: Primitive[]): void {
  out.push({
    kind: 'cylinder',
    a: [0, -1, 0],
    b: [0, 0, 0],
    radius: PLAZA_RADIUS,
    material: MAT_PLAZA,
  });
  const seg = (Math.PI * 2) / RAIL_SEGMENTS;
  const halfLen = RAIL_RADIUS * Math.tan(seg * 0.5);
  for (let i = 0; i < RAIL_SEGMENTS; i++) {
    const a = seg * i;
    out.push({
      kind: 'box',
      center: [RAIL_RADIUS * Math.cos(a), RAIL_HEIGHT * 0.5, RAIL_RADIUS * Math.sin(a)],
      half: [halfLen, RAIL_HEIGHT * 0.5, 0.2],
      quat: quatYaw(tangentYaw(a)),
      material: MAT_RAIL,
    });
  }
}

function emitSummit(out: Primitive[]): void {
  out.push({
    kind: 'cylinder',
    a: [0, SUMMIT_Y - 1.6, 0],
    b: [0, SUMMIT_Y, 0],
    radius: SUMMIT_RADIUS,
    material: MAT_SUMMIT,
  });
  // A small monument, so arriving looks like arriving somewhere.
  out.push({
    kind: 'box',
    center: [0, SUMMIT_Y + 1.9, 0],
    half: [0.7, 1.9, 0.7],
    quat: identityQuat(),
    material: MAT_SUMMIT,
  });
  const ring = 6;
  for (let i = 0; i < ring; i++) {
    const a = ((Math.PI * 2) / ring) * i;
    const h = 0.5 + 0.18 * i;
    out.push({
      kind: 'box',
      center: [5.5 * Math.cos(a), SUMMIT_Y + h, 5.5 * Math.sin(a)],
      half: [0.75, h, 0.75],
      quat: quatYaw(-a),
      material: MAT_SUMMIT,
    });
  }
}

// ---------------------------------------------------------------------------
// Landmarks — one set piece per zone
// ---------------------------------------------------------------------------
// The mountain had no answer to "how far up am I". A wandering polar walk looks
// the same from most of itself, the zone palettes only tell you which sixth of
// the climb you are on, and a checkpoint is a thing you find rather than a thing
// you see coming. So each zone gets one object bigger than anything the climb is
// built out of, standing clear of the route by a measured margin and tall enough
// to be a heading from the zone below and a receipt from the zone above.
//
// *Clear of the route*, and not "in airspace the climb may not enter" — there is
// no such band. `LANDMARK_R_MAX` buys decor six metres of rim that landings do
// not get, which is what lets a landmark be big without overlapping a pad on the
// limit; it is a ceiling, not a floor, and a landmark stands wherever its zone's
// own rim puts it, which on the Obsidian spire is r ≈ 21 and squarely inside the
// radius the Frozen Shelf below it climbs at. What is actually promised is what
// {@link emitLandmarks} measures and nothing more: at every angle it tries, the
// distance from the landmark to every landing whose height it shares, minus both
// their reaches, against `LANDMARK_KEEP` — and on a seed where no angle clears,
// the least bad one, honestly.
//
// They are decoration in the strictest sense the file has: nothing is solved
// against them, they carry no motion, they may not be part of any hop, and they
// **draw nothing** — every dimension below is a constant and every placement is
// pure geometry over nodes that already exist, exactly like the lobby floors and
// the lane furniture. Adding, resizing or deleting a landmark cannot move a
// single node. They are collidable, because everything in this file is;
// bouncing off the Verdant tree is legal and gets you nowhere.

/** Where a landmark stands and which way it faces — one frame, six builders. */
interface LandmarkFrame {
  x: number;
  y: number;
  z: number;
  /** Outward normal (away from the shaft) and the tangent at this angle. */
  nx: number;
  nz: number;
  tx: number;
  tz: number;
  /** The angle itself, for the pieces that want to be square to the mountain. */
  theta: number;
}

interface LandmarkSpec {
  /**
   * Furthest any piece reaches from the frame's centre in plan. This is what
   * gets paid for out of `LANDMARK_R_MAX`, and what the clearance search below
   * measures the course against, so it has to be an over-estimate and never an
   * under-one.
   */
  reach: number;
  /** How far below and above the anchor's node line the piece fills. */
  drop: number;
  rise: number;
  build: (out: Primitive[], at: LandmarkFrame) => void;
}

/**
 * The six, in zone order. Each is at most eight primitives — a landmark is
 * supposed to be one silhouette, and a silhouette assembled out of twenty boxes
 * costs the draw budget more than it gives the eye.
 */
const LANDMARKS: readonly LandmarkSpec[] = [
  // Meadow: a dolmen on a grass mound. Three stones standing on a small ring and
  // one laid across them, slipped off true — the smallest landmark on the
  // mountain, and deliberately, because it is the only one you meet *before* you
  // know what a landmark is here for. Somebody stood these up: that is the whole
  // sentence, and it is the first thing the climb says.
  {
    reach: 4.2,
    drop: 1.8,
    rise: 6.5,
    build: (out, at): void => {
      // The mound it stands on, so the dolmen reads as put there rather than
      // dropped there.
      out.push({
        kind: 'box',
        center: [at.x, at.y - 0.6, at.z],
        half: [3.2, 0.8, 2.6],
        quat: quatYaw(-at.theta),
        material: MAT_MEADOW_CANOPY,
      });
      for (let k = 0; k < 3; k++) {
        const a = k * 120 * DEG;
        const ux = at.tx * Math.cos(a) + at.nx * Math.sin(a);
        const uz = at.tz * Math.cos(a) + at.nz * Math.sin(a);
        out.push({
          kind: 'box',
          center: [at.x + ux * 1.9, at.y + 2.6, at.z + uz * 1.9],
          half: [0.6, 2.4, 0.5],
          quat: quatYaw(-at.theta + a),
          material: MAT_MEADOW_STONE,
        });
      }
      // The capstone, tipped a few degrees off level: three uprights and a flat
      // lid is a table, and a lid that has slipped is a thing with a history.
      out.push({
        kind: 'box',
        center: [at.x, at.y + 5.4, at.z],
        half: [2.6, 0.45, 2.0],
        quat: quatMul(quatYaw(-at.theta), quatTiltX(9 * DEG)),
        material: MAT_MEADOW_STONE,
      });
    },
  },
  // Verdant: a great tree. The same shape as the zone's own prop and four times
  // the size, which is the point — you learn what a tree looks like on the way
  // past a dozen small ones and then meet the one you can see from the plaza.
  {
    reach: 5.8,
    drop: 2,
    rise: 14.2,
    build: (out, at): void => {
      out.push({
        kind: 'cylinder',
        a: [at.x, at.y - 2, at.z],
        b: [at.x, at.y + 7, at.z],
        radius: 1.6,
        material: MAT_VERDANT_TRUNK,
      });
      const canopy: Array<[number, number, number, number]> = [
        [0, 0, 9.5, 4.2],
        [at.tx * 2.4, at.tz * 2.4, 7.6, 3.4],
        [-at.tx * 2.2, -at.tz * 2.2, 8.4, 3.2],
        [at.nx * 2.0, at.nz * 2.0, 11.2, 3.0],
      ];
      for (const [dx, dz, dy, r] of canopy) {
        out.push({
          kind: 'sphere',
          center: [at.x + dx, at.y + dy, at.z + dz],
          radius: r,
          material: MAT_VERDANT_CANOPY,
        });
      }
    },
  },
  // Ruins: a gate that no longer opens onto anything. Two columns still standing
  // and the lintel slipped off one of them — the zone's whole idea (this was
  // built, and then it stopped being looked after) at a size you cannot miss.
  {
    reach: 4.4,
    drop: 1,
    rise: 7.2,
    build: (out, at): void => {
      for (const side of [1, -1]) {
        out.push({
          kind: 'cylinder',
          a: [at.x + at.tx * 2.6 * side, at.y - 1, at.z + at.tz * 2.6 * side],
          b: [at.x + at.tx * 2.6 * side, at.y + 7, at.z + at.tz * 2.6 * side],
          radius: 1.2,
          material: MAT_RUINS_COLUMN,
        });
      }
      // Long axis on local +Z so that `quatTiltX` tips the *length* of the beam
      // rather than rolling it: one end is still up on a column, the other has
      // come down. `quatYaw(−theta)` is the yaw that puts local +Z on the
      // tangent, which is the line the two columns stand on.
      out.push({
        kind: 'box',
        center: [at.x, at.y + 5.6, at.z],
        half: [0.9, 0.4, 3.6],
        quat: quatMul(quatYaw(-at.theta), quatTiltX(18 * DEG)),
        material: MAT_RUINS_SLAB,
      });
    },
  },
  // Crystal: a geode broken open. Six shards of both hues out of one seam, the
  // tall ones twice the height of anything the zone's props grow.
  {
    reach: 4.8,
    drop: 1,
    rise: 6,
    build: (out, at): void => {
      for (let k = 0; k < 6; k++) {
        const a = k * 60 * DEG;
        const d = 1.4 + (k % 3) * 0.5;
        const h = 1.5 + (k % 3) * 1.0;
        const tilt = (14 + (k % 2) * 6) * DEG;
        const ux = at.tx * Math.cos(a) + at.nx * Math.sin(a);
        const uz = at.tz * Math.cos(a) + at.nz * Math.sin(a);
        out.push({
          kind: 'box',
          center: [at.x + ux * d, at.y - 1 + h * Math.cos(tilt), at.z + uz * d],
          half: [0.8, h, 0.8],
          quat: quatMul(quatYaw(-at.theta + a), quatTiltX(tilt)),
          material: k % 2 === 0 ? MAT_CRYSTAL_SHARD_A : MAT_CRYSTAL_SHARD_B,
        });
      }
    },
  },
  // Frozen: a cascade caught mid-fall. Five slabs stepping down and outward,
  // each wider than the one above, so it reads as ice pouring off the cornice.
  {
    reach: 4.4,
    drop: 5.1,
    rise: 5.5,
    build: (out, at): void => {
      for (let k = 0; k < 5; k++) {
        const wide = 1.2 + k * 0.2;
        const deep = 0.8 + k * 0.12;
        const outward = k * 0.45;
        const sway = k % 2 === 0 ? 0.6 : -0.6;
        out.push({
          kind: 'box',
          center: [
            at.x + at.nx * outward + at.tx * sway,
            at.y + 5 - k * 2.4,
            at.z + at.nz * outward + at.tz * sway,
          ],
          half: [wide, 0.5, deep],
          quat: quatYaw(-at.theta + k * 7 * DEG),
          material: k % 2 === 0 ? MAT_FROZEN_SPIKE : MAT_FROZEN_SNOW,
        });
      }
    },
  },
  // Obsidian: a monolith with the gold seams the summit disc is made of running
  // through it. The only thing on the spire wider than a landing, and the last
  // thing you pass on the way to the top.
  {
    reach: 2.1,
    drop: 1.5,
    rise: 10.2,
    build: (out, at): void => {
      const yaw = quatYaw(-at.theta);
      out.push({
        kind: 'box',
        center: [at.x, at.y + 4.0, at.z],
        half: [1.5, 5.5, 1.1],
        quat: yaw,
        material: MAT_OBSIDIAN_BASALT,
      });
      for (const dy of [1.2, 5.4]) {
        out.push({
          kind: 'box',
          center: [at.x, at.y + dy, at.z],
          half: [1.55, 0.16, 1.15],
          quat: yaw,
          material: MAT_OBSIDIAN_GOLD,
        });
      }
      out.push({
        kind: 'box',
        center: [at.x, at.y + 9.85, at.z],
        half: [1.2, 0.35, 0.9],
        quat: yaw,
        material: MAT_OBSIDIAN_GOLD,
      });
    },
  },
];

/**
 * The widest a node's landing can possibly be, as a plan radius. Deliberately
 * the worst case — a junction-widened pad, or the slab an island stands on —
 * because the landmark search below is measuring how much room it has *not* to
 * be in the way, and being wrong in the generous direction only moves a landmark
 * further out.
 */
function landingReach(course: Course, at: number): number {
  const node = course.nodes[at] as Node;
  const half = zoneOf(node).padHalf;
  const pad = Math.hypot(half[0], half[2]) * JUNCTION_PAD_SCALE;
  return course.islands.has(at) ? Math.max(pad, ISLAND_HALF * Math.SQRT2) : pad;
}

/**
 * Plant one landmark per zone.
 *
 * **Anchor.** The trunk node nearest the zone's mid-height. Mid-height and
 * nothing else, because a landmark is the answer to "how far up am I" and one
 * hung level with the summit disc answers a question nobody was asking — see
 * {@link LANDMARK_WINDOW} for what this replaced and why it had to go.
 *
 * **Radius.** The zone's **built** rim — the furthest out any of its nodes
 * actually went — plus {@link LANDMARK_OUT}, cut back so the landmark's far edge
 * stays inside `LANDMARK_R_MAX`. Off the *declared* rim, which is what it was,
 * a zone that did not use the outer half of its band got a landmark stranded in
 * open air several zones' worth of nothing away from anything you could stand
 * on. A landmark is meant to be part of the mountain, and part of the mountain
 * means "beside the highest thing here", not "beside the highest thing the table
 * said was allowed here".
 *
 * **Angle.** Straight out from the anchor if that is clear, and otherwise swung
 * a few degrees round the shaft, nearest-first, until the landmark's own disc
 * clears every landing whose height it shares. Pure geometry and no rolls — the
 * same idiom the trunk walk uses to refuse to stand on itself. If nothing
 * clears, the least bad angle is taken: a tree brushing the corner of a pad is
 * a worse mountain than one without a tree, but only slightly, and refusing to
 * build the landmark at all would be much worse than either. Which is the honest
 * shape of the promise: a landmark is *outside the route by a measured margin*,
 * and on a bad seed by the largest margin available. It is not in a reserved band
 * of airspace, and nothing anywhere is solved against one.
 */
function emitLandmarks(out: Primitive[], course: Course): void {
  const nodes = course.nodes;
  const trunk = course.lanes.find((lane) => lane.role === 'trunk');
  if (trunk === undefined) return;
  const path = lanePath(course, trunk);
  if (path.length === 0) return;

  for (let z = 0; z < ZONES.length; z++) {
    const spec = LANDMARKS[z] as LandmarkSpec;
    const lo = z === 0 ? FIRST_NODE_Y : (ZONE_BOUNDARY_YS[z - 1] as number);
    const hi = z === ZONES.length - 1 ? LAST_NODE_Y : (ZONE_BOUNDARY_YS[z] as number);
    const mid = (lo + hi) * 0.5;

    // The anchor: the trunk node nearest the middle of the zone.
    let anchor = -1;
    let bestDy = Infinity;
    for (const at of path) {
      const dy = Math.abs((nodes[at] as Node).y - mid);
      if (dy < bestDy) {
        bestDy = dy;
        anchor = at;
      }
    }
    if (anchor < 0) continue;
    const node = nodes[anchor] as Node;

    // The rim actually built *at this height* — over every lane, because a
    // branch out on a spur is as much of the mountain as the trunk is and a
    // landmark that ignored one would be standing in the middle of a fork. At
    // this height rather than over the whole zone, because a zone that reaches
    // r = 28 once and walks the rest of itself at 14 has two rims and only one
    // of them is anywhere near the landmark: measured over the zone, the tree
    // ends up level with nothing, six metres outside a radius the climb visits
    // forty metres lower down. The anchor is always in this window, so it is
    // never empty.
    let builtMax = 0;
    for (const other of nodes) {
      if (Math.abs(other.y - node.y) > LANDMARK_WINDOW) continue;
      if (other.radius > builtMax) builtMax = other.radius;
    }
    const r = Math.min(builtMax + LANDMARK_OUT, LANDMARK_R_MAX - spec.reach);
    // Which landings could possibly be in the way: the ones whose height the
    // landmark shares. Everything else on the mountain is irrelevant however
    // close it is in plan, which is most of it.
    const near: number[] = [];
    for (let i = 0; i < nodes.length; i++) {
      const other = nodes[i] as Node;
      if (other.y < node.y - spec.drop - 1 || other.y > node.y + spec.rise + 1) continue;
      near.push(i);
    }

    // A step of the search, in radians: `LANDMARK_STEP` metres of arc at the
    // radius the landmark is actually standing at.
    const swing = LANDMARK_STEP / Math.max(1, r);
    let bestTheta = node.theta;
    let bestGap = -Infinity;
    for (let s = 0; s <= LANDMARK_SWINGS && bestGap < 0; s++) {
      for (const sign of s === 0 ? [1] : [1, -1]) {
        const theta = node.theta + sign * s * swing;
        const cx = r * Math.cos(theta);
        const cz = r * Math.sin(theta);
        let gap = Infinity;
        for (const i of near) {
          const other = nodes[i] as Node;
          const d =
            Math.hypot(other.x - cx, other.z - cz) - spec.reach - landingReach(course, i);
          if (d < gap) gap = d;
        }
        gap -= LANDMARK_KEEP;
        if (gap > bestGap) {
          bestGap = gap;
          bestTheta = theta;
        }
        if (gap >= 0) break;
      }
    }

    const nx = Math.cos(bestTheta);
    const nz = Math.sin(bestTheta);
    spec.build(out, {
      x: r * nx,
      y: node.y,
      z: r * nz,
      nx,
      nz,
      tx: -nz,
      tz: nx,
      theta: bestTheta,
    });
  }
}

// ---------------------------------------------------------------------------
// Movers — the mountain that does not hold still
// ---------------------------------------------------------------------------
// A mover is a motion spec bolted to a primitive's rest pose and evaluated as a
// pure function of a shared clock (see `movers.ts`). Nothing about *motion* is
// decided in this file beyond what moves, how far and how fast — which is
// exactly the part that has to answer to the hop budget, and so exactly the part
// that belongs to the generator.
//
// The pass runs on its own RNG stream, forked off the level seed. The walk, the
// forks and the kinds are all settled before a single mover is considered and
// they stay settled: nothing here draws from the main stream, so retuning a
// mover table cannot move a landing pad by a millimetre.
//
// Two bargains are on offer, and which one a pad is offering is the whole
// difference. A **slider** never breaks the budget: its amplitude is solved so
// that even the worst phase of its travel leaves both of its hops inside
// `MOVER_GAP_BUDGET`, so it is simply a landing that happens to be moving when
// you get there. A **ferry** — and its vertical cousin the elevator, and the
// carousel that takes the whole shaft with it — is the opposite: you wait for
// it, you get on, and the hop it exists for is the one made at the far end of
// its travel. That is why every plan carries the answer rather than leaving it
// to be inferred: `level.selftest.ts` is allowed to measure the two against
// different inequalities, and it has to be told which.
//
// Hazards are the third thing. They are not landings and owe nobody a gap; they
// owe everybody clearance, checked at sampled poses against every surface the
// climb actually stands on.

const TAU = Math.PI * 2;

/**
 * The hop budget mover placements are solved against — the same three numbers
 * `level.selftest.ts` enforces as `MAX_GAP`, `MAX_RISE` and `MAX_DROP`.
 * Exported, and imported back by the test, so a ride is measured against one set
 * of numbers rather than two copies free to drift apart.
 */
export const MOVER_GAP_BUDGET = 7;
export const MOVER_RISE_BUDGET = 4.6;
export const MOVER_DROP_BUDGET = 8;
/**
 * Margin a slider holds inside the gap budget at its *worst* phase, and the same
 * margin a lift holds inside the rise budget at either end of its travel.
 * Exported: the placement is only as good as the inequality it was solved
 * against, and `level.selftest.ts` re-solves that inequality rather than
 * inventing a looser one.
 */
export const MOVER_SLIDE_MARGIN = 0.3;
/**
 * Slider travel. Under the floor the pad is twitching rather than moving and the
 * whole idea goes unread; over the ceiling it is no longer somewhere you can aim
 * at from two hops away, which is what a slider has to stay.
 */
const SLIDE_A_MIN = 0.9;
const SLIDE_A_MAX = 2.5;
/** Travels the slider solve walks between its two limits before giving up on a node. */
const SLIDE_A_STEPS = 9;
/** Slow enough to watch a full cycle before committing to the jump. */
const SLIDE_PERIOD_MIN = 5;
const SLIDE_PERIOD_MAX = 8;
/**
 * Ferry travel: far enough that one extreme parks by the node behind and the
 * other by the node ahead, which is what makes waiting for it the point rather
 * than an inconvenience.
 */
const FERRY_A_MIN = 3.0;
const FERRY_A_MAX = 4.5;
const FERRY_PERIOD_MIN = 7;
const FERRY_PERIOD_MAX = 10;
/**
 * Margin the *ridden* hops hold inside the budget, at either extreme of travel.
 * Wider than a slider's because a ferry is boarded rather than aimed at, and a
 * hop made from something still moving deserves the extra half metre.
 */
export const MOVER_FERRY_MARGIN = 0.5;
/** Amplitudes the ferry solve walks between its two limits before giving up on a node. */
const FERRY_A_STEPS = 13;
/**
 * Elevator travel. Two metres is already more rise than a bounce buys back, and
 * past three the wait at the bottom is longer than the climb it saves.
 */
const LIFT_A_MIN = 2.0;
const LIFT_A_MAX = 3.0;
/** Travels the lift solve walks between its two limits before giving up on a node. */
const LIFT_A_STEPS = 9;
const LIFT_PERIOD_MIN = 7;
const LIFT_PERIOD_MAX = 9;
/**
 * How far *below* the top of an elevator's travel the next landing may sit. A
 * lift that overshoots its own exit is a lift you step down off, which is fine
 * for a hand's width and a fall dressed up as a ride past that.
 */
export const MOVER_LIFT_STEP_DOWN = 2.0;
/**
 * The carousel takes twenty-eight seconds to come round. At r = 13 that is
 * 2.9 m/s — a pace you can stand on and watch the mountain turn, rather than one
 * that throws you off the moment you land.
 */
const CAROUSEL_PERIOD = 28;
/** Clearance a carousel pad holds from everything it orbits past. */
const CAROUSEL_KEEP = 0.4;
/**
 * How far inside its zone's own height a carousel opens. The set piece has to be
 * the middle of the Frozen Shelf, not the thing you meet stepping off a
 * checkpoint — and the band also keeps the sweep clear of both lobby floors.
 */
const CAROUSEL_ZONE_INSET = 8;
/**
 * Blink cycle. The duty is over a half deliberately: adjacent pads are staggered
 * half a cycle apart, so anything above 0.5 guarantees that at every instant at
 * least one of any neighbouring pair is solid and a route across the field
 * always exists. 0.62 leaves a fifth of the cycle where *both* are — long enough
 * to cross two pads on one read of the rhythm.
 */
const BLINK_PERIOD = 4.2;
const BLINK_DUTY = 0.62;
/**
 * How long a field runs. Two is the floor because one pad that comes and goes is
 * a trick and two is a rhythm; five is the ceiling because past that the field
 * stops being a thing the Hollow does to you and becomes the route itself. The
 * run is truncated by whatever the lane actually offers, so raising the ceiling
 * only lengthens the fields that had somewhere to go.
 */
const BLINK_RUN_MIN = 2;
const BLINK_RUN_MAX = 5;
/**
 * How far past the pad's own corner the bar reaches, and how much of that it
 * will give up to fit. Its reach may never fall below the pad's half-diagonal —
 * a bar that does not sweep its whole pad is a bar you can stand behind — but
 * the menace past the edge is negotiable, and negotiating it is the difference
 * between a zone with a sweeper in it and a zone without one.
 */
const SWEEPER_OVERHANGS: readonly number[] = [0.8, 0.6, 0.4, 0.2, 0];
/**
 * How thin the bar is, and how high it turns: where a body is, not where the
 * pogo tip is. A bar under the bounce would be a decoration and one over your
 * head is scenery.
 */
const SWEEPER_HALF_THICK = 0.12;
const SWEEPER_HALF_DEPTH = 0.28;
const SWEEPER_LIFT = 1.35;
/**
 * Seconds a sweeper takes to come round, by zone: patient low, vicious at the
 * crown. Zone 0's entry is the most patient of the lot and is never read — the
 * tutorial grows no bars — but the table is indexed by zone and a table with a
 * hole in it is a table that goes wrong the day the quota changes.
 */
const SWEEPER_PERIODS: readonly number[] = [8, 6, 6, 5, 5, 4];
/**
 * Zones that grow them. Meadow and Verdant teach — the bounce, and then the
 * climb — and neither also gets hit for it.
 */
const SWEEPER_ZONES: readonly number[] = [2, 4, 5];
/**
 * Bars one zone may grow, how far apart their pivots have to stand, and how much
 * of the summit approach is off limits to them.
 *
 * Two, because one bar in a forty-five metre zone is something that happened to
 * you once rather than something the zone does.
 *
 * Twelve metres, because that is twice the furthest a bar can reach from its own
 * pivot and the old ten was arithmetic done on the wrong pad. A bar's half-length
 * is the landing's own reach plus an overhang: the argument used to be "the
 * widest pad plus the longest of {@link SWEEPER_OVERHANGS} is a shade under four
 * metres", which is true of a *pad* — the roomiest zone that grows bars is the
 * Sunken Ruins at 3.08 m of half-diagonal — and false of the thing bars are
 * actually hung on. Islands sort to the front of the candidate list on purpose,
 * and an island's reach is `ISLAND_HALF · √2` = 4.95 m, so a real worst-case bar
 * is 5.75 m of arm and two of them ten metres apart overlap by a metre and a
 * half. Twelve is 2 × 5.75 with the rounding left over: no two arcs can ever
 * share air, which matters because two beams turning through each other is one
 * hazard drawn twice and reads as a fault rather than as a gauntlet. Measured
 * between pivots in 3D and not along a lane, which is what also keeps a pair
 * either side of a zone boundary honest.
 *
 * The {@link SUMMIT_APPROACH_NODES} at the top of the trunk are exempt from
 * carrying one at all, exactly as they are exempt from carrying a funnel. The
 * Obsidian Crown is the one zone whose bars would otherwise land there — it is
 * the last zone, the last checkpoint is forty metres below so nothing up here is
 * in a lobby's quarantine, and the top of the climb is therefore where a placer
 * looking for somewhere legal reliably ends up. It was stacking two bars and a
 * ferry into the final few metres, which is three beats in seven and not a
 * crescendo but a pile-up.
 */
const SWEEPERS_PER_ZONE = 2;
const SWEEPER_KEEP_APART = 12;
/**
 * A swinging log: the capsule *is* the arm as well as the log, hanging from a
 * pivot three and a half metres over the deck, so the thing you can see turning
 * is the thing you collide with.
 */
const LOG_PIVOT_LIFT = 3.5;
const LOG_TOP = 0.3;
const LOG_RADIUS = 0.3;
/** Just under a right angle either side: any wider and the log spends its swing overhead. */
const LOG_AMP_MIN = 0.85;
const LOG_AMP_MAX = 1.0;
const LOG_PERIOD_MIN = 3.0;
const LOG_PERIOD_MAX = 3.6;
/**
 * Air the bottom of the swing leaves over the deck — body height, not head
 * height, and not merely over the *planks*. A deck climbs, so the landing pads
 * at either end of a span stand half a rise proud of the middle of it, and a log
 * that only cleared the planks would be shouldering the pad it hangs between.
 * The drop is solved from this rather than named, so the two can never disagree.
 */
export const MOVER_LOG_DECK_CLEAR = 0.9;
const LOG_LENGTH = LOG_PIVOT_LIFT - MOVER_LOG_DECK_CLEAR - LOG_RADIUS;
/**
 * What a hazard owes every landing that is not the thing it is guarding. A
 * hazard is *meant* to crowd the route — that is the whole idea — so this is
 * only the promise that it never grows through a surface, with enough slack that
 * a sampled pose cannot skip past a real crossing. It is deliberately the same
 * number `level.selftest.ts` enforces: the planner is stricter than the test by
 * measuring more surfaces at more poses, not by wanting more air.
 */
const HAZARD_KEEP = 0.2;
/** Logs one deck may carry. Three is not a bridge with hazards on it, it is a wall. */
const LOG_MAX_PER_LANE = 2;
/**
 * Lowest zone a bridge deck grows logs: the teaching zones' bridges are bridges.
 * (Meadow never forks at all, so in practice this is about Verdant.)
 */
const LOG_MIN_ZONE = 2;
/**
 * Poses every clearance question is asked at, spread over one full cycle. Sixty
 * four is well past what the geometry needs — the pads are metres across and the
 * fastest thing here moves 3 m/s — and this is build time, not frame time.
 */
const MOVER_SAMPLES = 64;
/** Clear air a moving landing keeps from every other landing on the mountain. */
const MOVER_PAD_KEEP = 0.25;
/**
 * The tutorial reads static. Nothing may move on the first eighteen trunk nodes
 * — the whole of Meadow Foothills and the first hop out of it — and nothing that
 * moves may travel within this of one of them either: a pad sliding into the
 * airspace over node ten is still the tutorial moving.
 */
export const MOVER_TUTORIAL_NODES = 18;
const TUTORIAL_KEEP = 2.8;
/** Lane hops between two moving landings. A whole lane of them is a conveyor belt. */
const MOVER_SPACING = 3;
/**
 * The height band around a lobby floor nothing may move in. Asymmetric, and
 * deeper below than the ±4 m the pads themselves claim, because
 * {@link sealApproaches} throws every node within `LOBBY_CLEARANCE` of the slab's
 * underside out to the rim — and a relocated approach is a node whose position
 * was decided by the seal rather than by the walk. Covering the band covers them
 * without having to track which ones moved.
 */
const MOVER_LOBBY_BELOW = 5.5;
const MOVER_LOBBY_ABOVE = 4;
/**
 * Lane hops of quarantine either side of a checkpoint, and of a junction. Two,
 * because the last hops into a pad are the traverse round the lobby rim and
 * every one of them was solved against a landing that stays where it is.
 */
export const MOVER_PAD_HOPS = 2;
const MOVER_JUNCTION_HOPS = 1;
/**
 * Primitives the whole mountain may set in motion.
 *
 * Not a taste. `broadphase.ts` files the static geometry into 8 m height bands
 * and throws almost all of it away on one bucket lookup — but a mover's band
 * changes every substep, so every moving primitive lives in one flat list that
 * each query scans whole. A static prim is very nearly free and a moving one is
 * linear in the count, and this is the number that stops a generous seed from
 * quietly making its own physics step twice the price of a mean seed's.
 *
 * It is spent in the order {@link planMovers} runs: the four set pieces first —
 * at most eight primitives between the blink field, the carousel, the ferry and
 * the lift — and then the quota fillers, each of which asks what is left before
 * it claims and places fewer of itself when the answer is nothing. Which is the
 * same graceful shortfall they already take when the *geometry* says no, and is
 * reported the same way: by the census in `level.selftest.ts`, not by a throw.
 *
 * Exported so that census enforces this number rather than a second copy of it:
 * a ceiling the planner respects and the test does not know about is a ceiling
 * that gets quietly raised.
 */
export const MOVER_PRIM_BUDGET = 26;
/**
 * Sliders each zone wants. Falling short is allowed and reported; overshooting
 * is not.
 *
 * Meadow wants none — every node of it is inside `MOVER_TUTORIAL_NODES` anyway,
 * so the zero is the statement rather than the consequence. Verdant asks for
 * three rather than the two that teach the idea: the first two are met within a
 * few hops of each other and read as a lesson, and a third halfway up the skirt
 * is what turns the lesson into something the zone does. Above that these are
 * the mountain's backbone of movement — enough of them per zone that a long
 * stretch of pads that all hold still is a shape the seed chose rather than the
 * default everywhere.
 */
const SLIDERS_PER_ZONE: readonly number[] = [0, 3, 3, 2, 3, 2];
/** The zone the Frozen Shelf's carousel wants, and where it looks if that fails. */
const CAROUSEL_ZONES: readonly number[] = [4, 3, 5];
/** Where the Crystal Hollow's blink field opens. */
const CRYSTAL_ZONE = 3;
/**
 * Where the elevator looks for a shaft, in order. The Hollow first — a lift
 * belongs to the zone of long vertical caverns — but a lift's travel passes
 * through the *heights* of both its neighbours, so it needs a pair of landings
 * that are genuinely well apart in plan, and which zone happens to offer one is
 * a question about the seed rather than about the design.
 */
const LIFT_ZONES: readonly number[] = [3, 4, 5, 2];
/** Where the long ferry opens: the crown, where the gaps are already meanest. */
const FERRY_ZONE = 5;

type P2 = [number, number];

/**
 * A collidable box reduced to what a clearance test needs: its footprint in
 * plan, corners in cyclic order, and the slice of height it fills.
 *
 * Exact for the yawed boxes this course is built from — and for a posed slider
 * or a Y-orbiting bar, which are yawed boxes too. Anything else (a tilted ramp,
 * a post, a mushroom) is described by a box that *contains* it, so a clearance
 * this reports is one the real geometry also has.
 */
interface Foot {
  xz: P2[];
  loY: number;
  hiY: number;
}

/** The footprint and height span of an oriented box. */
function boxFoot(center: V3, half: V3, quat: V4): Foot {
  const [qx, qy, qz, qw] = quat;
  const r00 = 1 - 2 * (qy * qy + qz * qz);
  const r10 = 2 * (qx * qy + qw * qz);
  const r20 = 2 * (qx * qz - qw * qy);
  const r11 = 1 - 2 * (qx * qx + qz * qz);
  const r02 = 2 * (qx * qz + qw * qy);
  const r12 = 2 * (qy * qz - qw * qx);
  const r22 = 1 - 2 * (qx * qx + qy * qy);
  // The box's own axes in plan, scaled by the half-extent along each.
  const ax = r00 * half[0];
  const az = r20 * half[0];
  const cx = r02 * half[2];
  const cz = r22 * half[2];
  const ey = Math.abs(r10) * half[0] + Math.abs(r11) * half[1] + Math.abs(r12) * half[2];
  return {
    xz: [
      [center[0] - ax - cx, center[2] - az - cz],
      [center[0] + ax - cx, center[2] + az - cz],
      [center[0] + ax + cx, center[2] + az + cz],
      [center[0] - ax + cx, center[2] - az + cz],
    ],
    loY: center[1] - ey,
    hiY: center[1] + ey,
  };
}

/** The same, for whatever kind the primitive happens to be. */
function footOfPrim(prim: Primitive): Foot {
  switch (prim.kind) {
    case 'box':
      return boxFoot(prim.center, prim.half, prim.quat);
    case 'sphere':
      return boxFoot(prim.center, [prim.radius, prim.radius, prim.radius], identityQuat());
    default: {
      const r = prim.radius;
      return boxFoot(
        [
          (prim.a[0] + prim.b[0]) * 0.5,
          (prim.a[1] + prim.b[1]) * 0.5,
          (prim.a[2] + prim.b[2]) * 0.5,
        ],
        [
          Math.abs(prim.a[0] - prim.b[0]) * 0.5 + r,
          Math.abs(prim.a[1] - prim.b[1]) * 0.5 + r,
          Math.abs(prim.a[2] - prim.b[2]) * 0.5 + r,
        ],
        identityQuat(),
      );
    }
  }
}

/**
 * A lower bound on the clear air between two boxes: the widest gap any of their
 * own face normals can prise open, in plan, or in height.
 *
 * Negative where every axis overlaps, which is what an intersection is. Only the
 * boxes' own normals are tried, so where two corners are the closest pair the
 * answer comes back short of the true distance — conservative in the one
 * direction that is safe.
 */
function footGap(a: Foot, b: Foot): number {
  let flat = -Infinity;
  for (const poly of [a.xz, b.xz]) {
    for (let k = 0; k < poly.length; k++) {
      const p = poly[k] as P2;
      const q = poly[(k + 1) % poly.length] as P2;
      const nx = -(q[1] - p[1]);
      const nz = q[0] - p[0];
      const len = Math.hypot(nx, nz);
      if (len < 1e-9) continue;
      let aLo = Infinity;
      let aHi = -Infinity;
      for (const pt of a.xz) {
        const d = (pt[0] * nx + pt[1] * nz) / len;
        if (d < aLo) aLo = d;
        if (d > aHi) aHi = d;
      }
      let bLo = Infinity;
      let bHi = -Infinity;
      for (const pt of b.xz) {
        const d = (pt[0] * nx + pt[1] * nz) / len;
        if (d < bLo) bLo = d;
        if (d > bHi) bHi = d;
      }
      const gap = Math.max(aLo - bHi, bLo - aHi);
      if (gap > flat) flat = gap;
    }
  }
  const tall = Math.max(a.loY - b.hiY, b.loY - a.hiY);
  return Math.max(flat === -Infinity ? 0 : flat, tall);
}

/** Distance from a world point to the box a {@link Foot} describes. */
function pointFootDist(x: number, y: number, z: number, f: Foot): number {
  let pos = 0;
  let neg = 0;
  let near = Infinity;
  for (let k = 0; k < f.xz.length; k++) {
    const p = f.xz[k] as P2;
    const q = f.xz[(k + 1) % f.xz.length] as P2;
    const dx = q[0] - p[0];
    const dz = q[1] - p[1];
    const side = dx * (z - p[1]) - dz * (x - p[0]);
    if (side > 0) pos++;
    else if (side < 0) neg++;
    const len2 = dx * dx + dz * dz;
    const t = len2 < 1e-12 ? 0 : clamp(((x - p[0]) * dx + (z - p[1]) * dz) / len2, 0, 1);
    const d = Math.hypot(x - (p[0] + dx * t), z - (p[1] + dz * t));
    if (d < near) near = d;
  }
  const flat = pos === 0 || neg === 0 ? 0 : near;
  const tall = Math.max(0, f.loY - y, y - f.hiY);
  return Math.hypot(flat, tall);
}

/**
 * Samples along the swinging log's own axis, for the segment-to-box distance in
 * {@link segFootDist}. Distance is 1-Lipschitz along the segment, so subtracting
 * half the sample spacing turns a sampled minimum into a genuine lower bound.
 */
const SEG_SAMPLES = 17;

/** Distance from the segment `a`→`b` to a point. Exact, unlike its box cousin. */
function segPointDist(a: V3, b: V3, p: V3): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  const len2 = dx * dx + dy * dy + dz * dz;
  const t =
    len2 < 1e-12 ? 0 : clamp(((p[0] - a[0]) * dx + (p[1] - a[1]) * dy + (p[2] - a[2]) * dz) / len2, 0, 1);
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t), p[2] - (a[2] + dz * t));
}

/** A lower bound on the distance from the segment `a`→`b` to a box. */
function segFootDist(a: V3, b: V3, f: Foot): number {
  let best = Infinity;
  for (let s = 0; s < SEG_SAMPLES; s++) {
    const t = s / (SEG_SAMPLES - 1);
    const d = pointFootDist(
      a[0] + (b[0] - a[0]) * t,
      a[1] + (b[1] - a[1]) * t,
      a[2] + (b[2] - a[2]) * t,
      f,
    );
    if (d < best) best = d;
  }
  const len = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  return Math.max(0, best - len / (2 * (SEG_SAMPLES - 1)));
}

/** One full cycle of a spec, in seconds; zero for the kinds that never move. */
function moverCycle(spec: MoverSpec): number {
  if (spec.type === 'orbit') return spec.rate === 0 ? 0 : TAU / Math.abs(spec.rate);
  if (spec.type === 'blink') return 0;
  return spec.period;
}

/** What a moving landing is *for* — which is what decides how it may be measured. */
export type MoverClass = 'slider' | 'ferry' | 'elevator' | 'carousel' | 'blink';

/** A landing that travels: the spec its pad carries, and the bargain it offers. */
export interface NodeMoverPlan {
  spec: MoverSpec;
  cls: MoverClass;
  /**
   * Wait-and-ride. A ferry's *rest* gaps say nothing about whether its hops are
   * jumpable — the promise is about the extremes of its travel, and
   * `level.selftest.ts` re-solves the same inequality off the same spec.
   */
  ferry: boolean;
}

/** Everything {@link planMovers} decided, keyed by what has to build it. */
interface MoverPlan {
  /** Node index → the spec that node's landing pad carries. */
  nodes: Map<number, NodeMoverPlan>;
  /** Sweeper bars, sized and posed, waiting to be pushed. */
  sweepers: Primitive[];
  /** Swinging logs, by the id of the lane whose deck they hang over. */
  pendulums: Map<number, Primitive[]>;
}

/** The course, pre-chewed into the questions the placers keep asking of it. */
interface MoverCtx {
  course: Course;
  prev: number[];
  next: number[];
  /** Path of the lane that owns each node, and where along it the node sits. */
  path: Map<number, number[]>;
  posInLane: number[];
  /** May a moving landing sit here at all? See {@link moverEligibility}. */
  eligible: boolean[];
  /** Eligible node indices, by zone, in flat order. */
  byZone: number[][];
  /** The same, plus the islands — which may be *guarded* but never moved. */
  sweepable: number[][];
  /** The exact landing box each eligible node's emitter will push. */
  probePad: Array<Primitive | null>;
  /** Everything already standing on the mountain, as clearance boxes. */
  obstacles: MoverObstacle[];
  /** The first trunk nodes, whose airspace stays still. */
  tutorial: V3[];
  /** Positions along each lane that already carry a mover. */
  placed: Map<number, number[]>;
}

/** A static surface, plus the bounding circle the broad phase rejects it on. */
interface MoverObstacle {
  /** The node this box belongs to, or −1 — a pad never has to clear itself. */
  at: number;
  foot: Foot;
  /**
   * Set where the surface really is a sphere. A mushroom cap inside the box that
   * holds it is three quarters of a metre of clearance that is not there, and
   * this course puts mushrooms one hop from platforms all the way up.
   */
  ball?: { c: V3; r: number };
  cx: number;
  cz: number;
  r: number;
}

/**
 * The order a quota of `want` placements searches a candidate list in: `want`
 * evenly spread starting points, each walked outward until something fits. A
 * zone's movers end up spread down its climb rather than clumped at whichever
 * end the scan happened to begin.
 */
function spreadOrder(len: number, want: number, slot: number): number[] {
  if (len <= 0) return [];
  return outwardFrom(len, Math.min(len - 1, Math.floor(((slot + 0.5) * len) / Math.max(1, want))));
}

/** `0 … len − 1`, ordered by distance from `start`, nearer end first on a tie. */
function outwardFrom(len: number, start: number): number[] {
  const order: number[] = [];
  for (let k = 0; k < len; k++) order.push(k);
  return order.sort((a, b) => Math.abs(a - start) - Math.abs(b - start) || a - b);
}

/**
 * The most travel a sliding pad may take along `axis` before its *worst* phase
 * stretches the hop between `a` and `b` past `budget`. Which of the two carries
 * the pad does not matter: the answer is the same either way round.
 *
 * A slide reaches `±A · axis` either side of the rest pose, so the hop it has to
 * answer for is the longer of the two ends: `max(|d ± A·u|) ≤ budget`, with
 * `d` the horizontal offset between the nodes. Both halves are one quadratic,
 * and its root is this — negative where the hop is already over budget without
 * any help, which is the caller's cue to leave the node alone.
 *
 * Solving the real worst case rather than charging the hop the whole amplitude
 * is what makes the top of the mountain possible at all: the zone chords up
 * there are 5.9 and 6.1 m, so a pad that paid the full amplitude on a hop it
 * only travels *across* would have nothing left to move with. Sliding sideways
 * to a 6 m hop costs it a tenth of a metre; sliding along it costs the lot.
 */
function slideRoom(a: Node, b: Node, axis: V3, budget: number): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const along = dx * axis[0] + dz * axis[2];
  const disc = along * along + budget * budget - (dx * dx + dz * dz);
  return disc <= 0 ? -1 : Math.sqrt(disc) - Math.abs(along);
}

/**
 * Which nodes may carry a moving landing.
 *
 * Every rule here is about somewhere the climb has already promised something
 * else. The tutorial promised a course that holds still. A junction promised
 * that the moment of choosing is not also a landing problem, and the hop either
 * side of one is part of that promise. A checkpoint promised a rest stop, and
 * two hops either side of a pad is how far the approach over the lobby rim
 * reaches. A lobby floor's height band promised nothing at all — it is where
 * {@link sealApproaches} moves nodes to suit itself, and a pad whose position was
 * decided by the seal is not one to start sliding around. And an island already
 * carries a set piece.
 *
 * The sweepers' list is the same one with the islands left in and the summit
 * approach taken out — see {@link SUMMIT_APPROACH_NODES}.
 */
function moverEligibility(ctx: MoverCtx, junctions: Set<number>): void {
  const nodes = ctx.course.nodes;
  const pads = nodes.filter((node) => node.kind === 'checkpoint');
  /**
   * The last stretch of the *trunk path*, off limits to a hazard. Read off the
   * path rather than off the array for the reason `assignKinds` reads it off the
   * path: once branches are appended, "near the end of `nodes`" stops meaning
   * "near the summit".
   */
  const nearSummit = new Set<number>();
  {
    const trunk = ctx.course.lanes.find((lane) => lane.role === 'trunk');
    const path = trunk === undefined ? [] : ((ctx.path.get(trunk.id) ?? []) as number[]);
    for (let k = Math.max(0, path.length - SUMMIT_APPROACH_NODES); k < path.length; k++) {
      nearSummit.add(path[k] as number);
    }
  }

  // Quarantine, walked along the lanes: "two hops from a pad" is a question
  // about the route, not about where a node landed in the flat array.
  const blocked = new Set<number>();
  for (const lane of ctx.course.lanes) {
    const path = ctx.path.get(lane.id) as number[];
    for (let k = 0; k < path.length; k++) {
      const at = path[k] as number;
      const pad = (nodes[at] as Node).kind === 'checkpoint';
      if (!pad && !junctions.has(at)) continue;
      const span = pad ? MOVER_PAD_HOPS : MOVER_JUNCTION_HOPS;
      for (let d = -span; d <= span; d++) {
        const j = path[k + d];
        if (j !== undefined) blocked.add(j);
      }
    }
  }

  const bands = [...ZONE_BOUNDARY_YS, ...pads.map((pad) => pad.y)];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as Node;
    if (node.kind !== 'platform' && node.kind !== 'ledge') continue;
    if (blocked.has(i)) continue;
    if ((ctx.prev[i] as number) < 0 || (ctx.next[i] as number) < 0) continue;
    if (node.lane === 0 && (ctx.posInLane[i] as number) < MOVER_TUTORIAL_NODES) continue;
    let inBand = false;
    for (const y of bands) {
      if (node.y > y - MOVER_LOBBY_BELOW && node.y < y + MOVER_LOBBY_ABOVE) inBand = true;
    }
    if (inBand) continue;
    // An island carries a set piece already, so nothing may *move* it — but a
    // bar turning over one is the set piece, which is why the sweepers get their
    // own list with the islands left in. And the summit approach left out: the
    // last hops onto the rim answer to the hop budget and to nothing else.
    if (!nearSummit.has(i)) (ctx.sweepable[node.zone] as number[]).push(i);
    if (ctx.course.islands.has(i)) continue;
    ctx.eligible[i] = true;
    (ctx.byZone[node.zone] as number[]).push(i);
  }
}

/**
 * Everything already standing on the mountain, as boxes to keep clear of.
 *
 * Built by running the *real* emitters into a throwaway array rather than by
 * describing their output a second time — every one of them that matters here is
 * pure geometry over nodes that already exist, so the planner measures against
 * exactly the surfaces the build will produce. The two exceptions are a ramp,
 * whose tilt is not drawn until emission and which is therefore covered by an
 * upright box tall enough to hold it whichever way it ends up leaning, and the
 * rolled decorative props, which are not landings and are nobody's clearance
 * problem.
 */
function moverObstacles(
  course: Course,
  junctions: Set<number>,
  padScale: number[],
): { obstacles: MoverObstacle[]; probePad: Array<Primitive | null> } {
  const nodes = course.nodes;
  const { prev, next } = laneNeighbours(course);
  const obstacles: MoverObstacle[] = [];
  const probePad: Array<Primitive | null> = nodes.map(() => null);
  let pads = 0;

  const take = (at: number, from: Primitive[], start: number): void => {
    for (let p = start; p < from.length; p++) {
      const prim = from[p] as Primitive;
      const foot = footOfPrim(prim);
      let cx = 0;
      let cz = 0;
      for (const pt of foot.xz) {
        cx += pt[0] / foot.xz.length;
        cz += pt[1] / foot.xz.length;
      }
      let r = 0;
      for (const pt of foot.xz) r = Math.max(r, Math.hypot(pt[0] - cx, pt[1] - cz));
      obstacles.push({
        at,
        foot,
        cx,
        cz,
        r,
        ...(prim.kind === 'sphere'
          ? { ball: { c: [prim.center[0], prim.center[1], prim.center[2]] as V3, r: prim.radius } }
          : {}),
      });
    }
  };

  const probe: Primitive[] = [];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as Node;
    const scale = padScale[i] as number;
    const start = probe.length;
    switch (node.kind) {
      case 'platform':
        emitPlatform(probe, node, (junctions.has(i) ? JUNCTION_PAD_SCALE : 1) * scale);
        probePad[i] = probe[start] as Primitive;
        break;
      case 'ledge':
        emitLedge(probe, node, scale);
        probePad[i] = probe[start] as Primitive;
        break;
      case 'ramp': {
        // The tilt is rolled at emission; this is the box that holds every tilt
        // the roll can produce, which is what {@link nodeFootprint} already knows.
        const foot = nodeFootprint(node, junctions.has(i), false);
        const half = zoneOf(node).padHalf;
        if (foot !== null) {
          probe.push({
            kind: 'box',
            center: [node.x, (foot.lo + foot.hi) * 0.5, node.z],
            half: [half[0] * scale, (foot.hi - foot.lo) * 0.5, half[2] * scale],
            quat: quatYaw(node.yaw),
            material: zoneOf(node).mats.ramp,
          });
        }
        break;
      }
      case 'mushroom':
        emitMushroom(probe, node);
        break;
      case 'pillar':
        emitPillar(probe, node, scale);
        break;
      case 'funnel':
        emitFunnel(probe, node, aheadAngle(nodes, prev, next, i), scale);
        break;
      case 'checkpoint':
        emitLobbyFloor(probe, node, ZONES[Math.min(pads + 1, ZONES.length - 1)] as Zone);
        emitCheckpoint(probe, node);
        pads++;
        break;
    }
    if (course.islands.has(i)) emitIsland(probe, node, scale);
    take(i, probe, start);
  }

  // Lane furniture — a bridge's planks are landings like any other, and a mover
  // sweeping through a deck would be the same fault as one sweeping through a pad.
  for (const lane of course.lanes) {
    if (lane.role === 'trunk') continue;
    const start = probe.length;
    decorateLane(probe, course, lane, junctions, []);
    take(-1, probe, start);
  }

  return { obstacles, probePad };
}

/** Bounds of one posed primitive, as the tutorial and shaft rules measure it. */
function footBounds(f: Foot): { min: V3; max: V3 } {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  for (const pt of f.xz) {
    x0 = Math.min(x0, pt[0]);
    x1 = Math.max(x1, pt[0]);
    z0 = Math.min(z0, pt[1]);
    z1 = Math.max(z1, pt[1]);
  }
  return { min: [x0, f.loY, z0], max: [x1, f.hiY, z1] };
}

/** Distance from a point to an axis-aligned box; zero inside it. */
function pointAabbDist(p: V3, box: { min: V3; max: V3 }): number {
  let sum = 0;
  for (let k = 0; k < 3; k++) {
    const d = Math.max(0, (box.min[k] as number) - (p[k] as number), (p[k] as number) - (box.max[k] as number));
    sum += d * d;
  }
  return Math.sqrt(sum);
}

/**
 * Does everything this primitive does over one cycle stay off the shaft, inside
 * the mountain, and out of the tutorial's airspace?
 *
 * Asked of the **sampled poses** rather than of the swept AABB, for the same
 * reason `shaftMetrics` in `level.selftest.ts` has always taken the hull of a
 * box's corners rather than its bounding box: a radial question answered by an
 * axis-aligned box is answered wrong. A pad out on the rim at 45° has an AABB
 * corner two metres further from the axis than any part of the pad, and the
 * bounding box of a full *orbit* contains the axis outright — a carousel that
 * never comes within four metres of the shaft would fail that test every time.
 * The swept box is still exactly the right tool for the broad phase in
 * {@link moverIsClear}, which is where it is used.
 */
function moverStaysHome(ctx: MoverCtx, base: Primitive, spec: MoverSpec, keep: number): boolean {
  const cycle = moverCycle(spec);
  const probe = clonePrimitive(base);
  const n = cycle === 0 ? 1 : MOVER_SAMPLES;
  for (let s = 0; s < n; s++) {
    applyMover(base, spec, cycle === 0 ? 0 : (s / n) * cycle, probe);
    const foot = footOfPrim(probe);
    if (foot.hiY > SHAFT_MIN_Y) {
      if (pointFootDist(0, (foot.loY + foot.hiY) * 0.5, 0, foot) < SHAFT_RADIUS) return false;
    }
    for (const pt of foot.xz) if (Math.hypot(pt[0], pt[1]) > R_HARD_MAX) return false;
    const bounds = footBounds(foot);
    for (const node of ctx.tutorial) if (pointAabbDist(node, bounds) < keep) return false;
  }
  return true;
}

/**
 * Does this primitive keep `keep` metres of air around everything else, at every
 * pose it takes?
 *
 * The obstacles are filtered against the whole journey's bounds first — the
 * broad phase leaves a handful of boxes out of several hundred — and the survivors
 * are then measured at every sampled pose. A capsule is measured from its own
 * axis, so it owes its radius on top.
 */
function moverIsClear(
  ctx: MoverCtx,
  base: Primitive,
  spec: MoverSpec,
  keep: number,
  own: number,
  restIsBaseline: boolean,
): boolean {
  const box = moverSweptAabb(base, spec);
  const radius = base.kind === 'capsule' || base.kind === 'cylinder' ? base.radius : 0;
  const margin = keep + radius;
  const near: MoverObstacle[] = [];
  for (const o of ctx.obstacles) {
    if (o.at === own) continue;
    if (o.cx + o.r < (box.min[0] as number) - margin) continue;
    if (o.cx - o.r > (box.max[0] as number) + margin) continue;
    if (o.cz + o.r < (box.min[2] as number) - margin) continue;
    if (o.cz - o.r > (box.max[2] as number) + margin) continue;
    if (o.foot.hiY < (box.min[1] as number) - margin) continue;
    if (o.foot.loY > (box.max[1] as number) + margin) continue;
    near.push(o);
  }
  if (near.length === 0) return true;

  /** Clear air between one pose of the mover and one obstacle. */
  const gapTo = (prim: Primitive, o: MoverObstacle): number => {
    if (prim.kind === 'capsule' || prim.kind === 'cylinder') {
      return o.ball !== undefined
        ? segPointDist(prim.a, prim.b, o.ball.c) - o.ball.r - radius
        : segFootDist(prim.a, prim.b, o.foot) - radius;
    }
    const foot = footOfPrim(prim);
    return o.ball !== undefined
      ? pointFootDist(o.ball.c[0], o.ball.c[1], o.ball.c[2], foot) - o.ball.r
      : footGap(foot, o.foot);
  };

  // What each obstacle is owed. Normally `keep` — but a landing pad that starts
  // out crowded was crowded before anything moved, and the trimming
  // {@link padScales} does is the answer to that, not this. So a *pad* is asked
  // only never to make matters worse than the course already made them; a
  // hazard, whose rest pose is nothing but an arbitrary point on its own cycle,
  // owes the whole clearance at every pose it takes.
  const owed: number[] = [];
  for (const o of near) owed.push(restIsBaseline ? Math.min(keep, gapTo(base, o)) : keep);

  const cycle = moverCycle(spec);
  const probe = clonePrimitive(base);
  const n = cycle === 0 ? 1 : MOVER_SAMPLES;
  for (let s = 0; s < n; s++) {
    applyMover(base, spec, cycle === 0 ? 0 : (s / n) * cycle, probe);
    for (let k = 0; k < near.length; k++) {
      if (gapTo(probe, near[k] as MoverObstacle) < (owed[k] as number)) return false;
    }
  }
  return true;
}

/**
 * Primitives this plan has already set in motion, counted the way the physics
 * will count them — one per moving landing, one per bar, one per log. Recomputed
 * at each claim rather than carried as a field: three tiny collections walked a
 * few dozen times at build time is not worth a number that can fall out of step
 * with the thing it counts. See {@link MOVER_PRIM_BUDGET}.
 */
function movingPrims(plan: MoverPlan): number {
  let n = plan.nodes.size + plan.sweepers.length;
  for (const logs of plan.pendulums.values()) n += logs.length;
  return n;
}

/** Would a mover here crowd one already placed on the same lane? */
function moverSpaced(ctx: MoverCtx, i: number): boolean {
  const lane = (ctx.course.nodes[i] as Node).lane;
  const at = ctx.posInLane[i] as number;
  for (const q of ctx.placed.get(lane) ?? []) {
    if (Math.abs(q - at) < MOVER_SPACING) return false;
  }
  return true;
}

/** Write a decided mover into the plan, and claim its stretch of lane. */
function claimMover(
  ctx: MoverCtx,
  plan: MoverPlan,
  i: number,
  spec: MoverSpec,
  cls: MoverClass,
  ferry: boolean,
): void {
  plan.nodes.set(i, { spec, cls, ferry });
  const lane = (ctx.course.nodes[i] as Node).lane;
  const list = ctx.placed.get(lane) ?? [];
  list.push(ctx.posInLane[i] as number);
  ctx.placed.set(lane, list);
}

/** Can a landing pad at node `i` carry this spec without hitting anything? */
function padFits(ctx: MoverCtx, i: number, spec: MoverSpec, keep: number): boolean {
  const base = ctx.probePad[i];
  if (base === null || base === undefined) return false;
  if (!moverStaysHome(ctx, base, spec, TUTORIAL_KEEP)) return false;
  return moverIsClear(ctx, base, spec, keep, i, true);
}

/**
 * Sliding pads: a landing that moves and is still, at every phase, a landing.
 *
 * The amplitude is not a taste, it is a solve — see {@link slideRoom}. Whichever
 * of the two hops has less room to give decides how far the pad may travel, and
 * a node with less than `SLIDE_A_MIN` of room is passed over rather than given a
 * twitch. The rolled amplitude is then read against whatever ceiling that leaves,
 * so a generous node moves further than a tight one without either being a
 * different kind of thing.
 *
 * Both axes are tried, rolled one first. They are not interchangeable: a pad
 * sliding *along* the course spends its whole travel out of the hop's budget,
 * which the wide chords near the summit cannot afford, while one sliding across
 * it barely spends anything — so a zone where one axis is impossible is usually
 * a zone where the other is fine, and rolling one and giving up would quietly
 * cost the crown every slider it has.
 *
 * The travel itself is walked outward from the rolled figure for the same
 * reason: what a pad may spend under the *gap* budget and what it can spend
 * without shouldering the mushroom next door are different numbers, and a node
 * that cannot take 2.1 m of travel will very often take 1.4 m.
 */
function placeSliders(rng: Rng, ctx: MoverCtx, plan: MoverPlan): void {
  const nodes = ctx.course.nodes;
  const budget = MOVER_GAP_BUDGET - MOVER_SLIDE_MARGIN;
  for (let z = 0; z < SLIDERS_PER_ZONE.length; z++) {
    const want = SLIDERS_PER_ZONE[z] as number;
    const list = ctx.byZone[z] as number[];
    for (let slot = 0; slot < want; slot++) {
      // Four draws for every slot, whether or not the zone has anywhere to put
      // it: a zone that runs out of candidates must not shift the next zone's.
      // Which is also why the budget is consulted *after* them and not instead
      // of them — a slot the mountain cannot afford still costs the stream what
      // an affordable one costs.
      const uAmp = rng();
      const uPeriod = rng();
      const uPhase = rng();
      const uAxis = rng();
      if (movingPrims(plan) >= MOVER_PRIM_BUDGET) continue;
      let done = false;
      for (const k of spreadOrder(list.length, want, slot)) {
        if (done) break;
        const i = list[k] as number;
        if (plan.nodes.has(i) || !moverSpaced(ctx, i)) continue;
        const node = nodes[i] as Node;
        const back = nodes[ctx.prev[i] as number] as Node;
        const ahead = nodes[ctx.next[i] as number] as Node;
        const across: V3 = [-Math.sin(node.theta), 0, Math.cos(node.theta)];
        const out: V3 = [Math.cos(node.theta), 0, Math.sin(node.theta)];
        for (const axis of uAxis < 0.5 ? [across, out] : [out, across]) {
          const room = Math.min(
            slideRoom(back, node, axis, budget),
            slideRoom(node, ahead, axis, budget),
          );
          if (room < SLIDE_A_MIN) continue;
          const ceiling = Math.min(SLIDE_A_MAX, room);
          for (const t of outwardFrom(SLIDE_A_STEPS, Math.round(uAmp * (SLIDE_A_STEPS - 1)))) {
            const spec: MoverSpec = {
              type: 'slide',
              axis,
              amplitude: lerp(SLIDE_A_MIN, ceiling, t / (SLIDE_A_STEPS - 1)),
              period: lerp(SLIDE_PERIOD_MIN, SLIDE_PERIOD_MAX, uPeriod),
              phase: uPhase * TAU,
            };
            if (!padFits(ctx, i, spec, MOVER_PAD_KEEP)) continue;
            claimMover(ctx, plan, i, spec, 'slider', false);
            done = true;
            break;
          }
          if (done) break;
        }
      }
    }
  }
}

/**
 * The long ferry: a pad that runs the chord from the node behind it to the node
 * ahead, so one extreme of its travel parks within reach of where you are and
 * the other within reach of where you are going.
 *
 * Its rest gaps are the course's own — the node never moved — but they are not
 * what the ride is made on, and the amplitude is solved against the extremes
 * instead. Walked outward from the rolled amplitude rather than taken from it,
 * because a node that cannot take 3.4 m of travel will often take 3.9 m, and
 * throwing the whole node away over that would cost the crown its ferry.
 */
function placeFerry(rng: Rng, ctx: MoverCtx, plan: MoverPlan): void {
  const nodes = ctx.course.nodes;
  const uAmp = rng();
  const uPeriod = rng();
  const uPhase = rng();
  const list = ctx.byZone[FERRY_ZONE] as number[];
  for (const k of spreadOrder(list.length, 1, 0)) {
    const i = list[k] as number;
    if (plan.nodes.has(i) || !moverSpaced(ctx, i)) continue;
    const node = nodes[i] as Node;
    const back = nodes[ctx.prev[i] as number] as Node;
    const ahead = nodes[ctx.next[i] as number] as Node;
    const dx = ahead.x - back.x;
    const dz = ahead.z - back.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-6) continue;
    const axis: V3 = [dx / len, 0, dz / len];
    for (const s of outwardFrom(FERRY_A_STEPS, Math.round(uAmp * (FERRY_A_STEPS - 1)))) {
      const amplitude = lerp(FERRY_A_MIN, FERRY_A_MAX, s / (FERRY_A_STEPS - 1));
      const onto = Math.hypot(node.x - axis[0] * amplitude - back.x, node.z - axis[2] * amplitude - back.z);
      const off = Math.hypot(node.x + axis[0] * amplitude - ahead.x, node.z + axis[2] * amplitude - ahead.z);
      if (onto > MOVER_GAP_BUDGET - MOVER_FERRY_MARGIN) continue;
      if (off > MOVER_GAP_BUDGET - MOVER_FERRY_MARGIN) continue;
      const spec: MoverSpec = {
        type: 'slide',
        axis,
        amplitude,
        period: lerp(FERRY_PERIOD_MIN, FERRY_PERIOD_MAX, uPeriod),
        phase: uPhase * TAU,
      };
      if (!padFits(ctx, i, spec, MOVER_PAD_KEEP)) continue;
      claimMover(ctx, plan, i, spec, 'ferry', true);
      return;
    }
  }
}

/**
 * The elevator: the same bargain as a ferry, turned on its side.
 *
 * You drop onto it at the bottom of its travel and step off the top, so the
 * reachability the placement owes is *not* the node's own rise: it is the rise
 * from the node behind to the pad at its lowest, and from the pad at its highest
 * to the node ahead. Placements where the next landing sits above the top of the
 * travel are preferred, and one that overshoots by more than a short step down
 * is refused outright — a lift you have to fall off is not a lift.
 *
 * Its travel is walked down from the rolled figure the way a ferry's is, and for
 * a sharper reason: a lift's whole journey passes through the *heights* of both
 * its neighbours, so where a slider only ever has to miss what is beside it, a
 * lift has to miss everything level with either end of its shaft. Half a metre
 * less travel is routinely the difference.
 */
function placeElevator(rng: Rng, ctx: MoverCtx, plan: MoverPlan): void {
  const nodes = ctx.course.nodes;
  const uAmp = rng();
  const uPeriod = rng();
  const uPhase = rng();
  for (const z of LIFT_ZONES) {
    const list = ctx.byZone[z] as number[];
    for (const k of spreadOrder(list.length, 1, 0)) {
      const i = list[k] as number;
      if (plan.nodes.has(i) || !moverSpaced(ctx, i)) continue;
      const node = nodes[i] as Node;
      const back = nodes[ctx.prev[i] as number] as Node;
      const ahead = nodes[ctx.next[i] as number] as Node;
      // The ceiling is the overshoot rule: travel past the exit by more than a
      // short step down and the lift becomes something you fall off the top of.
      const ceiling = Math.min(LIFT_A_MAX, ahead.y - node.y + MOVER_LIFT_STEP_DOWN);
      if (ceiling < LIFT_A_MIN) continue;
      for (const s of outwardFrom(LIFT_A_STEPS, Math.round(uAmp * (LIFT_A_STEPS - 1)))) {
        const amplitude = lerp(LIFT_A_MIN, ceiling, s / (LIFT_A_STEPS - 1));
        const low = node.y - amplitude - back.y;
        const high = ahead.y - (node.y + amplitude);
        if (low > MOVER_RISE_BUDGET - MOVER_SLIDE_MARGIN || low < -MOVER_DROP_BUDGET) continue;
        if (high > MOVER_RISE_BUDGET - MOVER_SLIDE_MARGIN) continue;
        const spec: MoverSpec = {
          type: 'slide',
          axis: [0, 1, 0],
          amplitude,
          period: lerp(LIFT_PERIOD_MIN, LIFT_PERIOD_MAX, uPeriod),
          phase: uPhase * TAU,
        };
        if (!padFits(ctx, i, spec, MOVER_PAD_KEEP)) continue;
        claimMover(ctx, plan, i, spec, 'elevator', true);
        return;
      }
    }
  }
}

/**
 * The Frozen Shelf's carousel: one pad orbiting the entire shaft, the way its
 * own lane is already going, back in its slot every twenty-eight seconds.
 *
 * Nothing about the course changes — the node is where it always was at `t = 0`,
 * so both its neighbours' gaps are the ones the walk solved, and the ride is a
 * whole lap of the mountain you take because you waited rather than because you
 * had to. Which is also why it is the one mover with nothing rolled: a set piece
 * this large is either in the right place or it is not, and the search is over
 * every candidate in the zone until one is.
 */
function placeCarousel(ctx: MoverCtx, plan: MoverPlan): void {
  const nodes = ctx.course.nodes;
  for (const z of CAROUSEL_ZONES) {
    const lo = z === 0 ? FIRST_NODE_Y : (ZONE_BOUNDARY_YS[z - 1] as number);
    const hi = z === ZONES.length - 1 ? LAST_NODE_Y : (ZONE_BOUNDARY_YS[z] as number);
    const list = ctx.byZone[z] as number[];
    for (const k of spreadOrder(list.length, 1, 0)) {
      const i = list[k] as number;
      const node = nodes[i] as Node;
      if (node.lane !== 0) continue;
      if (node.y < lo + CAROUSEL_ZONE_INSET || node.y > hi - CAROUSEL_ZONE_INSET) continue;
      if (plan.nodes.has(i) || !moverSpaced(ctx, i)) continue;
      const ahead = nodes[ctx.next[i] as number] as Node;
      const turn = Math.sign(ahead.theta - node.theta) || 1;
      const spec: MoverSpec = {
        type: 'orbit',
        pivot: [0, node.y, 0],
        axis: [0, turn, 0],
        rate: TAU / CAROUSEL_PERIOD,
        phase: 0,
      };
      if (!padFits(ctx, i, spec, CAROUSEL_KEEP)) continue;
      claimMover(ctx, plan, i, spec, 'carousel', true);
      return;
    }
  }
}

/**
 * The Crystal Hollow's blink field: a run of consecutive pads that take turns
 * existing.
 *
 * Nothing moves, so no gap budget is touched — the field is entirely a question
 * of timing, and the timing is rigged to be survivable. Adjacent pads are half a
 * cycle apart and the duty is over a half, so at every instant at least one of
 * any neighbouring pair is solid: there is always a way across, and the only
 * thing being asked of you is to read the rhythm before you commit.
 */
function placeBlinkField(rng: Rng, ctx: MoverCtx, plan: MoverPlan): void {
  const nodes = ctx.course.nodes;
  const uLen = rng();
  const want = BLINK_RUN_MIN + Math.floor(uLen * (BLINK_RUN_MAX - BLINK_RUN_MIN + 1));
  const list = ctx.byZone[CRYSTAL_ZONE] as number[];
  for (const k of spreadOrder(list.length, 1, 0)) {
    const head = list[k] as number;
    const path = ctx.path.get((nodes[head] as Node).lane) as number[];
    const at = ctx.posInLane[head] as number;
    const run: number[] = [];
    for (let d = 0; d < want; d++) {
      const j = path[at + d];
      if (j === undefined) break;
      if (ctx.eligible[j] !== true || plan.nodes.has(j)) break;
      const pad = nodes[j] as Node;
      // Platforms only. A ledge you have to time as well as hit is two narrow
      // chances for one hop, which is the thing `assignKinds` refuses to build
      // anywhere else — and it is what lets the field wear the shard colours,
      // which are a platform's.
      if (pad.zone !== CRYSTAL_ZONE || pad.kind !== 'platform') break;
      run.push(j);
    }
    if (run.length < BLINK_RUN_MIN) continue;
    if (!moverSpaced(ctx, run[0] as number)) continue;
    for (let d = 0; d < run.length; d++) {
      claimMover(
        ctx,
        plan,
        run[d] as number,
        { type: 'blink', period: BLINK_PERIOD, duty: BLINK_DUTY, phase: (d % 2) * 0.5 },
        'blink',
        false,
      );
    }
    return;
  }
}

/** How far a pad reaches from its node, whichever way it is turned. */
function padReach(ctx: MoverCtx, i: number): number {
  const node = ctx.course.nodes[i] as Node;
  const foot = nodeFootprint(node, false, ctx.course.islands.has(i));
  return foot === null ? 0 : foot.reach;
}

/**
 * Sweeper bars: a thin beam turning over a wide landing at the height a body
 * stands at, so the roomiest pads on the mountain stop being the restful ones.
 *
 * They go where there is something worth guarding — the islands first, and then
 * the widest ordinary pads — and never onto a landing that is itself moving: a
 * bar pivoted on a fixed point over a pad that slides out from under it is two
 * ideas fighting. Direction and phase are rolled; the rate is the zone's, so the
 * crown's bars come round half again as fast as the ruins'.
 *
 * A zone asks for {@link SWEEPERS_PER_ZONE} of them and takes what it can get.
 * The two are rolled independently — the second is not the first's twin turning
 * the other way, it is another bar — and held `SWEEPER_KEEP_APART` from every
 * bar already hanging, which is what stops the sort from putting both of them on
 * the two widest pads of the same terrace.
 */
function placeSweepers(rng: Rng, ctx: MoverCtx, plan: MoverPlan): void {
  const nodes = ctx.course.nodes;
  /** Pads already turning a bar, over the whole mountain rather than per zone. */
  const hung: number[] = [];
  for (const z of SWEEPER_ZONES) {
    // Islands first, then the widest pads: a bar over a narrow ledge is a coin
    // flip, and the point of one is that it makes a roomy landing a decision.
    const cands = (ctx.sweepable[z] as number[]).slice().sort((a, b) => {
      const ia = ctx.course.islands.has(a) ? 1 : 0;
      const ib = ctx.course.islands.has(b) ? 1 : 0;
      return ib - ia || padReach(ctx, b) - padReach(ctx, a) || a - b;
    });
    for (let slot = 0; slot < SWEEPERS_PER_ZONE; slot++) {
      // Two draws per bar, drawn whether or not the zone has anywhere to hang
      // one and before the budget is consulted, for the reason every other pass
      // here draws first: a bar that cannot be built must cost the stream what a
      // built one costs, or the crown's bars depend on the ruins' luck.
      const uDir = rng();
      const uPhase = rng();
      if (movingPrims(plan) >= MOVER_PRIM_BUDGET) continue;
      /** Hang a bar over one pad, shortening it until it fits. */
      const hang = (i: number): boolean => {
        const node = nodes[i] as Node;
        const pivot: V3 = [node.x, node.y + SWEEPER_LIFT, node.z];
        const spec: MoverSpec = {
          type: 'orbit',
          pivot,
          axis: [0, 1, 0],
          rate: (uDir < 0.5 ? -1 : 1) * (TAU / (SWEEPER_PERIODS[z] as number)),
          phase: uPhase * TAU,
        };
        for (const over of SWEEPER_OVERHANGS) {
          const bar: Primitive = {
            kind: 'box',
            center: [pivot[0], pivot[1], pivot[2]],
            half: [padReach(ctx, i) + over, SWEEPER_HALF_THICK, SWEEPER_HALF_DEPTH],
            quat: quatYaw(node.yaw),
            material: MAT_HAZARD,
          };
          if (!moverStaysHome(ctx, bar, spec, TUTORIAL_KEEP)) continue;
          if (!moverIsClear(ctx, bar, spec, HAZARD_KEEP, -2, false)) continue;
          bar.mover = spec;
          plan.sweepers.push(bar);
          return true;
        }
        return false;
      };
      for (const i of cands) {
        if (plan.nodes.has(i)) continue;
        if (!barSpaced(ctx, hung, i)) continue;
        if (hang(i)) {
          hung.push(i);
          break;
        }
      }
    }
  }
}

/** Would a bar over this pad turn through air one already hanging has claimed? */
function barSpaced(ctx: MoverCtx, hung: number[], i: number): boolean {
  const node = ctx.course.nodes[i] as Node;
  for (const j of hung) {
    const other = ctx.course.nodes[j] as Node;
    const d = Math.hypot(node.x - other.x, node.y - other.y, node.z - other.z);
    if (d < SWEEPER_KEEP_APART) return false;
  }
  return true;
}

/**
 * Swinging logs over the bridge decks.
 *
 * A deck is the one place on the mountain where the route is a floor rather than
 * a series of landings, and a floor is somewhere you stop paying attention. The
 * axis is taken *along* the run, so the log swings across it and the deck has a
 * width again; the pivot sits high enough that the bottom of the swing passes at
 * knee height, which is a shove rather than a wall.
 *
 * The teaching zones' bridges are exempt: a built structure met while the climb
 * is still explaining itself is allowed to be a built structure. Meadow never
 * forks at all, so in practice the exemption is Verdant's.
 */
function placePendulums(rng: Rng, ctx: MoverCtx, plan: MoverPlan): void {
  const nodes = ctx.course.nodes;
  for (const lane of ctx.course.lanes) {
    if (lane.motif !== 'bridgeRun' || lane.body.length < 2) continue;
    // A fixed block per lane, and a fixed block per possible log inside it, so
    // an ineligible deck costs the stream exactly what an eligible one does.
    const uCount = rng();
    const rolls: number[][] = [];
    for (let g = 0; g < LOG_MAX_PER_LANE; g++) rolls.push([rng(), rng(), rng()]);
    const zone = (nodes[lane.body[0] as number] as Node).zone;
    if (zone < LOG_MIN_ZONE) continue;
    const spans = lane.body.length - 1;
    const want = Math.min(spans, 1 + Math.floor(uCount * LOG_MAX_PER_LANE));
    const logs: Primitive[] = [];
    /** Spans already carrying one — two logs over the same planks is a gate. */
    const hung = new Set<number>();
    for (let g = 0; g < want; g++) {
      // Last pass of the plan, so this is where a mountain that has already
      // spent its motion elsewhere stops. A deck without a log is still a deck;
      // a zone whose only interactive thing was cut is not still a zone, which
      // is why the logs yield and the sliders do not.
      if (movingPrims(plan) + logs.length >= MOVER_PRIM_BUDGET) break;
      const [uAmp, uPeriod, uPhase] = rolls[g] as number[];
      for (const s of spreadOrder(spans, want, g)) {
        if (hung.has(s)) continue;
        const a = nodes[lane.body[s] as number] as Node;
        const b = nodes[lane.body[s + 1] as number] as Node;
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const flat = Math.hypot(dx, dz);
        if (flat < 1e-6) continue;
        const px = (a.x + b.x) * 0.5;
        const pz = (a.z + b.z) * 0.5;
        // The clearance is owed to whatever actually stands under the arc, not
        // to the line the log hangs from. Two things can be higher than the
        // span's mid-height: the plank covering the midpoint — a plank is level
        // at its own centre, so on a climbing span it stands up to half a
        // plank-length of rise proud (mirror the layout in `decorateLane`'s
        // bridgeRun branch) — and a widened landing pad whose footprint reaches
        // past the middle of a short span. Hang the pivot off the tallest of
        // them; the window keeps a wall towering beside the deck from counting
        // as its floor (a wall *in* the swing is `moverIsClear`'s to refuse).
        const midY = (a.y + b.y) * 0.5;
        const planks = Math.max(1, Math.floor(flat / PLANK_STEP));
        const run = planks * PLANK_STEP;
        let deckTop = midY;
        for (let p = 0; p < planks; p++) {
          const t = (flat - run + PLANK_STEP * (2 * p + 1)) / (2 * flat);
          if (Math.abs(t - 0.5) * flat > PLANK_HALF[0]) continue;
          deckTop = Math.max(deckTop, a.y + (b.y - a.y) * t);
        }
        for (const o of ctx.obstacles) {
          if (o.foot.hiY <= deckTop || o.foot.hiY > midY + 4) continue;
          if (Math.hypot(px - o.cx, pz - o.cz) > o.r) continue;
          if (pointFootDist(px, o.foot.hiY, pz, o.foot) === 0) deckTop = o.foot.hiY;
        }
        const pivotY = deckTop + LOG_PIVOT_LIFT;
        const log: Primitive = {
          kind: 'capsule',
          a: [px, pivotY - LOG_TOP, pz],
          b: [px, pivotY - LOG_LENGTH, pz],
          radius: LOG_RADIUS,
          material: MAT_HAZARD,
        };
        const spec: MoverSpec = {
          type: 'pendulum',
          pivot: [px, pivotY, pz],
          axis: [dx / flat, 0, dz / flat],
          amplitude: lerp(LOG_AMP_MIN, LOG_AMP_MAX, uAmp as number),
          period: lerp(LOG_PERIOD_MIN, LOG_PERIOD_MAX, uPeriod as number),
          phase: (uPhase as number) * TAU,
        };
        if (!moverStaysHome(ctx, log, spec, TUTORIAL_KEEP)) continue;
        if (!moverIsClear(ctx, log, spec, HAZARD_KEEP, -2, false)) continue;
        log.mover = spec;
        logs.push(log);
        hung.add(s);
        break;
      }
    }
    if (logs.length > 0) plan.pendulums.set(lane.id, logs);
  }
}

/**
 * Decide what the mountain does with itself.
 *
 * Runs after the course is completely settled — sealed, faced, kinds assigned,
 * pads scaled — and before a single primitive is emitted, because a mover is a
 * property of a landing that already exists rather than a landing of its own.
 *
 * The order is set pieces first and quota fillers after. Each of the four set
 * pieces wants a *particular* node — a run of pads in the Crystal Hollow, a
 * trunk node in the middle of the Frozen Shelf, the two ends of a chord in the
 * crown — and taking the pick of the eligible nodes for them before the sliders
 * fill in around them is the difference between a mountain with four ideas on it
 * and one with thirteen sliders and an apology.
 *
 * It is also the order {@link MOVER_PRIM_BUDGET} is spent in, and for the same
 * reason: what the mountain can least afford to lose goes first, and the passes
 * that run last are the ones that place fewer of themselves when the flat list
 * the physics scans every substep is already as long as it may get.
 */
function planMovers(
  rng: Rng,
  course: Course,
  junctions: Set<number>,
  padScale: number[],
): MoverPlan {
  const { prev, next } = laneNeighbours(course);
  const path = new Map<number, number[]>();
  const posInLane = new Array<number>(course.nodes.length).fill(-1);
  for (const lane of course.lanes) {
    const walk = lanePath(course, lane);
    path.set(lane.id, walk);
    for (let k = 0; k < walk.length; k++) {
      const at = walk[k] as number;
      if ((course.nodes[at] as Node).lane === lane.id) posInLane[at] = k;
    }
  }
  const trunkPath = path.get(0) ?? [];
  const tutorial: V3[] = [];
  for (let k = 0; k < Math.min(MOVER_TUTORIAL_NODES, trunkPath.length); k++) {
    const node = course.nodes[trunkPath[k] as number] as Node;
    tutorial.push([node.x, node.y, node.z]);
  }
  const { obstacles, probePad } = moverObstacles(course, junctions, padScale);

  const ctx: MoverCtx = {
    course,
    prev,
    next,
    path,
    posInLane,
    eligible: new Array<boolean>(course.nodes.length).fill(false),
    byZone: ZONES.map(() => []),
    sweepable: ZONES.map(() => []),
    probePad,
    obstacles,
    tutorial,
    placed: new Map<number, number[]>(),
  };
  moverEligibility(ctx, junctions);

  const plan: MoverPlan = {
    nodes: new Map<number, NodeMoverPlan>(),
    sweepers: [],
    pendulums: new Map<number, Primitive[]>(),
  };

  placeBlinkField(rng, ctx, plan);
  placeCarousel(ctx, plan);
  placeFerry(rng, ctx, plan);
  placeElevator(rng, ctx, plan);
  placeSliders(rng, ctx, plan);
  placeSweepers(rng, ctx, plan);
  placePendulums(rng, ctx, plan);
  return plan;
}

/**
 * The colour a moving landing wears: its zone's, or — in the Crystal Hollow —
 * the shard hue of whichever half of a blink field it belongs to. The kind is
 * read off the node rather than assumed, because a ledge that moves is still a
 * ledge and the tag is what physics and the level stats go by.
 */
function moverPadMaterial(node: Node, plan: NodeMoverPlan): Material {
  if (plan.spec.type === 'blink') {
    return plan.spec.phase < 0.25 ? MAT_CRYSTAL_BLINK_A : MAT_CRYSTAL_BLINK_B;
  }
  const mats = zoneOf(node).mats;
  return node.kind === 'ledge' ? mats.moverLedge : mats.mover;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Salt for the mover stream. Any constant would do; the point is only that the
 * two streams never line up, so that what moves is decided independently of
 * where the course went and neither can be retuned into the other.
 */
const MOVER_SEED_SALT = 0x5f356495;

function generate(seed: number): {
  primitives: Primitive[];
  course: Course;
  checkpoints: V3[];
  movers: MoverPlan;
} {
  const rng = mulberry32(seed);
  const course = layoutNodes(rng);
  const nodes = course.nodes;
  // Where the course goes from each node, for the one emitter that cares: a
  // funnel's gap has to open the way its own lane runs.
  const { prev, next } = laneNeighbours(course);
  /** Nodes two lanes share: their pads are widened, and their entries signposted. */
  const junctions = new Set<number>();
  for (const lane of course.lanes) {
    if (lane.role === 'trunk') continue;
    junctions.add(lane.entry);
    junctions.add(lane.exit);
  }
  /** The ends of the climb, which never grow a prop. Read off the trunk path. */
  const bare = new Set<number>();
  {
    const trunk = course.lanes.find((lane) => lane.role === 'trunk');
    const path = trunk !== undefined ? lanePath(course, trunk) : [];
    if (path.length > 0) bare.add(path[0] as number);
    for (let k = Math.max(0, path.length - 2); k < path.length; k++) bare.add(path[k] as number);
  }
  /** What each landing has to give up to keep clear of its neighbours. */
  const pad = padScales(course, junctions);
  /**
   * And which of them travel. Its own stream, drawn from after the course is
   * completely settled and before anything is built, so the mover tables and the
   * course tables can be retuned without either disturbing the other.
   */
  const movers = planMovers(mulberry32((seed ^ MOVER_SEED_SALT) >>> 0), course, junctions, pad);
  const primitives: Primitive[] = [];
  const checkpoints: V3[] = [];

  emitPlaza(primitives);

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as Node;
    const scale = pad[i] as number;
    /** Where this node's own landing surface will land in the array — see below. */
    const landing = primitives.length;
    switch (node.kind) {
      case 'platform':
        emitPlatform(primitives, node, (junctions.has(i) ? JUNCTION_PAD_SCALE : 1) * scale);
        break;
      case 'ledge':
        emitLedge(primitives, node, scale);
        break;
      case 'ramp':
        emitRamp(primitives, node, rng, scale);
        break;
      case 'mushroom':
        emitMushroom(primitives, node);
        break;
      case 'pillar':
        emitPillar(primitives, node, scale);
        break;
      case 'checkpoint': {
        // The floor wears the palette of the zone it *opens*, keyed off how many
        // pads have been laid rather than `node.zone + 1`: the y = 75.5 pad has
        // already crossed into zone 2, so `node.zone + 1` would be 3 — skipping
        // Sunken Ruins and dressing its lobby in the palette above it.
        const lobby = ZONES[Math.min(checkpoints.length + 1, ZONES.length - 1)] as Zone;
        emitLobbyFloor(primitives, node, lobby);
        emitCheckpoint(primitives, node);
        checkpoints.push([node.x, node.y, node.z]);
        break;
      }
      case 'funnel': {
        const forward = nodes[next[i] as number];
        const back = nodes[prev[i] as number];
        const ahead =
          forward !== undefined
            ? forward.theta - node.theta
            : back !== undefined
              ? node.theta - back.theta
              : 0;
        emitFunnel(primitives, node, ahead, scale);
        break;
      }
    }

    // The mover goes on the landing itself, which is the *first* thing every one
    // of those emitters pushes — only `platform` and `ledge` are ever planned,
    // and both push exactly one box. The pad also changes colour: a landing that
    // travels has to say so before you are committed to it.
    const moving = movers.nodes.get(i);
    const surface = primitives[landing];
    if (moving !== undefined && surface !== undefined) {
      surface.mover = moving.spec;
      surface.material = moverPadMaterial(node, moving);
    }

    // Under the pad, not instead of it: the island is what the node is standing
    // on. Pure geometry over a node that already exists, so it draws nothing.
    if (course.islands.has(i)) emitIsland(primitives, node, scale);

    // Rolled for every node so the stream does not depend on which nodes are
    // eligible. Props stay off the trunk's first node and its last two — the
    // ends of the *climb*, which is a question about the trunk path and not
    // about where a node happens to sit in the array — and off the two kinds
    // whose silhouette has to stay readable.
    const roll = rng();
    const decorable = !bare.has(i) && node.kind !== 'checkpoint' && node.kind !== 'funnel';
    if (decorable && roll < DECOR_CHANCE) emitDecor(primitives, node, rng, node.zone);
  }

  // The bars, once every pad they turn over exists. They carry their own spec
  // and their own warning colour, so there is nothing left to decide here.
  for (const bar of movers.sweepers) primitives.push(bar);

  // Signposts and lane furniture last, and in lane order: neither draws anything
  // from the stream, and keeping them out of the node loop keeps that loop one
  // node, one roll.
  for (const lane of course.lanes) {
    if (lane.role === 'trunk') continue;
    const first = nodes[lane.body[0] as number];
    const anchor = nodes[lane.entry];
    if (first === undefined || anchor === undefined) continue;
    emitJunctionMarker(primitives, anchor, first, lane.role);
    decorateLane(primitives, course, lane, junctions, movers.pendulums.get(lane.id) ?? []);
  }

  // One set piece per zone, out past the rim the climb is held inside. Like the
  // lane furniture it draws nothing, so it goes after everything that does.
  emitLandmarks(primitives, course);

  emitSummit(primitives);
  return { primitives, course, checkpoints, movers };
}

/**
 * Build the level for a seed.
 *
 * Identical input always produces byte-identical output; `hashLevel` is the
 * cheap check two clients compare at boot.
 */
export function buildLevel(seed: number = LEVEL_SEED): LevelData {
  const built = generate(seed);
  return {
    primitives: built.primitives,
    spawn: [SPAWN[0], SPAWN[1], SPAWN[2]],
    topY: SUMMIT_Y,
    checkpoints: built.checkpoints,
  };
}

/**
 * Landing-surface centres of every helix node, in climb order.
 *
 * Diagnostics only — `level.selftest.ts` uses this to check that consecutive
 * jumps stay inside the reachable rise/gap budget. It is deliberately *not*
 * part of `LevelData`: the level contract stays "an array of primitives".
 */
export function debugNodes(seed: number = LEVEL_SEED): V3[] {
  return generate(seed).course.nodes.map((n): V3 => [n.x, n.y, n.z]);
}

/** One lane, flattened to the things a test can assert about. */
export interface DebugLane {
  /** Node indices in traversal order — see {@link lanePath}. */
  path: number[];
  role: LaneRole;
  motif: MotifId;
}

/**
 * One moving landing, as a test has to see it.
 *
 * The spec alone is not enough to check a hop against: the same 4 m of travel is
 * a fault on a slider and the entire point of a ferry. So the class the planner
 * decided with comes out too, and `level.selftest.ts` re-solves whichever
 * inequality that class was placed under, off this spec and these node
 * positions. Two things computing the same thing from the same data is what
 * makes the check worth anything.
 */
export interface DebugMover {
  /** Index into `nodes` of the landing carrying the spec. */
  node: number;
  spec: MoverSpec;
  cls: MoverClass;
  ferry: boolean;
}

/**
 * The course as lanes, not as an array.
 *
 * `debugNodes` answers "where are the landing surfaces"; this answers "and in
 * what order does anyone actually visit them", which stops being the same
 * question the moment the climb forks. `level.selftest.ts` walks these paths to
 * check the rise and gap budget, so a fork with an unjumpable step in it fails
 * the same test the trunk always has.
 *
 * Diagnostics only, like `debugNodes` — the level contract stays "an array of
 * primitives".
 */
export function debugCourse(
  seed: number = LEVEL_SEED,
): {
  nodes: V3[];
  kinds: NodeKind[];
  zones: number[];
  lanes: DebugLane[];
  movers: DebugMover[];
} {
  const { course, movers } = generate(seed);
  return {
    nodes: course.nodes.map((n): V3 => [n.x, n.y, n.z]),
    kinds: course.nodes.map((n) => n.kind),
    zones: course.nodes.map((n) => n.zone),
    lanes: course.lanes.map((lane): DebugLane => ({
      path: lanePath(course, lane),
      role: lane.role,
      motif: lane.motif,
    })),
    movers: [...movers.nodes.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([node, plan]): DebugMover => ({
        node,
        spec: plan.spec,
        cls: plan.cls,
        ferry: plan.ferry,
      })),
  };
}

/**
 * FNV-1a hash of every primitive's defining numbers, for the determinism check
 * in §Verification (two clients must log the same value at boot).
 */
export function hashLevel(level: LevelData): string {
  let h = 0x811c9dc5;
  const mix = (n: number): void => {
    // Quantise to 1e-4 so float printing differences cannot change the hash.
    const q = Math.round(n * 10000) | 0;
    for (let i = 0; i < 4; i++) {
      h ^= (q >>> (i * 8)) & 0xff;
      h = Math.imul(h, 0x01000193) >>> 0;
    }
  };
  for (const p of level.primitives) {
    mix(p.kind.length);
    if (p.kind === 'box') {
      mix(p.center[0]); mix(p.center[1]); mix(p.center[2]);
      mix(p.half[0]); mix(p.half[1]); mix(p.half[2]);
      mix(p.quat[0]); mix(p.quat[1]); mix(p.quat[2]); mix(p.quat[3]);
    } else if (p.kind === 'sphere') {
      mix(p.center[0]); mix(p.center[1]); mix(p.center[2]);
      mix(p.radius);
    } else {
      mix(p.a[0]); mix(p.a[1]); mix(p.a[2]);
      mix(p.b[0]); mix(p.b[1]); mix(p.b[2]);
      mix(p.radius);
    }
    mix(p.material.color);
    // Motion is level data too. Two clients that agree about where a pad *is*
    // and disagree about where it is going have not built the same mountain —
    // and a retuned mover has to invalidate a saved checkpoint the same way a
    // moved pad does. The discriminant goes in first so no two kinds can hash
    // alike on the numbers they happen to share.
    const mover = p.mover;
    if (mover === undefined) continue;
    switch (mover.type) {
      case 'slide':
        mix(1);
        mix(mover.axis[0]); mix(mover.axis[1]); mix(mover.axis[2]);
        mix(mover.amplitude); mix(mover.period); mix(mover.phase);
        break;
      case 'orbit':
        mix(2);
        mix(mover.pivot[0]); mix(mover.pivot[1]); mix(mover.pivot[2]);
        mix(mover.axis[0]); mix(mover.axis[1]); mix(mover.axis[2]);
        mix(mover.rate); mix(mover.phase);
        break;
      case 'pendulum':
        mix(3);
        mix(mover.pivot[0]); mix(mover.pivot[1]); mix(mover.pivot[2]);
        mix(mover.axis[0]); mix(mover.axis[1]); mix(mover.axis[2]);
        mix(mover.amplitude); mix(mover.period); mix(mover.phase);
        break;
      case 'blink':
        mix(4);
        mix(mover.period); mix(mover.duty); mix(mover.phase);
        break;
    }
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
